import React, { useEffect, useRef, useState, forwardRef, useImperativeHandle, memo } from 'react';
import { createPortal } from 'react-dom';
import Timeline, { TimelineItem, TimelineEffects } from './Timeline';
import Carousel, { type CarouselSlide } from './Carousel';
import FAQ, { type FaqItem } from './FAQ';
import Table, { type TableModel } from './Table';
import TableSettings from './TableSettings';
import CarouselSettings from './CarouselSettings';
import FAQSettings from './FAQSettings';
import BlockSettingsModal from './BlockSettingsModal';
import TimelineSettings from './TimelineSettings';
import Embed from './Embed';
import EmbedSettings from './EmbedSettings';
import EmbedDimensionsPopup from './embed-dimensions-popup';

type BlockType = 'paragraph' | 'heading1' | 'heading2' | 'quote' | 'image' | 'code' | 'timeline' | 'carousel' | 'faq' | 'table' | 'embed' | 'embeddims';

type PostBlock = {
  id: string;
  type: BlockType;
  text?: string;
  src?: string;
  group?: string;
  items?: TimelineItem[];
  timelineVariant?: 'classic' | 'dots' | 'cards' | 'split';
  timelineScrollable?: boolean;
  timelineExpandable?: boolean;
  timelineEffects?: TimelineEffects;
  slides?: CarouselSlide[];
  carouselShowDots?: boolean;
  carouselShowArrows?: boolean;
  carouselVariant?: 'classic' | 'cards' | 'minimal';
  carouselDotPosition?: 'top' | 'bottom' | 'overlay-top' | 'overlay-bottom';
  carouselDotStyle?: 'dot' | 'pill' | 'bar';
  faqItems?: FaqItem[];
  faqPalette?: 'indigo' | 'violet' | 'emerald' | 'rose' | 'amber' | 'sky' | 'slate';
  faqStyle?: 'classic' | 'card' | 'underline';
  faqShowIcons?: boolean;
  faqIcon?: string;
  // Table
  rows?: number;
  cols?: number;
  data?: string[][];
  colWidths?: number[];
  rowHeights?: number[];
  dense?: boolean;
  bordered?: boolean;
  fitCols?: boolean;
  fitRows?: boolean;
  // Embed
  embedHtml?: string;
  embedHeightMode?: 'auto'|'fixed';
  embedFixedHeight?: number;
  embedBordered?: boolean;
  embedRadius?: number;
  embedShadow?: boolean;
  embedAllowScripts?: boolean;
  embedAllowSameOrigin?: boolean;
  embedAllowPopups?: boolean;
  embedAllowScroll?: boolean;
  embedRemoveBg?: boolean;
  // Embed Dimensions specific
  embedDimRatio?: number; // width / height
  embedDimZoom?: number;  // preview zoom
};

type PostModel = {
  title: string;
  excerpt?: string;
  cover?: string;
  commentsEnabled?: boolean;
  blocks: PostBlock[];
  rowColFracsByGroup?: Record<string, number[]>;
  rowLeftPadByGroup?: Record<string, number>;
  rowRightPadByGroup?: Record<string, number>;
};

type Props = {
  post: PostModel;
  editable?: boolean;
  onChange?: (next: PostModel) => void;
  showInnerTitle?: boolean;
  selectionArrowMode?: 'nothing' | 'move';
};

const newId = () => Math.random().toString(36).slice(2, 10);
export type PostEditorHandle = {
  inline: (action: 'bold' | 'italic' | 'underline' | 'justifyLeft' | 'justifyCenter' | 'justifyRight' | 'justifyFull' | 'insertUnorderedList' | 'insertOrderedList') => void;
  setColor: (hex: string) => void;
  setFontSize: (px: number) => void;
  setFontFamily: (family: string) => void;
  setApplyToSelectionOnly: (v: boolean) => void;
  setAutoBullet: (v: boolean) => void;
  setAutoNumber: (v: boolean) => void;
  addMarker: () => void;
  clearMarkers: () => void;
  hasMarkers: () => boolean;
  getMarkedLineCount: () => number;
  getSelectionLineCount: () => number;
  block: (type: Extract<BlockType, 'paragraph' | 'heading1' | 'heading2' | 'quote'>) => void;
  insert: (type: Extract<BlockType, 'image' | 'code' | 'timeline' | 'carousel' | 'faq' | 'table' | 'embed'>) => void;
  insertImage: (url: string) => void;
  insertImageAt: (url: string, index: number) => void;
  insertImageAtWithGroup: (url: string, index: number, groupTargetId?: string | null, side?: 'left' | 'right' | null) => void;
  getModel: () => PostModel;
  endEditing: () => void;
  commitPending: () => void;
  restoreFocus: () => void;
  getActiveBlockId: () => string | null;
  setActiveBlockId: (id: string, caret?: 'start' | 'end') => void;
};

const DisplayPostImpl = forwardRef(function DisplayPostImplInner(
  { post, editable = false, onChange, showInnerTitle = false, selectionArrowMode = 'move' }: Props,
  ref: React.Ref<PostEditorHandle>
) {
  const EmbedDimsBlock: React.FC<{ html?: string; ratio?: number; zoom?: number; extraDelta?: number; onApply?: (p:{ratio:number;zoom:number})=>void }> = ({ html = '', ratio = 16/9, zoom = 1, extraDelta = 0, onApply }) => {
    const wrapRef = useRef<HTMLDivElement | null>(null);
    const [w, setW] = useState(0);
    const [showDims, setShowDims] = useState(false);
    // Local state to immediately reflect changes before parent model propagation
    const [rLocal, setRLocal] = useState<number>(Number(ratio) || (16/9));
    const [zLocal, setZLocal] = useState<number>(Number(zoom) || 1);
    // Aspect ratio quick menu
    const RATIOS: { label: string; value: number }[] = [
      { label: '16:9 (HD)', value: 16/9 },
      { label: '19:9 (Ultra‑wide)', value: 19/9 },
      { label: '21:9 (Cinema)', value: 21/9 },
      { label: '32:9 (Super‑ultra‑wide)', value: 32/9 },
      { label: '2:1', value: 2/1 },
      { label: '18:9', value: 18/9 },
      { label: '3:2', value: 3/2 },
      { label: '4:3', value: 4/3 },
      { label: '5:4', value: 5/4 },
      { label: '1:1 (Square)', value: 1 },
      { label: '9:16 (Portrait)', value: 9/16 },
      { label: '4:5 (Portrait)', value: 4/5 },
    ];
    const [showRatioMenu, setShowRatioMenu] = useState(false);
    const badgeRef = useRef<HTMLDivElement | null>(null);
    const menuRef = useRef<HTMLDivElement | null>(null);
    useEffect(() => { setRLocal(Number(ratio) || (16/9)); }, [ratio]);
    useEffect(() => { setZLocal(Number(zoom) || 1); }, [zoom]);
    useEffect(() => {
      if (!showRatioMenu) return;
      const onDocDown = (e: MouseEvent) => {
        const t = e.target as Node | null;
        if (menuRef.current && menuRef.current.contains(t)) return;
        if (badgeRef.current && badgeRef.current.contains(t)) return;
        setShowRatioMenu(false);
      };
      document.addEventListener('mousedown', onDocDown, true);
      return () => { document.removeEventListener('mousedown', onDocDown, true); };
    }, [showRatioMenu]);
    useEffect(() => {
      const el = wrapRef.current; if (!el || typeof (window as any).ResizeObserver === 'undefined') return;
      const ro = new (window as any).ResizeObserver((entries: any[]) => {
        const r = entries && entries[0] ? entries[0].contentRect : null;
        if (r) setW(Math.round(r.width || 0));
      });
      ro.observe(el);
      return () => { try { ro.disconnect(); } catch {} };
    }, []);
    const scale = Math.max(0.1, Math.min(3, Number(zLocal) || 1));
    const invPercent = (100 / scale) + '%';
    const baseH = Math.round((w || 0) / Math.max(0.0001, Number(rLocal) || (16/9)));
    const h = Math.max(1, baseH + Math.round(Number(extraDelta) || 0));
    const fmtRatio = (r: number) => {
      const rr = Math.max(0.0001, Number(r) || 0);
      let best = { n: 16, d: 9, err: Math.abs(16/9 - rr) };
      for (let d = 1; d <= 64; d++) {
        const n = Math.max(1, Math.round(rr * d));
        const val = n / d;
        const err = Math.abs(val - rr);
        if (err < best.err) best = { n, d, err };
      }
      return `${best.n}:${best.d}`;
    };
    const ratioLabel = fmtRatio(Number(rLocal) || (16/9));
    const srcDoc = `<!doctype html><html><head><meta charset="utf-8"/><meta name="viewport" content="width=device-width, initial-scale=1"/><style>html,body{margin:0;padding:0;overflow:hidden}</style></head><body>${html||''}</body></html>`;
    return (
      <div ref={wrapRef} className="rounded-lg border border-gray-200 bg-white p-3">
        <div className="relative overflow-hidden rounded-md border border-gray-200 bg-gray-50" style={{ width: '100%', height: h }}>
          <div ref={badgeRef} className="absolute left-2 top-2 z-50 inline-flex items-center gap-2 rounded-full border border-gray-200 bg-white/90 px-2 py-0.5 text-[11px] text-gray-700 shadow-sm" data-editor-action>
            <button
              type="button"
              className="font-medium hover:text-emerald-700"
              onMouseDown={(e)=>{ e.preventDefault(); e.stopPropagation(); }}
              onClick={(e)=>{ e.preventDefault(); e.stopPropagation(); setShowRatioMenu(v=>!v); }}
              title="Change aspect ratio"
            >
              {ratioLabel}
            </button>
            <button
              type="button"
              title="Open dimensions preview"
              className="inline-flex items-center justify-center w-5 h-5 rounded-md border border-gray-200 bg-white text-emerald-600 hover:bg-emerald-50"
              onMouseDown={(e)=>{ e.preventDefault(); e.stopPropagation(); }}
              onClick={()=> setShowDims(true)}
            >
              <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="14" rx="2"/><path d="M8 21h8"/></svg>
            </button>
            {showRatioMenu && (
              <div
                ref={menuRef}
                className="absolute left-0 top-full mt-1 w-40 rounded-md border border-gray-200 bg-white shadow-lg z-[60]"
                onMouseDown={(e)=>{ e.stopPropagation(); }}
                onClick={(e)=>{ e.stopPropagation(); }}
              >
                <div className="max-h-64 overflow-auto py-1 text-[12px]">
                  {RATIOS.map(opt => (
                    <button
                      key={opt.label}
                      type="button"
                      className={`block w-full text-left px-2 py-1.5 hover:bg-emerald-50 ${Math.abs(opt.value - (Number(rLocal)||0)) < 1e-6 ? 'text-emerald-700 font-semibold' : 'text-gray-800'}`}
                      onClick={()=> {
                        const val = Number(opt.value)|| (16/9);
                        setRLocal(val);
                        try { onApply && onApply({ ratio: val, zoom: Number(zLocal)||1 }); } finally { setShowRatioMenu(false); }
                      }}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
          <div style={{ width: invPercent, height: invPercent, transform: `scale(${scale})`, transformOrigin: 'top left' }}>
            <iframe
              title="Embed Dimensions"
              srcDoc={srcDoc}
              className="h-full w-full"
              sandbox="allow-scripts allow-same-origin allow-forms"
            />
          </div>
        </div>
        <EmbedDimensionsPopup
          open={showDims}
          width={w}
          height={h}
          html={html}
          initialRatio={Number(rLocal) || (16/9)}
          onClose={()=> setShowDims(false)}
          onApply={({ ratio: r, zoom: z }) => { try { setRLocal(Number(r) || (16/9)); setZLocal(Number(z) || 1); onApply && onApply({ ratio: r, zoom: z }); } finally { setShowDims(false); } }}
        />
      </div>
    );
  };
  const [model, setModel] = useState<PostModel>(post);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const selfEditRef = useRef(false);
  const modelRef = useRef<PostModel>(post);
  const isEditingRef = useRef(false);
  const lastSelRangeRef = useRef<Range | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [showAdd, setShowAdd] = useState(false);
  // When true, tools apply only to the current selection; alignment won't auto-expand to whole block
  const applyToSelectionOnlyRef = useRef<boolean>(false);
  // Auto list modes for Enter behavior
  const autoBulletRef = useRef<boolean>(false);
  const autoNumberRef = useRef<boolean>(false);
  const [insertIndex, setInsertIndex] = useState<number | null>(null);
  const [insertGroupTargetId, setInsertGroupTargetId] = useState<string | null>(null);
  const [insertSide, setInsertSide] = useState<'left' | 'right' | null>(null);
  const [blockSettings, setBlockSettings] = useState<{ id: string; type: 'timeline' | 'carousel' | 'faq' | 'table' | 'embed' } | null>(null);
  const [timelinePreview, setTimelinePreview] = useState<null | {
    id: string;
    items: TimelineItem[];
    variant: 'classic' | 'dots' | 'cards' | 'split';
    scrollable: boolean;
    expandable: boolean;
    effects?: TimelineEffects;
  }>(null);
  const [carouselPreview, setCarouselPreview] = useState<null | {
    id: string;
    slides: CarouselSlide[];
    showDots: boolean;
    showArrows: boolean;
    variant: 'classic' | 'cards' | 'minimal';
    dotPosition: 'top' | 'bottom' | 'overlay-top' | 'overlay-bottom';
    dotStyle: 'dot' | 'pill' | 'bar';
  }>(null);
  const [embedPlaceMode, setEmbedPlaceMode] = useState<string | null>(null);
  const [embedPlaceTargetIndex, setEmbedPlaceTargetIndex] = useState<number | null>(null);
  // Anchor and position for the embed quick-adjust popup (rendered via portal to avoid clipping)
  const quickPortalAnchorRef = useRef<HTMLElement | null>(null);
  const [quickPortalPos, setQuickPortalPos] = useState<{ left: number; top: number } | null>(null);
  const updateQuickPortalFromEl = (el: HTMLElement | null) => {
    if (!el) return;
    const btnRect = el.getBoundingClientRect();
    const width = 256; // w-64
    // Try to position relative to the whole block container to avoid covering content
    const container = el.closest('[data-block-id]') as HTMLElement | null;
    const targetRect = container ? container.getBoundingClientRect() : btnRect;
    const spaceRight = window.innerWidth - targetRect.right - 8;
    const spaceLeft = targetRect.left - 8;
    let left: number;
    let top: number;
    if (spaceRight >= width) {
      // Place to the right of the block
      left = targetRect.right + 8;
      top = Math.max(8, targetRect.top);
    } else if (spaceLeft >= width) {
      // Place to the left of the block
      left = targetRect.left - width - 8;
      top = Math.max(8, targetRect.top);
    } else {
      // Fallback: below the button, clamped within viewport
      left = Math.max(8, Math.min(window.innerWidth - width - 8, btnRect.right - width));
      top = Math.max(8, btnRect.bottom + 8);
    }
    setQuickPortalPos({ left, top });
  };
  useEffect(() => {
    if (!embedPlaceMode) { setQuickPortalPos(null); return; }
    const handler = () => updateQuickPortalFromEl(quickPortalAnchorRef.current);
    window.addEventListener('resize', handler);
    window.addEventListener('scroll', handler, true);
    handler();
    return () => {
      window.removeEventListener('resize', handler);
      window.removeEventListener('scroll', handler, true);
    };
  }, [embedPlaceMode]);
  const [faqPreview, setFaqPreview] = useState<null | { id: string; items: FaqItem[]; palette?: 'indigo' | 'violet' | 'emerald' | 'rose' | 'amber' | 'sky' | 'slate'; style?: 'classic' | 'card' | 'underline'; showIcons?: boolean; icon?: string }>(null);
  const [tablePreview, setTablePreview] = useState<null | (TableModel & { id: string })>(null);
  const [embedPreview, setEmbedPreview] = useState<null | { id: string; html?: string; heightMode?: 'auto'|'fixed'; fixedHeight?: number; bordered?: boolean; radius?: number; shadow?: boolean; allowScripts?: boolean; allowSameOrigin?: boolean; allowPopups?: boolean; allowScroll?: boolean; removeBg?: boolean; responsiveFit?: boolean; offsetX?: number; offsetY?: number }>(null);
  const wordStacksRef = useRef<Record<string, string[]>>({});
  const prevTokensRef = useRef<Record<string, string[]>>({});
  const prevEndedBoundaryRef = useRef<Record<string, boolean>>({});
  const actionsRef = useRef<Record<string, (
    | { type: 'word'; text: string; start?: number; end?: number }
    | { type: 'br'; start: number; end: number; nth?: number; ctxB?: string; newLine?: boolean }
    | { type: 'color'; hex: string; start: number; end: number; text?: string }
    | { type: 'fontSize'; px: number; start: number; end: number; text?: string }
    | { type: 'fontFamily'; family: string; start: number; end: number; text?: string }
    | { type: 'align'; mode: 'left' | 'center' | 'right' | 'justify'; start: number; end: number; block?: boolean; text?: string }
    | { type: 'delete'; text: string; start: number; end: number }
  )[]>>({});
  const suppressActionsRef = useRef<boolean>(false);
  const stackPersistRef = useRef<Record<string, boolean>>({});
  const stackPosPersistRef = useRef<Record<string, {x:number;y:number}>>({});
  const redoStacksRef = useRef<Record<string, any[]>>({});
  useEffect(() => { modelRef.current = model; }, [model]);

  const DragLine: React.FC = () => (
    <div className="w-full">
      <div className="mx-auto h-1 rounded-full bg-gradient-to-r from-purple-400 via-fuchsia-400 to-pink-400 shadow-sm" />
    </div>
  );

  // Spacer height grown by dragging the line; persisted per block id
  const [dragExtraById, setDragExtraById] = useState<Record<string, number>>({});
  const dragActiveRef = useRef<boolean>(false);
  const dragStartYRef = useRef<number>(0);
  const dragStartExtraRef = useRef<number>(0);
  const dragTargetIdRef = useRef<string | null>(null);
  const dragStartEmbedHeightRef = useRef<number>(0);
  const dragRafRef = useRef<number | null>(null);
  const dragNextHeightRef = useRef<number | null>(null);

  // Live quick offset state for smooth slider interaction
  const [quickOffsetById, setQuickOffsetById] = useState<Record<string, { x?: number; y?: number }>>({});
  const quickOffsetRafRef = useRef<number | null>(null);
  const setQuickOffset = (id: string, patch: { x?: number; y?: number }) => {
    const apply = () => {
      quickOffsetRafRef.current = null;
      setQuickOffsetById(prev => ({
        ...prev,
        [id]: { ...(prev[id] || {}), ...patch },
      }));
    };
    if (quickOffsetRafRef.current == null) quickOffsetRafRef.current = requestAnimationFrame(apply);
  };
  // Drag-to-adjust state for embed placement
  const embedDragActiveRef = useRef<boolean>(false);
  const embedDragStartRef = useRef<{ id: string; x: number; y: number; offX: number; offY: number } | null>(null);
  const ignoreNextClickRef = useRef<boolean>(false);
  const startDragResize = (e: React.MouseEvent, blockId: string) => {
    e.preventDefault();
    dragActiveRef.current = true;
    dragTargetIdRef.current = blockId;
    dragStartYRef.current = e.clientY;
    dragStartExtraRef.current = dragExtraById[blockId] || 0;
    // Cache starting embed height (if applicable) to avoid jitter while updating during drag
    const curStart = modelRef.current;
    const startIdx = curStart.blocks.findIndex(b => b.id === blockId);
    if (startIdx >= 0 && (curStart.blocks[startIdx] as any).type === 'embed') {
      const b0 = curStart.blocks[startIdx] as any;
      dragStartEmbedHeightRef.current = Math.max(120, Number(b0.embedFixedHeight || 360));
    } else {
      dragStartEmbedHeightRef.current = 0;
    }
    const onMove = (ev: MouseEvent) => {
      if (!dragActiveRef.current) return;
      const dy = ev.clientY - dragStartYRef.current;
      const id = dragTargetIdRef.current;
      if (!id) return;
      // If the block is an embed, resize the embed's fixed height directly
      const cur = modelRef.current;
      const idx = cur.blocks.findIndex(b => b.id === id);
      if (idx >= 0 && (cur.blocks[idx] as any).type === 'embed') {
        const base = dragStartEmbedHeightRef.current || Math.max(120, Number((cur.blocks[idx] as any).embedFixedHeight || 360));
        const nextHeight = Math.max(120, base + dy);
        dragNextHeightRef.current = nextHeight;
        if (dragRafRef.current == null) {
          dragRafRef.current = requestAnimationFrame(() => {
            dragRafRef.current = null;
            const h = dragNextHeightRef.current;
            if (h == null) return;
            // live update block height
            updateBlock(id, { embedFixedHeight: h } as any);
            // clear extra padding for embeds while resizing
            setDragExtraById(prev => (prev[id] === 0 ? prev : { ...prev, [id]: 0 }));
            // if settings modal is open for this embed, sync preview
            if (blockSettings && blockSettings.type === 'embed' && blockSettings.id === id) {
              setEmbedPreview(prev => (prev && prev.id === id ? { ...prev, fixedHeight: h } : prev));
            }
          });
        }
        return;
      }
      // Default behavior: adjust extra bottom spacing for non-embed blocks
      const next = Math.max(0, dragStartExtraRef.current + dy);
      setDragExtraById(prev => (prev[id] === next ? prev : { ...prev, [id]: next }));
    };
    const onUp = () => {
      dragActiveRef.current = false;
      dragTargetIdRef.current = null;
      if (dragRafRef.current != null) { cancelAnimationFrame(dragRafRef.current); dragRafRef.current = null; }
      dragNextHeightRef.current = null;
      dragStartEmbedHeightRef.current = 0;
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  // Hydrate helper maps from the incoming post so preview renders saved custom widths/paddings
  useEffect(() => {
    // Avoid overwriting in-progress drag state
    if (lPadActiveRef.current || rPadActiveRef.current || hDragActiveRef.current) return;
    const fr = (post as any).rowColFracsByGroup as Record<string, number[]> | undefined;
    const lp = (post as any).rowLeftPadByGroup as Record<string, number> | undefined;
    const rp = (post as any).rowRightPadByGroup as Record<string, number> | undefined;
    if (fr && typeof fr === 'object' && Object.keys(fr).length > 0) {
      setRowColFractionsByGroup(prev => {
        const merged: Record<string, number[]> = { ...prev };
        for (const k of Object.keys(fr)) {
          if (!(k in merged)) merged[k] = fr[k];
        }
        return merged;
      });
    }
    if (lp && typeof lp === 'object' && Object.keys(lp).length > 0) {
      setRowLeftPadByGroup(prev => {
        const merged: Record<string, number> = { ...prev };
        for (const k of Object.keys(lp)) {
          if (!(k in merged)) merged[k] = lp[k];
        }
        return merged;
      });
    }
    if (rp && typeof rp === 'object' && Object.keys(rp).length > 0) {
      setRowRightPadByGroup(prev => {
        const merged: Record<string, number> = { ...prev };
        for (const k of Object.keys(rp)) {
          if (!(k in merged)) merged[k] = rp[k];
        }
        return merged;
      });
    }
  }, [post]);

  // Left padding drag
  const lPadActiveRef = useRef<boolean>(false);
  const lPadGroupRef = useRef<string | null>(null);
  const lPadStartXRef = useRef<number>(0);
  const lPadStartValRef = useRef<number>(0);
  const persistLayoutMaps = () => {
    const next: PostModel = { ...modelRef.current } as any;
    if (rowColFractionsByGroup && Object.keys(rowColFractionsByGroup).length > 0) (next as any).rowColFracsByGroup = rowColFractionsByGroup;
    if (rowLeftPadByGroup && Object.keys(rowLeftPadByGroup).length > 0) (next as any).rowLeftPadByGroup = rowLeftPadByGroup;
    if (rowRightPadByGroup && Object.keys(rowRightPadByGroup).length > 0) (next as any).rowRightPadByGroup = rowRightPadByGroup;
    // Make available synchronously for getModel()/preview navigation
    modelRef.current = next;
    setModel(next);
    if (onChange) onChange(next);
  };

  // Emit latest layout during drags so preview navigations see most recent values
  const emitLayoutDuringDrag = (
    nextFracs?: Record<string, number[]>,
    nextLP?: Record<string, number>,
    nextRP?: Record<string, number>
  ) => {
    const next: PostModel = { ...modelRef.current } as any;
    const fr = nextFracs ?? rowColFractionsByGroup;
    const lp = nextLP ?? rowLeftPadByGroup;
    const rp = nextRP ?? rowRightPadByGroup;
    if (fr && Object.keys(fr).length > 0) (next as any).rowColFracsByGroup = fr;
    if (lp && Object.keys(lp).length > 0) (next as any).rowLeftPadByGroup = lp;
    if (rp && Object.keys(rp).length > 0) (next as any).rowRightPadByGroup = rp;
    modelRef.current = next;
    if (onChange) onChange(next);
  };
  const startLeftPaddingResize = (e: React.MouseEvent, groupId: string) => {
    e.preventDefault(); e.stopPropagation();
    lPadActiveRef.current = true;
    lPadGroupRef.current = groupId;
    lPadStartXRef.current = e.clientX;
    lPadStartValRef.current = rowLeftPadByGroup[groupId] ?? 0;
    const onMove = (ev: MouseEvent) => {
      if (!lPadActiveRef.current) return;
      const dx = lPadStartXRef.current - ev.clientX; // dragging left increases padding
      const next = Math.max(0, lPadStartValRef.current + dx);
      const gid = lPadGroupRef.current; if (!gid) return;
      setRowLeftPadByGroup(prev => {
        const updated = (prev[gid] === next ? prev : { ...prev, [gid]: next });
        if (updated !== prev) emitLayoutDuringDrag(undefined, updated, undefined);
        return updated;
      });
    };
    const onUp = () => {
      lPadActiveRef.current = false; lPadGroupRef.current = null;
      persistLayoutMaps();
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  // Per-row extra right padding (for last block in row)
  const [rowRightPadByGroup, setRowRightPadByGroup] = useState<Record<string, number>>({});
  const BASE_RIGHT_PAD = 8; // px baseline right inset to avoid clipping against container border
  // Per-row extra left padding (for first block in row)
  const [rowLeftPadByGroup, setRowLeftPadByGroup] = useState<Record<string, number>>({});
  const BASE_LEFT_PAD = 0; // px baseline left inset
  const rPadActiveRef = useRef<boolean>(false);
  const rPadGroupRef = useRef<string | null>(null);
  const rPadStartXRef = useRef<number>(0);
  const rPadStartValRef = useRef<number>(0);
  const startRightPaddingResize = (e: React.MouseEvent, groupId: string) => {
    e.preventDefault(); e.stopPropagation();
    rPadActiveRef.current = true;
    rPadGroupRef.current = groupId;
    rPadStartXRef.current = e.clientX;
    rPadStartValRef.current = rowRightPadByGroup[groupId] ?? 0;
    const onMove = (ev: MouseEvent) => {
      if (!rPadActiveRef.current) return;
      const dx = ev.clientX - rPadStartXRef.current;
      const next = Math.max(0, rPadStartValRef.current + dx);
      const gid = rPadGroupRef.current; if (!gid) return;
      setRowRightPadByGroup(prev => {
        const updated = (prev[gid] === next ? prev : { ...prev, [gid]: next });
        if (updated !== prev) emitLayoutDuringDrag(undefined, undefined, updated);
        return updated;
      });
    };
    const onUp = () => {
      rPadActiveRef.current = false; rPadGroupRef.current = null;
      persistLayoutMaps();
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  // Horizontal splitter for multi-col rows (supports N columns)
  const [rowColFractionsByGroup, setRowColFractionsByGroup] = useState<Record<string, number[]>>({});
  const hDragActiveRef = useRef<boolean>(false);
  const hDragGroupRef = useRef<string | null>(null);
  const hDragStartXRef = useRef<number>(0);
  const hDragLeftIdxRef = useRef<number>(0);
  const startHorizontalResize = (e: React.MouseEvent, groupId: string, leftIdx: number) => {
    e.preventDefault(); e.stopPropagation();
    hDragActiveRef.current = true;
    hDragGroupRef.current = groupId;
    hDragStartXRef.current = e.clientX;
    hDragLeftIdxRef.current = leftIdx;
    const rowEl = document.querySelector(`[data-row-group="${groupId}"]`) as HTMLElement | null;
    const rect = rowEl ? rowEl.getBoundingClientRect() : null;
    const onMove = (ev: MouseEvent) => {
      if (!hDragActiveRef.current) return;
      if (!rect) return;
      const gid = hDragGroupRef.current; if (!gid) return;
      const children = rowEl ? Array.from(rowEl.children) : [];
      const cols = Math.max(2, children.length);
      const minFrac = 0.05;
      setRowColFractionsByGroup(prev => {
        const current = prev[gid];
        let fracs = (current && current.length === cols) ? [...current] : Array(cols).fill(1 / cols);
        const leftIdxNow = Math.min(Math.max(0, hDragLeftIdxRef.current), cols - 2);
        // Sum before leftIdx
        const sumBefore = fracs.slice(0, leftIdxNow).reduce((a,b)=>a+b,0);
        const pairSum = fracs[leftIdxNow] + fracs[leftIdxNow+1];
        const x = ev.clientX;
        const fracAtX = (x - rect.left) / Math.max(1, rect.width);
        let newLeft = fracAtX - sumBefore;
        // Constrain within [minFrac, pairSum - minFrac]
        newLeft = Math.max(minFrac, Math.min(pairSum - minFrac, newLeft));
        const newRight = pairSum - newLeft;
        if (Math.abs(fracs[leftIdxNow] - newLeft) < 0.0005 && Math.abs(fracs[leftIdxNow+1] - newRight) < 0.0005) return prev;
        fracs[leftIdxNow] = newLeft;
        fracs[leftIdxNow+1] = newRight;
        const updated = { ...prev, [gid]: fracs };
        emitLayoutDuringDrag(updated, undefined, undefined);
        return updated;
      });
    };
    const onUp = () => {
      hDragActiveRef.current = false;
      hDragGroupRef.current = null;
      persistLayoutMaps();
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  const commitEdits = () => {
    // push shadow model into state and parent, then allow external syncs
    const cur = modelRef.current;
    const next: PostModel = { ...cur } as any;
    if (rowColFractionsByGroup && Object.keys(rowColFractionsByGroup).length > 0) (next as any).rowColFracsByGroup = rowColFractionsByGroup;
    if (rowLeftPadByGroup && Object.keys(rowLeftPadByGroup).length > 0) (next as any).rowLeftPadByGroup = rowLeftPadByGroup;
    if (rowRightPadByGroup && Object.keys(rowRightPadByGroup).length > 0) (next as any).rowRightPadByGroup = rowRightPadByGroup;
    modelRef.current = next;
    setModel(next);
    onChange && onChange(next);
    selfEditRef.current = false;
  };

  // Move helpers: reorder blocks and/or regroup to place into rows
  const getRowRange = (blocks: PostBlock[], idx: number): { start: number; end: number; group: string | null } => {
    const g = blocks[idx].group || null;
    if (!g) return { start: idx, end: idx, group: null };
    let s = idx; let e = idx;
    while (s - 1 >= 0 && blocks[s - 1].group === g) s--;
    while (e + 1 < blocks.length && blocks[e + 1].group === g) e++;
    return { start: s, end: e, group: g };
  };
  const setBlocksAndCommit = (blocks: PostBlock[]) => {
    const next = { ...modelRef.current, blocks };
    modelRef.current = next; setModel(next); onChange && onChange(next);
  };
  const moveBlockToIndex = (id: string, targetIndex: number) => {
    const blocks = [...modelRef.current.blocks];
    const from = blocks.findIndex(b => b.id === id);
    if (from < 0) return;
    const item = blocks.splice(from, 1)[0];
    let to = Math.max(0, Math.min(targetIndex, blocks.length));
    if (from < to) to -= 1;
    blocks.splice(to, 0, item);
    setBlocksAndCommit(blocks);
  };
  const moveBlockLeft = (id: string) => {
    const blocks = [...modelRef.current.blocks];
    const idx = blocks.findIndex(b => b.id === id); if (idx < 0) return;
    const { start, end } = getRowRange(blocks, idx);
    if (idx <= start) {
      // wrap to far right of this row
      const moving = blocks.splice(idx, 1)[0];
      const insertPos = end; // after removal, row occupies start..end-1; inserting at 'end' appends to row
      blocks.splice(insertPos, 0, moving);
      setBlocksAndCommit(blocks);
      return;
    }
    const tmp = blocks[idx - 1]; blocks[idx - 1] = blocks[idx]; blocks[idx] = tmp;
    setBlocksAndCommit(blocks);
  };
  const moveBlockRight = (id: string) => {
    const blocks = [...modelRef.current.blocks];
    const idx = blocks.findIndex(b => b.id === id); if (idx < 0) return;
    const { start, end } = getRowRange(blocks, idx);
    if (idx >= end) {
      // wrap to far left of this row
      const moving = blocks.splice(idx, 1)[0];
      const insertPos = start; // insert at row start
      blocks.splice(insertPos, 0, moving);
      setBlocksAndCommit(blocks);
      return;
    }
    const tmp = blocks[idx + 1]; blocks[idx + 1] = blocks[idx]; blocks[idx] = tmp;
    setBlocksAndCommit(blocks);
  };
  const moveBlockUp = (id: string) => {
    const blocks = [...modelRef.current.blocks];
    const idx = blocks.findIndex(b => b.id === id); if (idx < 0) return;
    const curRange = getRowRange(blocks, idx);
    // Find previous row
    let p = curRange.start - 1;
    // If there is no previous row
    if (p < 0) {
      const moving = { ...blocks[idx] } as PostBlock;
      // Remove from current row
      blocks.splice(idx, 1);
      if (curRange.start === curRange.end) {
        // Topmost row with a single block: move to a new row at the very bottom
        delete (moving as any).group;
        blocks.push(moving);
      } else {
        // Top row but multi-column: create a new row above current
        delete (moving as any).group;
        const insertPos = curRange.start;
        blocks.splice(insertPos, 0, moving);
      }
      // Clean up source group if it now has a single member
      const g0 = curRange.group;
      if (g0) {
        const members = blocks.filter(b => b.group === g0);
        if (members.length === 1) {
          const oneIdx = blocks.findIndex(b => b.id === members[0].id);
          blocks[oneIdx] = { ...blocks[oneIdx] };
          delete (blocks[oneIdx] as any).group;
        }
      }
      setBlocksAndCommit(blocks);
      return;
    }
    const prevRange = getRowRange(blocks, p);
    // Prepare moving block
    const moving = { ...blocks[idx] } as PostBlock;
    // Remove from current (removal occurs after prev row, so its indices remain stable)
    blocks.splice(idx, 1);
    // Determine/ensure destination group
    let destGroup = prevRange.group;
    if (!destGroup) {
      destGroup = newId();
      for (let i = prevRange.start; i <= prevRange.end; i++) blocks[i] = { ...blocks[i], group: destGroup };
    }
    (moving as any).group = destGroup;
    // Insert at the end of the previous row so it shares space with that row
    const insertPos = prevRange.end + 1;
    blocks.splice(insertPos, 0, moving);
    // If source group now has a single item, clear its group
    const g = curRange.group;
    if (g) {
      const members = blocks.filter(b => b.group === g);
      if (members.length === 1) {
        const oneIdx = blocks.findIndex(b => b.id === members[0].id);
        blocks[oneIdx] = { ...blocks[oneIdx] };
        delete (blocks[oneIdx] as any).group;
      }
    }
    setBlocksAndCommit(blocks);
  };
  const moveBlockDown = (id: string) => {
    const blocks = [...modelRef.current.blocks];
    const idx = blocks.findIndex(b => b.id === id); if (idx < 0) return;
    const curRange = getRowRange(blocks, idx);
    // Find next row start
    let n = curRange.end + 1;
    // If there is no next row
    if (n >= blocks.length) {
      const moving = { ...blocks[idx] } as PostBlock;
      // Remove from current row first
      blocks.splice(idx, 1);
      // If this row had only this block, move it to a new row at the very top
      if (curRange.start === curRange.end) {
        delete (moving as any).group; // single row at top
        blocks.splice(0, 0, moving);
      } else {
        // Otherwise, create a new single row below current
        delete (moving as any).group;
        let insertPos = curRange.end + 1;
        if (idx <= curRange.end) insertPos -= 1;
        blocks.splice(insertPos, 0, moving);
      }
      // If source group now has a single item, clear its group
      const g = curRange.group;
      if (g) {
        const members = blocks.filter(b => b.group === g);
        if (members.length === 1) {
          const oneIdx = blocks.findIndex(b => b.id === members[0].id);
          blocks[oneIdx] = { ...blocks[oneIdx] };
          delete (blocks[oneIdx] as any).group;
        }
      }
      setBlocksAndCommit(blocks);
      return;
    }
    // Next row exists
    let nextRange = getRowRange(blocks, n);
    let destGroup = nextRange.group;
    const moving = { ...blocks[idx] } as PostBlock;
    // Remove from current before computing insertion
    blocks.splice(idx, 1);
    // If next row has no group, form a new group with that single block and place moved block to the right
    if (!destGroup) {
      destGroup = newId();
      // After removal, nextRange indices may shift if idx < nextRange.start
      let start = nextRange.start;
      let end = nextRange.end;
      if (idx < nextRange.start) { start -= 1; end -= 1; }
      for (let i = start; i <= end; i++) blocks[i] = { ...blocks[i], group: destGroup };
      (moving as any).group = destGroup;
      // insert to the right end of that row
      const insertPos = end + 1;
      blocks.splice(insertPos, 0, moving);
    } else {
      // Destination already a group: append to end of that row
      (moving as any).group = destGroup;
      // Recompute nextRange after removal to get accurate end
      nextRange = getRowRange(blocks, idx < nextRange.start ? nextRange.start - 1 : nextRange.start);
      const insertPos = nextRange.end + 1;
      blocks.splice(insertPos, 0, moving);
    }
    // If source group now has a single item, clear its group
    const g = curRange.group;
    if (g) {
      const members = blocks.filter(b => b.group === g);
      if (members.length === 1) {
        const oneIdx = blocks.findIndex(b => b.id === members[0].id);
        blocks[oneIdx] = { ...blocks[oneIdx] };
        delete (blocks[oneIdx] as any).group;
      }
    }
    setBlocksAndCommit(blocks);
  };

  // Global Arrow key handling to prevent page scroll when a block is selected (only in 'move' mode and not typing)
  useEffect(() => {
    if (!editable || !selectedId || selectionArrowMode !== 'move') return;
    const onKey = (e: KeyboardEvent) => {
      // If currently typing inside an editable, let native behavior (caret move) happen
      if (isEditingRef.current) return;
      const key = e.key;
      if (key === 'ArrowUp' || key === 'ArrowDown' || key === 'ArrowLeft' || key === 'ArrowRight') {
        e.preventDefault();
        e.stopPropagation();
        const id = selectedId;
        if (key === 'ArrowUp') { moveBlockUp(id); return; }
        if (key === 'ArrowDown') { moveBlockDown(id); return; }
        if (key === 'ArrowLeft') { moveBlockLeft(id); return; }
        if (key === 'ArrowRight') { moveBlockRight(id); return; }
      }
    };
    document.addEventListener('keydown', onKey, true);
    return () => { document.removeEventListener('keydown', onKey, true); };
  }, [editable, selectedId, selectionArrowMode]);

  // Global Escape handling: when not typing and a block is selected, ESC deselects the block (exit active mode)
  useEffect(() => {
    if (!editable) return;
    const onEsc = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      // If currently typing, let the block-level handler process the first ESC
      if (isEditingRef.current) return;
      if (selectedId) {
        e.preventDefault();
        e.stopPropagation();
        setSelectedId(null);
      }
    };
    document.addEventListener('keydown', onEsc, true);
    return () => { document.removeEventListener('keydown', onEsc, true); };
  }, [editable, selectedId]);

  useEffect(() => {
    const onSel = () => {
      const sel = window.getSelection();
      if (!sel || sel.rangeCount === 0) return;
      const r = sel.getRangeAt(0);
      const host = containerRef.current;
      if (host && r && host.contains(r.startContainer)) {
        lastSelRangeRef.current = r.cloneRange();
      }
    };
    document.addEventListener('selectionchange', onSel);
    return () => document.removeEventListener('selectionchange', onSel);
  }, []);

  useEffect(() => {
    if (!editable) return;
    if (!embedPlaceMode) return;
    const onMove = (e: MouseEvent) => {
      const host = containerRef.current; if (!host) return;
      const nodes = host.querySelectorAll('[data-block-id]');
      let best: { idx: number; dy: number } | null = null;
      nodes.forEach((el) => {
        const rect = (el as HTMLElement).getBoundingClientRect();
        const cy = rect.top + rect.height / 2;
        const dy = Math.abs(e.clientY - cy);
        const id = (el as HTMLElement).dataset.blockId as string;
        const idx = modelRef.current.blocks.findIndex(b => b.id === id);
        if (idx >= 0) {
          if (!best || dy < best.dy) best = { idx, dy };
        }
      });
      const candidate = best as { idx: number; dy: number } | null;
      if (candidate !== null) setEmbedPlaceTargetIndex(candidate.idx);
    };
    const onClick = (e: MouseEvent) => {
      // Ignore click if it directly follows a drag
      if (ignoreNextClickRef.current) { ignoreNextClickRef.current = false; return; }
      const t = e.target as Element | null;
      if (t && t.closest) {
        if (t.closest('[data-quick-portal]') || t.closest('[data-editor-action]')) return;
        if (embedPlaceMode && t.closest(`[data-block-id="${embedPlaceMode}"]`)) return;
      }
      e.preventDefault();
      e.stopPropagation();
      const id = embedPlaceMode; if (!id) return;
      const target = embedPlaceTargetIndex;
      if (typeof target === 'number') moveBlockToIndex(id, target);
      setEmbedPlaceMode(null);
      setEmbedPlaceTargetIndex(null);
    };
    const clamp = (v: number, a: number, b: number) => Math.max(a, Math.min(b, v));
    const onDocMoveAdjust = (e: MouseEvent) => {
      if (!embedDragActiveRef.current || !embedDragStartRef.current) return;
      const s = embedDragStartRef.current;
      const dx = e.clientX - s.x;
      const dy = e.clientY - s.y;
      // Apply constraints: Shift = vertical only, Ctrl = horizontal only
      const horizOnly = !!(e.ctrlKey || (e as any).metaKey === true);
      const vertOnly = !!e.shiftKey;
      const nx = horizOnly ? clamp(s.offX + dx, -300, 300) : (vertOnly ? s.offX : clamp(s.offX + dx, -300, 300));
      const ny = vertOnly ? clamp(s.offY + dy, -300, 300) : (horizOnly ? s.offY : clamp(s.offY + dy, -300, 300));
      setQuickOffset(s.id, { x: nx, y: ny });
    };
    const onDocUpAdjust = () => {
      if (!embedDragActiveRef.current || !embedDragStartRef.current) return;
      const s = embedDragStartRef.current;
      const curr = quickOffsetById[s.id] || {};
      const finX = typeof curr.x === 'number' ? curr.x : s.offX;
      const finY = typeof curr.y === 'number' ? curr.y : s.offY;
      updateBlock(s.id, { embedOffsetX: finX, embedOffsetY: finY } as any);
      embedDragActiveRef.current = false;
      embedDragStartRef.current = null;
      ignoreNextClickRef.current = true;
    };
    document.addEventListener('mousemove', onMove, true);
    document.addEventListener('click', onClick, true);
    document.addEventListener('mousemove', onDocMoveAdjust, true);
    document.addEventListener('mouseup', onDocUpAdjust, true);
    return () => {
      document.removeEventListener('mousemove', onMove, true);
      document.removeEventListener('click', onClick, true);
      document.removeEventListener('mousemove', onDocMoveAdjust, true);
      document.removeEventListener('mouseup', onDocUpAdjust, true);
    };
  }, [editable, embedPlaceMode, embedPlaceTargetIndex]);

  useEffect(() => {
    const handleDocPointer = (e: MouseEvent | TouchEvent) => {
      const host = containerRef.current;
      if (!host) return;
      const target = e.target as Node | null;
      // If interacting with editor toolbar actions, do not deactivate editor
      if (target && (target as Element).closest && (target as Element).closest('[data-editor-action]')) {
        return;
      }
      if (target && !host.contains(target)) {
        const active = document.activeElement as HTMLElement | null;
        if (active && host.contains(active)) {
          try { active.blur(); } catch {}
        }
        commitEdits();
        setSelectedId(null);
      }
    };
    document.addEventListener('mousedown', handleDocPointer, true);
    document.addEventListener('touchstart', handleDocPointer, { passive: true } as any);
    return () => {
      document.removeEventListener('mousedown', handleDocPointer, true);
      document.removeEventListener('touchstart', handleDocPointer as any);
    };
  }, []);

  useEffect(() => {
    // Only sync from parent when change appears external to this component
    if (selfEditRef.current || isEditingRef.current) return;
    if (JSON.stringify(post) !== JSON.stringify(model)) {
      setModel(post);
    }
  }, [post]);

  const update = (patch: Partial<PostModel>) => {
    const next = { ...model, ...patch };
    setModel(next);
    onChange && onChange(next);
  };

  const updateBlock = (id: string, patch: Partial<PostBlock>) => {
    const blocks = model.blocks.map(b => b.id === id ? { ...b, ...patch } : b);
    update({ blocks });
  };

  const insertBlockAfter = (id: string, type: BlockType) => {
    const idx = model.blocks.findIndex(b => b.id === id);
    const blocks = [...model.blocks];
    let base: PostBlock;
    if (type === 'image') base = { id: newId(), type, src: '/uploads/sample.jpg' };
    else if (type === 'timeline') base = { id: newId(), type, items: [
      { date: new Date().toISOString().slice(0,10), title: 'Getting started', description: 'Kick off your journey.' },
      { date: new Date(Date.now()+86400000).toISOString().slice(0,10), title: 'Next milestone', description: 'Add your next step.' }
    ] } as PostBlock;
    else if (type === 'carousel') base = { id: newId(), type, slides: [
      { title: 'Beautiful card', text: 'Tell a short story here.', image: '/uploads/sample.jpg' },
      { title: 'Second slide', text: 'More context or media.', image: '/uploads/sample.jpg' }
    ], carouselShowDots: true, carouselShowArrows: true, carouselVariant: 'classic', carouselDotPosition: 'top', carouselDotStyle: 'dot' } as PostBlock;
    else if (type === 'faq') base = { id: newId(), type, faqItems: [
      { q: 'What is this?', a: 'This is an example FAQ item. Edit or add more.' },
      { q: 'How do I use it?', a: 'Add your questions and answers to build an FAQ section.' }
    ], faqPalette: 'indigo', faqStyle: 'classic', faqShowIcons: false, faqIcon: 'help' } as PostBlock;
    else if (type === 'table') base = { id: newId(), type, rows: 3, cols: 3, data: [
      ['A1','B1','C1'],
      ['A2','B2','C2'],
      ['A3','B3','C3'],
    ], colWidths: [0,0,0], rowHeights: [0,0,0], dense: false, bordered: true } as PostBlock;
    else if (type === 'embed') base = { id: newId(), type, embedHtml: '<div style="padding:16px;font:14px system-ui">Hello from Embed</div>', embedHeightMode: 'auto', embedFixedHeight: 360, embedBordered: true, embedRadius: 12, embedShadow: false, embedAllowScripts: true, embedAllowSameOrigin: true, embedAllowPopups: true, embedAllowScroll: false, embedRemoveBg: false, embedResponsiveFit: false, embedOffsetX: 0, embedOffsetY: 0 } as PostBlock;
    else base = { id: newId(), type, text: '' };
    blocks.splice(idx + 1, 0, base);
    update({ blocks });
  };

  const removeBlock = (id: string) => {
    const cur = modelRef.current;
    const blocks = cur.blocks.filter(b => b.id !== id);
    const next = { ...cur, blocks };
    modelRef.current = next;
    setModel(next);
    onChange && onChange(next);
    if (selectedId === id) setSelectedId(null);
  };

  const createBlock = (type: BlockType): PostBlock => {
    const id = newId();
    if (type === 'image') return { id, type, src: '/uploads/sample.jpg' };
    if (type === 'timeline') return { id, type, items: [
      { date: new Date().toISOString().slice(0,10), title: 'Getting started', description: 'Kick off your journey.' },
      { date: new Date(Date.now()+86400000).toISOString().slice(0,10), title: 'Next milestone', description: 'Add your next step.' }
    ] } as PostBlock;
    if (type === 'carousel') return { id, type, slides: [
      { title: 'Beautiful card', text: 'Tell a short story here.', image: '/uploads/sample.jpg' },
      { title: 'Second slide', text: 'More context or media.', image: '/uploads/sample.jpg' }
    ], carouselShowDots: true, carouselShowArrows: true, carouselVariant: 'classic', carouselDotPosition: 'top', carouselDotStyle: 'dot' } as PostBlock;
    if (type === 'faq') return { id, type, faqItems: [
      { q: 'What is this?', a: 'This is an example FAQ item. Edit or add more.' },
      { q: 'How do I use it?', a: 'Add your questions and answers to build an FAQ section.' }
    ], faqPalette: 'indigo', faqStyle: 'classic', faqShowIcons: false, faqIcon: 'help' } as PostBlock;
    if (type === 'table') return { id, type, rows: 3, cols: 3, data: [
      ['A1','B1','C1'],
      ['A2','B2','C2'],
      ['A3','B3','C3'],
    ], colWidths: [0,0,0], rowHeights: [0,0,0], dense: false, bordered: true } as PostBlock;
    if (type === 'embed') return { id, type, embedHtml: '<div style="padding:16px;font:14px system-ui">Hello from Embed</div>', embedHeightMode: 'auto', embedFixedHeight: 360, embedBordered: true, embedRadius: 12, embedShadow: false, embedAllowScripts: true, embedAllowSameOrigin: true, embedAllowPopups: true, embedAllowScroll: false, embedRemoveBg: false, embedResponsiveFit: false, embedOffsetX: 0, embedOffsetY: 0 } as PostBlock;
    const placeholders: Record<Exclude<BlockType, 'image'>, string> = {
      paragraph: 'Start writing…',
      heading1: 'Heading',
      heading2: 'Subheading',
      quote: 'Quote',
      code: 'Code snippet',
      timeline: '' as any,
      carousel: '' as any,
      faq: '' as any,
      table: '' as any,
      embed: '' as any,
      embeddims: '' as any
    } as const;
    return { id, type, text: placeholders[type as Exclude<BlockType, 'image'>] } as PostBlock;
  };

  const focusEditableById = (id: string, caret: 'start' | 'end' = 'end') => {
    const host = containerRef.current; if (!host) return;
    const root = host.querySelector(`[data-block-id="${id}"]`) as HTMLElement | null;
    if (!root) return;
    let target: HTMLElement | null = null;
    if (root.tagName.toLowerCase() === 'pre') {
      target = root.querySelector('code') as HTMLElement | null;
    } else {
      target = root as HTMLElement;
    }
    if (!target) return;
    try { target.focus({ preventScroll: true } as any); } catch {}
    // Place caret at requested edge
    const range = document.createRange();
    range.selectNodeContents(target);
    range.collapse(caret === 'start');
    const sel = window.getSelection();
    if (sel) { sel.removeAllRanges(); sel.addRange(range); }
    lastSelRangeRef.current = range.cloneRange();
  };

  const addBlockAtStart = (type: BlockType) => {
    const cur = modelRef.current;
    const b = createBlock(type);
    // If there is a pending group target, we need to group with the first block
    let blocks = [...cur.blocks];
    if (insertGroupTargetId) {
      const targetIdx = blocks.findIndex(x => x.id === insertGroupTargetId);
      const target = blocks[targetIdx];
      const g = target?.group ?? newId();
      // assign group to target if it didn't have one
      if (target && !target.group) blocks[targetIdx] = { ...target, group: g };
      b.group = g;
      // default to insert before target for 'left', after for 'right'
      const i = insertSide === 'right' ? targetIdx + 1 : targetIdx;
      blocks.splice(i, 0, b);
    } else {
      blocks = [b, ...blocks];
    }
    const next = { ...cur, blocks };
    modelRef.current = next;
    setModel(next);
    onChange && onChange(next);
    setSelectedId(b.id);
    setShowAdd(false);
    setInsertIndex(null);
    setInsertGroupTargetId(null);
    setInsertSide(null);
    // focus newly added editable if applicable
    requestAnimationFrame(() => focusEditableById(b.id));
  };

  const addBlockAt = (index: number, type: BlockType) => {
    const cur = modelRef.current;
    const b = createBlock(type);
    const blocks = [...cur.blocks];
    // If we are adding as a same-row neighbor, attach to group
    if (insertGroupTargetId) {
      const targetIdx = blocks.findIndex(x => x.id === insertGroupTargetId);
      const target = blocks[targetIdx];
      const g = target?.group ?? newId();
      if (target && !target.group) blocks[targetIdx] = { ...target, group: g };
      b.group = g;
    }
    const i = Math.max(0, Math.min(index, blocks.length));
    blocks.splice(i, 0, b);
    const next = { ...cur, blocks };
    modelRef.current = next;
    setModel(next);
    onChange && onChange(next);
    setSelectedId(b.id);
    setShowAdd(false);
    setInsertIndex(null);
    setInsertGroupTargetId(null);
    setInsertSide(null);
    requestAnimationFrame(() => focusEditableById(b.id));
  };

  const applyCmd = (cmd: string, value?: string) => {
    let sel = window.getSelection();
    const host = containerRef.current;
    const active = document.activeElement as HTMLElement | null;
    const selInside = !!(sel && sel.rangeCount > 0 && host && host.contains(sel.getRangeAt(0).startContainer));
    const focusInside = !!(active && host && host.contains(active));
    // If selection is missing or outside, or focus is outside, restore last in-editor selection
    if (!selInside || !focusInside) {
      const saved = lastSelRangeRef.current;
      if (saved) {
        const s = window.getSelection();
        if (s) { s.removeAllRanges(); s.addRange(saved.cloneRange()); }
      }
      sel = window.getSelection();
    }
    let el: HTMLElement | null = null;
    if (sel && sel.rangeCount > 0) {
      const range = sel.getRangeAt(0);
      el = range.startContainer as any;
      while (el && (el as any).nodeType === 3) el = (el as any).parentElement as any;
      while (el && (el as any).dataset && (el as any).dataset.blockId == null) el = (el as any).parentElement as any;
    }
    // Fallback to the currently selected block if there is no live selection (e.g., toolbar clicked while only a block is active)
    if (!el) {
      const hostEl = containerRef.current;
      if (hostEl && selectedId) el = hostEl.querySelector(`[data-block-id="${selectedId}"]`) as HTMLElement | null;
    }
    const id = (el as any)?.dataset?.blockId;
    // If no active block id (focus lost due to dropdown), still allow global marker updates
    if (!id) {
      if ((cmd === 'fontSizePx' || cmd === 'foreColor' || cmd === 'fontFamily') && value) {
        try {
          const container = containerRef.current;
          const spans = container?.querySelectorAll('span[data-editor-marker]') || [] as any;
          if ((spans as any).length > 0) {
            commitEdits();
            const px = cmd === 'fontSizePx' ? String(value).replace(/[^0-9]/g, '') : '';
            (spans as NodeListOf<Element>).forEach((node) => {
              const s = node as HTMLElement;
              if (cmd === 'fontSizePx') {
                try {
                  const sized = s.querySelectorAll('font[size], [style*="font-size"], [style*="line-height"]');
                  sized.forEach((e) => {
                    const el = e as HTMLElement;
                    if (el.tagName === 'FONT' && (el as any).getAttribute && (el as any).getAttribute('size')) {
                      const span = document.createElement('span');
                      while (el.firstChild) span.appendChild(el.firstChild);
                      el.replaceWith(span);
                    } else if ((el as any).style) {
                      try { (el as any).style.fontSize = ''; } catch {}
                      try { (el as any).style.lineHeight = ''; } catch {}
                    }
                  });
                } catch {}
                s.style.fontSize = `${px}px`; s.style.lineHeight = '1.3';
              }
              if (cmd === 'foreColor') {
                try {
                  // Clear descendant colors so marker color wins
                  const colored = s.querySelectorAll('[style*="color"], font[color]');
                  colored.forEach((e) => {
                    const el = e as HTMLElement;
                    try { (el as any).style && ((el as any).style.color = ''); } catch {}
                    if (el.tagName === 'FONT') { try { (el as any).removeAttribute('color'); } catch {} }
                  });
                } catch {}
                s.style.color = String(value);
              }
              if (cmd === 'fontFamily') {
                try {
                  // Clear descendant font-family so marker font wins
                  const ff = s.querySelectorAll('[style*="font-family"]');
                  ff.forEach((e) => { try { (e as any).style && ((e as any).style.fontFamily = ''); } catch {} });
                } catch {}
                s.style.fontFamily = String(value);
              }
            });
            if (container) {
              const cur = modelRef.current;
              const blocks = cur.blocks.map(b => {
                const el = container.querySelector(`[data-block-id="${b.id}"]`) as HTMLElement | null;
                return el ? { ...b, text: el.innerHTML } : b;
              });
              const next = { ...cur, blocks };
              modelRef.current = next;
              setModel(next);
              onChange && onChange(next);
              // Record color/font-size actions per block for each marker span
              try {
                blocks.forEach((blk) => {
                  const blockEl = container.querySelector(`[data-block-id="${blk.id}"]`) as HTMLElement | null;
                  if (!blockEl) return;
                  const marked = blockEl.querySelectorAll('span[data-editor-marker]');
                  if (marked.length === 0) return;
                  marked.forEach((m) => {
                    // Compute plain-text start/end offsets of marker span within block
                    try {
                      let acc = 0; let started = false; let start = -1; let end = -1;
                      const tw = document.createTreeWalker(blockEl, NodeFilter.SHOW_TEXT, null);
                      let node = tw.nextNode();
                      while (node) {
                        const t = node as Text;
                        if ((m as HTMLElement).contains(t)) {
                          if (!started) { start = acc; started = true; }
                          acc += t.data.length;
                          end = acc;
                        } else {
                          acc += t.data.length;
                        }
                        node = tw.nextNode();
                      }
                      if (start >= 0 && end > start) {
                        const plain = (blockEl.innerText || '');
                        const text = plain.slice(start, end);
                        if (cmd === 'fontSizePx') {
                          (actionsRef.current[blk.id] || (actionsRef.current[blk.id] = [])).push({ type: 'fontSize', px: Number(px), start, end, text } as any);
                        } else if (cmd === 'foreColor') {
                          (actionsRef.current[blk.id] || (actionsRef.current[blk.id] = [])).push({ type: 'color', hex: String(value), start, end, text } as any);
                        } else if (cmd === 'fontFamily') {
                          (actionsRef.current[blk.id] || (actionsRef.current[blk.id] = [])).push({ type: 'fontFamily', family: String(value), start, end, text } as any);
                        }
                      }
                    } catch {}
                  });
                });
              } catch {}
            }
            try {
              const s = window.getSelection();
              if (s && s.rangeCount > 0) lastSelRangeRef.current = s.getRangeAt(0).cloneRange();
            } catch {}
            return; // handled globally
          }
        } catch {}
      }
      return;
    }
    // Ensure the contentEditable element regains focus before applying the command
    const editableHost = el as HTMLElement;
    try { editableHost.focus({ preventScroll: true } as any); } catch {}
    // Re-apply the saved selection after focus to avoid the browser clearing it
    const savedAfterFocus = lastSelRangeRef.current;
    if (savedAfterFocus) {
      const s = window.getSelection();
      if (s) { s.removeAllRanges(); s.addRange(savedAfterFocus.cloneRange()); }
    }
    // If no selection and the command is alignment, select the entire block so alignment applies to whole block
    try {
      const isJustify = (cmd === 'justifyLeft' || cmd === 'justifyCenter' || cmd === 'justifyRight' || cmd === 'justifyFull');
      if (isJustify && !applyToSelectionOnlyRef.current) {
        const s = window.getSelection();
        const collapsed = !s || s.rangeCount === 0 || s.getRangeAt(0).collapsed;
        if (collapsed) {
          // Prefer the innermost contentEditable element within this block
          let target: HTMLElement | null = editableHost;
          if (!(target as any).isContentEditable) {
            const inner = editableHost.querySelector('[contenteditable="true"]') as HTMLElement | null;
            if (inner) target = inner;
          }
          if (target) {
            const r = document.createRange();
            // Select the entire block contents (non-collapsed) so execCommand aligns without inserting breaks
            r.selectNodeContents(target);
            const sel2 = window.getSelection();
            if (sel2) { sel2.removeAllRanges(); sel2.addRange(r); }
            try { lastSelRangeRef.current = r.cloneRange(); } catch {}
          }
        }
      }
    } catch {}
    // Before applying inline command, commit any pending shadow edits so typing is a separate undo step
    // This creates a checkpoint (via onChange) representing the state prior to styling
    commitEdits();
    // If there are any markers in the editor and we're changing font size or color, apply to all marked spans
    if ((cmd === 'fontSizePx' || cmd === 'foreColor' || cmd === 'fontFamily') && value) {
      try {
        const container = containerRef.current;
        if (container) {
          const spans = container.querySelectorAll('span[data-editor-marker]');
          if (spans.length > 0) {
            const px = String(value).replace(/[^0-9]/g, '');
            spans.forEach((node) => {
              const s = node as HTMLElement;
              if (cmd === 'fontSizePx') {
                try {
                  const sized = s.querySelectorAll('font[size], [style*="font-size"], [style*="line-height"]');
                  sized.forEach((e) => {
                    const el = e as HTMLElement;
                    if (el.tagName === 'FONT' && (el as any).getAttribute && (el as any).getAttribute('size')) {
                      const span = document.createElement('span');
                      while (el.firstChild) span.appendChild(el.firstChild);
                      el.replaceWith(span);
                    } else if ((el as any).style) {
                      try { (el as any).style.fontSize = ''; } catch {}
                      try { (el as any).style.lineHeight = ''; } catch {}
                    }
                  });
                } catch {}
                s.style.fontSize = `${px}px`; s.style.lineHeight = '1.3';
              }
              if (cmd === 'foreColor') {
                try {
                  // Clear descendant colors so marker color wins
                  const colored = s.querySelectorAll('[style*="color"], font[color]');
                  colored.forEach((e) => {
                    const el = e as HTMLElement;
                    try { (el as any).style && ((el as any).style.color = ''); } catch {}
                    if (el.tagName === 'FONT') { try { (el as any).removeAttribute('color'); } catch {} }
                  });
                } catch {}
                s.style.color = String(value);
              }
              if (cmd === 'fontFamily') {
                try {
                  const ff = s.querySelectorAll('[style*="font-family"]');
                  ff.forEach((e) => { try { (e as any).style && ((e as any).style.fontFamily = ''); } catch {} });
                } catch {}
                s.style.fontFamily = String(value);
              }
            });
            // Sync the entire model from DOM for blocks that changed
            const cur = modelRef.current;
            const blocks = cur.blocks.map(b => {
              const el = container.querySelector(`[data-block-id="${b.id}"]`) as HTMLElement | null;
              return el ? { ...b, text: el.innerHTML } : b;
            });
            const next = { ...cur, blocks };
            modelRef.current = next;
            setModel(next);
            onChange && onChange(next);
            // Record color/font-size action per block for each marker span
            try {
              blocks.forEach((blk) => {
                const blockEl = container.querySelector(`[data-block-id="${blk.id}"]`) as HTMLElement | null;
                if (!blockEl) return;
                const marked = blockEl.querySelectorAll('span[data-editor-marker]');
                if (marked.length === 0) return;
                marked.forEach((m) => {
                  try {
                    let acc = 0; let started = false; let start = -1; let end = -1;
                    const tw = document.createTreeWalker(blockEl, NodeFilter.SHOW_TEXT, null);
                    let node = tw.nextNode();
                    while (node) {
                      const t = node as Text;
                      if ((m as HTMLElement).contains(t)) {
                        if (!started) { start = acc; started = true; }
                        acc += t.data.length;
                        end = acc;
                      } else {
                        acc += t.data.length;
                      }
                      node = tw.nextNode();
                    }
                    if (start >= 0 && end > start) {
                      const plain = (blockEl.innerText || '');
                      const text = plain.slice(start, end);
                      if (cmd === 'fontSizePx') {
                        (actionsRef.current[blk.id] || (actionsRef.current[blk.id] = [])).push({ type: 'fontSize', px: Number(px), start, end, text } as any);
                      } else if (cmd === 'foreColor') {
                        (actionsRef.current[blk.id] || (actionsRef.current[blk.id] = [])).push({ type: 'color', hex: String(value), start, end, text } as any);
                      } else if (cmd === 'fontFamily') {
                        (actionsRef.current[blk.id] || (actionsRef.current[blk.id] = [])).push({ type: 'fontFamily', family: String(value), start, end, text } as any);
                      }
                    }
                  } catch {}
                });
              });
            } catch {}
            // Preserve selection snapshot for subsequent operations
            try {
              const s = window.getSelection();
              if (s && s.rangeCount > 0) {
                lastSelRangeRef.current = s.getRangeAt(0).cloneRange();
              }
            } catch {}
            return; // handled marker styling; skip execCommand path
          }
        }
      } catch {}
    }
    // Capture selection offsets for color or font-size action tracking
    let colorSelOffsets: { start: number; end: number } | null = null;
    let colorSelText: string | null = null;
    let sizeSelOffsets: { start: number; end: number } | null = null;
    let sizeSelText: string | null = null;
    const computeOffsets = () => {
      const s = window.getSelection();
      if (!s || s.rangeCount === 0) return null;
      const r = s.getRangeAt(0);
      const walker = document.createTreeWalker(editableHost, NodeFilter.SHOW_TEXT, null);
      let acc = 0;
      let start = -1, end = -1;
      let node = walker.nextNode();
      while (node) {
        const t = node as Text;
        if (t === r.startContainer) start = acc + r.startOffset;
        if (t === r.endContainer) { end = acc + r.endOffset; break; }
        acc += t.data.length;
        node = walker.nextNode();
      }
      if (start >= 0 && end >= 0 && end > start) return { start, end };
      return null;
    };
    if (cmd === 'foreColor' && value) {
      try {
        colorSelOffsets = computeOffsets();
        const s = window.getSelection();
        colorSelText = s && s.rangeCount > 0 ? s.toString() : null;
      } catch {}
    }
    if (cmd === 'fontSizePx' && value) {
      try {
        sizeSelOffsets = computeOffsets();
        const s = window.getSelection();
        sizeSelText = s && s.rangeCount > 0 ? s.toString() : null;
      } catch {}
    }
    const runExec = () => {
      const editableHost = el as HTMLElement;
      // Pre-handle list toggling: if inside a matching list, unwrap instead of relying on execCommand
      if (cmd === 'insertUnorderedList' || cmd === 'insertOrderedList') {
        try {
          const sel = window.getSelection();
          if (sel && sel.rangeCount > 0) {
            let node: Node | null = sel.getRangeAt(0).startContainer as Node;
            if (node.nodeType === 3) node = (node as Text).parentElement as any;
            const li = (node as Element)?.closest?.('li');
            const list = (node as Element)?.closest?.('ul,ol') as HTMLElement | null;
            if (list && li) {
              const isUL = list.tagName.toLowerCase() === 'ul';
              const wantUL = (cmd === 'insertUnorderedList');
              if ((isUL && wantUL) || (!isUL && !wantUL)) {
                // unwrap the entire list into plain lines separated by <br>
                const frag = document.createDocumentFragment();
                const items = Array.from(list.querySelectorAll(':scope > li')) as HTMLElement[];
                items.forEach((item, idx) => {
                  // move children of li into a span wrapper to preserve inline styles
                  const span = document.createElement('span');
                  while (item.firstChild) span.appendChild(item.firstChild);
                  frag.appendChild(span);
                  if (idx < items.length - 1) frag.appendChild(document.createElement('br'));
                });
                list.replaceWith(frag);
                // Sync model from editable host
                try {
                  const html = editableHost.innerHTML;
                  const cur = modelRef.current;
                  const blocks = cur.blocks.map(b => b.id === id ? { ...b, text: html } : b);
                  modelRef.current = { ...cur, blocks };
                  const evt = new Event('input', { bubbles: true });
                  editableHost.dispatchEvent(evt);
                  commitEdits();
                } catch {}
                // Done; skip execCommand path
                return;
              }
            }
          }
        } catch {}
      }
      if (cmd === 'bold') document.execCommand('bold');
      if (cmd === 'italic') document.execCommand('italic');
      if (cmd === 'underline') document.execCommand('underline');
      if (cmd === 'justifyLeft') document.execCommand('justifyLeft');
      if (cmd === 'justifyCenter') document.execCommand('justifyCenter');
      if (cmd === 'justifyRight') document.execCommand('justifyRight');
      if (cmd === 'justifyFull') document.execCommand('justifyFull');
      if (cmd === 'insertUnorderedList') document.execCommand('insertUnorderedList');
      if (cmd === 'insertOrderedList') document.execCommand('insertOrderedList');
      if (cmd === 'foreColor' && value) document.execCommand('foreColor', false, value);
    // fontFamily is handled via marker updates; we avoid execCommand for family
      if (cmd === 'fontSizePx' && value) {
        // Use legacy command to create wrappers, then normalize to spans with CSS font-size
        try { document.execCommand('fontSize', false, '7'); } catch {}
        try {
          const editableHost = el as HTMLElement;
          const px = String(value).replace(/[^0-9]/g, '');
          const fonts = editableHost.querySelectorAll('font[size="7"]');
          fonts.forEach((f) => {
            const span = document.createElement('span');
            span.style.fontSize = `${px}px`;
            // Optionally a slightly tighter line-height looks better for large sizes
            span.style.lineHeight = '1.3';
            // Move children
            while (f.firstChild) span.appendChild(f.firstChild);
            f.replaceWith(span);
          });
        } catch {}
      }
      // If a list command just ran, normalize list styling so bullets/numbers and indentation are visible
      if (cmd === 'insertUnorderedList' || cmd === 'insertOrderedList') {
        try {
          const selector = cmd === 'insertUnorderedList' ? 'ul' : 'ol';
          const lists = editableHost ? editableHost.querySelectorAll(selector) : [] as any;
          (lists as any).forEach((n: Element) => {
            const L = n as HTMLElement;
            L.style.listStyleType = selector === 'ul' ? 'disc' : 'decimal';
            L.style.listStylePosition = 'outside';
            L.style.paddingLeft = '1.25rem';
            L.style.marginLeft = '0';
            try {
              const items = L.querySelectorAll('li');
              items.forEach(li => { (li as HTMLElement).style.listStyle = 'inherit'; });
            } catch {}
          });
        } catch {}
      }
      // Save current selection as last known and force input event so our shadow model updates immediately
      try {
        const s = window.getSelection();
        if (s && s.rangeCount > 0) {
          lastSelRangeRef.current = s.getRangeAt(0).cloneRange();
        }
        // Update shadow model directly from editable host to guarantee a diff exists
        const html = editableHost.innerHTML;
        const cur = modelRef.current;
        const blocks = cur.blocks.map(b => b.id === id ? { ...b, text: html } : b);
        modelRef.current = { ...cur, blocks };
        // Also dispatch input so any other listeners update
        const evt = new Event('input', { bubbles: true });
        editableHost.dispatchEvent(evt);
        // Immediately commit shadow edits so the active block reflects changes without requiring blur
        // This keeps the caret/focus intact while syncing the model and notifying parent
        commitEdits();
        // Record color action with offsets so we can undo precisely
        if (cmd === 'foreColor' && value) {
          try {
            const off = colorSelOffsets || computeOffsets();
            if (off && off.end > off.start) {
              (actionsRef.current[id] || (actionsRef.current[id] = [])).push({ type: 'color', hex: value, start: off.start, end: off.end, text: (colorSelText || '').trim() });
            }
          } catch {}
        }
        if (cmd === 'fontFamily' && value) {
          try {
            const off = computeOffsets();
            const sel = window.getSelection();
            const txt = sel && sel.rangeCount > 0 ? sel.toString() : '';
            if (off && off.end > off.start) {
              (actionsRef.current[id] || (actionsRef.current[id] = [])).push({ type: 'fontFamily', family: String(value), start: off.start, end: off.end, text: (txt || '').trim() });
            }
          } catch {}
        }
        if (cmd === 'fontSizePx' && value) {
          try {
            const off = sizeSelOffsets || computeOffsets();
            if (off && off.end > off.start) {
              (actionsRef.current[id] || (actionsRef.current[id] = [])).push({ type: 'fontSize', px: Number(String(value).replace(/[^0-9]/g, '')), start: off.start, end: off.end, text: (sizeSelText || '').trim() });
            }
          } catch {}
        }
        if (cmd === 'justifyLeft' || cmd === 'justifyCenter' || cmd === 'justifyRight' || cmd === 'justifyFull') {
          try {
            const off = computeOffsets();
            const selNow = window.getSelection();
            const txt = selNow && selNow.rangeCount > 0 ? selNow.toString() : '';
            const plain = (editableHost.innerText || '');
            const start = off ? off.start : 0;
            const end = off ? off.end : plain.length;
            const block = off ? (off.start === 0 && off.end >= plain.length) : true;
            const mode = (cmd === 'justifyLeft' ? 'left' : cmd === 'justifyRight' ? 'right' : cmd === 'justifyCenter' ? 'center' : 'justify');
            (actionsRef.current[id] || (actionsRef.current[id] = [])).push({ type: 'align', mode, start, end, block, text: (txt || '').trim() } as any);
          } catch {}
        }
      } catch {}
    };
    // Defer inline commands to the next frame so focus/selection restoration settles
    if (cmd === 'bold' || cmd === 'italic' || cmd === 'underline' || cmd === 'foreColor' || cmd === 'fontSizePx' || cmd === 'insertUnorderedList' || cmd === 'insertOrderedList') {
      requestAnimationFrame(runExec);
    } else {
      runExec();
    }
    if (cmd === 'h1') updateBlock(id, { type: 'heading1' });
    if (cmd === 'h2') updateBlock(id, { type: 'heading2' });
    if (cmd === 'paragraph') updateBlock(id, { type: 'paragraph' });
    if (cmd === 'quote') updateBlock(id, { type: 'quote' });
    if (cmd === 'image') insertBlockAfter(id, 'image');
    if (cmd === 'code') insertBlockAfter(id, 'code');
  };

  useImperativeHandle(ref, () => ({
    inline: (a) => applyCmd(a),
    setColor: (hex) => applyCmd('foreColor', hex),
    setFontSize: (px: number) => applyCmd('fontSizePx', String(px)),
    setFontFamily: (family: string) => applyCmd('fontFamily', family),
    setApplyToSelectionOnly: (v: boolean) => { applyToSelectionOnlyRef.current = !!v; },
    setAutoBullet: (v: boolean) => { autoBulletRef.current = !!v; if (v) autoNumberRef.current = false; },
    setAutoNumber: (v: boolean) => { autoNumberRef.current = !!v; if (v) autoBulletRef.current = false; },
    addMarker: () => {
      const host = containerRef.current; if (!host) return;
      // Restore selection within editor if needed
      const saved = lastSelRangeRef.current;
      if (saved) {
        const s = window.getSelection(); if (s) { s.removeAllRanges(); s.addRange(saved.cloneRange()); }
      }
      const sel = window.getSelection(); if (!sel || sel.rangeCount === 0) return;
      const r = sel.getRangeAt(0);
      // Ensure selection lies within a single editable block
      let startEl: HTMLElement | null = r.startContainer as any;
      while (startEl && startEl.nodeType === 3) startEl = startEl.parentElement as any;
      let endEl: HTMLElement | null = r.endContainer as any;
      while (endEl && endEl.nodeType === 3) endEl = endEl.parentElement as any;
      if (!startEl || !endEl) return;
      const startHost = (startEl.closest('[data-block-id]') as HTMLElement | null);
      const endHost = (endEl.closest('[data-block-id]') as HTMLElement | null);
      if (!startHost || !endHost || startHost !== endHost) return;
      const id = startHost.dataset.blockId as string;
      try { (startHost as any).focus?.({ preventScroll: true }); } catch {}
      // Try to wrap selection in a marker span
      const span = document.createElement('span');
      span.setAttribute('data-editor-marker', '1');
      span.className = 'bg-blue-100 ring-2 ring-blue-300/60 rounded-sm px-0.5 -mx-[1px]';
      try {
        try {
          r.surroundContents(span);
        } catch {
          const frag = r.cloneContents();
          span.appendChild(frag);
          r.deleteContents();
          r.insertNode(span);
        }
        // Move caret to end of marker for a smooth UX
        const s = window.getSelection();
        if (s) { s.removeAllRanges(); const nr = document.createRange(); nr.selectNodeContents(span); nr.collapse(false); s.addRange(nr); }
      } catch {}
      // Sync model from editable host and commit
      const html = startHost.innerHTML;
      const cur = modelRef.current;
      const blocks = cur.blocks.map(b => b.id === id ? { ...b, text: html } : b);
      modelRef.current = { ...cur, blocks };
      onChange && onChange(modelRef.current);
      setModel(modelRef.current);
    },
    clearMarkers: () => {
      const host = containerRef.current; if (!host) return;
      const spans = host.querySelectorAll('span[data-editor-marker]');
      if (spans.length === 0) return;
      spans.forEach((sp) => {
        const s = sp as HTMLElement;
        // If the marker span carries inline styles (font-size, line-height, color), preserve them by replacing
        // the marker with a regular span that keeps those styles. Otherwise unwrap as before.
        const hasInlineSize = !!(s.style && (s.style.fontSize || s.style.lineHeight));
        const hasInlineColor = !!(s.style && s.style.color);
        const hasInlineFamily = !!(s.style && s.style.fontFamily);
        const parent = s.parentNode; if (!parent) return;
        if (hasInlineSize || hasInlineColor || hasInlineFamily) {
          const keep = document.createElement('span');
          if (s.style.fontSize) keep.style.fontSize = s.style.fontSize;
          if (s.style.lineHeight) keep.style.lineHeight = s.style.lineHeight;
          if (s.style.color) keep.style.color = s.style.color;
          if (s.style.fontFamily) keep.style.fontFamily = s.style.fontFamily;
          while (s.firstChild) keep.appendChild(s.firstChild);
          parent.replaceChild(keep, s);
        } else {
          while (s.firstChild) parent.insertBefore(s.firstChild, s);
          parent.removeChild(s);
        }
      });
      // Update model from DOM
      const cur = modelRef.current;
      const blocks = cur.blocks.map(b => {
        const el = host.querySelector(`[data-block-id="${b.id}"]`) as HTMLElement | null;
        return el ? { ...b, text: el.innerHTML } : b;
      });
      const next = { ...cur, blocks };
      modelRef.current = next;
      setModel(next);
      onChange && onChange(next);
    },
    hasMarkers: () => {
      const host = containerRef.current; if (!host) return false;
      return host.querySelector('span[data-editor-marker]') != null;
    },
    getMarkedLineCount: () => {
      try {
        const host = containerRef.current; if (!host) return 0;
        const spans = host.querySelectorAll('span[data-editor-marker]');
        if (!spans || spans.length === 0) return 0;
        let total = 0;
        spans.forEach((node) => {
          const text = (node as HTMLElement).innerText || '';
          const norm = text.replace(/\r\n/g, '\n');
          if (norm.length === 0) return;
          const lines = norm.split('\n');
          total += lines.length;
        });
        return total;
      } catch { return 0; }
    },
    getSelectionLineCount: () => {
      try {
        const host = containerRef.current; if (!host) return 0;
        const s = window.getSelection();
        if (!s || s.rangeCount === 0 || s.isCollapsed) return 0;
        const r = s.getRangeAt(0);
        if (!host.contains(r.startContainer)) return 0;
        // Clone selected contents into a temporary element to get visual innerText with line breaks
        const frag = r.cloneContents();
        const div = document.createElement('div');
        div.appendChild(frag);
        const txt = (div.innerText || div.textContent || '').replace(/\r\n/g, '\n');
        if (!txt) return 0;
        const lines = txt.split('\n');
        return lines.length;
      } catch { return 0; }
    },
    block: (t) => {
      // If a block is selected, transform it via applyCmd; otherwise append at end
      if (selectedId) {
        applyCmd(t === 'paragraph' ? 'paragraph' : t === 'quote' ? 'quote' : t === 'heading1' ? 'h1' : 'h2');
        return;
      }
      const cur = modelRef.current;
      const b = createBlock(t as any);
      const blocks = [...cur.blocks, b];
      const next = { ...cur, blocks };
      modelRef.current = next; setModel(next); onChange && onChange(next);
      setSelectedId(b.id);
      requestAnimationFrame(() => focusEditableById(b.id));
    },
    insert: (t) => {
      // Insert a non-text block. If a block is selected, insert after it; otherwise append.
      const cur0 = modelRef.current;
      const type = t as any as BlockType;
      const b = createBlock(type);
      if (selectedId) {
        const idx = cur0.blocks.findIndex(bl => bl.id === selectedId);
        if (idx >= 0) {
          const blocks = [...cur0.blocks.slice(0, idx + 1), b, ...cur0.blocks.slice(idx + 1)];
          const next = { ...cur0, blocks };
          modelRef.current = next; setModel(next); onChange && onChange(next);
          setSelectedId(b.id);
          requestAnimationFrame(() => focusEditableById(b.id));
          return;
        }
      }
      const blocks = [...cur0.blocks, b];
      const next = { ...cur0, blocks };
      modelRef.current = next; setModel(next); onChange && onChange(next);
      setSelectedId(b.id);
      requestAnimationFrame(() => focusEditableById(b.id));
    },
    insertImage: (url: string) => {
      const cur = modelRef.current;
      const id = selectedId;
      let idx = id ? cur.blocks.findIndex(b => b.id === id) : -1;
      if (idx < 0) idx = cur.blocks.length - 1;
      const newBlock: PostBlock = { id: newId(), type: 'image', src: url };
      const blocks = [
        ...cur.blocks.slice(0, idx + 1),
        newBlock,
        ...cur.blocks.slice(idx + 1),
      ];
      const next = { ...cur, blocks };
      modelRef.current = next;
      setModel(next);
      onChange && onChange(next);
      setSelectedId(newBlock.id);
    },
    insertImageAtWithGroup: (url: string, index: number, groupTargetId?: string | null, side?: 'left' | 'right' | null) => {
      const cur = modelRef.current;
      let blocks = [...cur.blocks];
      let clamped = Math.max(0, Math.min(index, blocks.length));
      const newBlock: PostBlock = { id: newId(), type: 'image', src: url };
      if (groupTargetId) {
        const tIdx = blocks.findIndex(b => b.id === groupTargetId);
        if (tIdx !== -1) {
          const target = { ...blocks[tIdx] } as PostBlock;
          let grp = target.group || newId();
          target.group = grp;
          blocks[tIdx] = target;
          newBlock.group = grp;
          clamped = side === 'left' ? tIdx : (side === 'right' ? tIdx + 1 : clamped);
        }
      }
      const nextBlocks = [
        ...blocks.slice(0, clamped),
        newBlock,
        ...blocks.slice(clamped),
      ];
      const next = { ...cur, blocks: nextBlocks };
      modelRef.current = next;
      setModel(next);
      onChange && onChange(next);
      setSelectedId(newBlock.id);
    },
    insertImageAt: (url: string, index: number) => {
      const cur = modelRef.current;
      const clamped = Math.max(0, Math.min(index, cur.blocks.length));
      const newBlock: PostBlock = { id: newId(), type: 'image', src: url };
      const blocks = [
        ...cur.blocks.slice(0, clamped),
        newBlock,
        ...cur.blocks.slice(clamped),
      ];
      const next = { ...cur, blocks };
      modelRef.current = next;
      setModel(next);
      onChange && onChange(next);
      setSelectedId(newBlock.id);
    },
    getModel: () => (modelRef.current),
    endEditing: () => {
      const host = containerRef.current;
      if (!host) return;
      const active = document.activeElement as HTMLElement | null;
      if (active && host.contains(active)) {
        try { active.blur(); } catch {}
      }
      isEditingRef.current = false;
      commitEdits();
    },
    commitPending: () => {
      // commit without changing focus/caret, but allow external sync
      // Do NOT toggle isEditingRef here; background autosave must not interrupt typing
      commitEdits();
    },
    restoreFocus: () => {
      const host = containerRef.current; if (!host) return;
      // Attempt to restore the saved selection if it targets an element within the editor
      const saved = lastSelRangeRef.current;
      if (saved) {
        const s = window.getSelection();
        if (s) { s.removeAllRanges(); s.addRange(saved.cloneRange()); }
        try { (saved.startContainer as any)?.parentElement?.focus?.({ preventScroll: true }); } catch {}
        return;
      }
      // Fallback: focus currently selected block by id
      const id = selectedId; if (!id) return;
      focusEditableById(id);
    },
    getActiveBlockId: () => selectedId,
    setActiveBlockId: (id: string, caret: 'start' | 'end' = 'end') => {
      if (!id) return;
      setSelectedId(id);
      requestAnimationFrame(() => focusEditableById(id, caret));
    },
    deleteSelected: () => {
      const id = selectedId; if (!id) return;
      const cur = modelRef.current;
      const blocks = cur.blocks.filter(b => b.id !== id);
      const next = { ...cur, blocks };
      modelRef.current = next;
      setModel(next);
      onChange && onChange(next);
      setSelectedId(null);
    },
  }));

  const onBlockInput = (id: string, e: React.FormEvent<HTMLElement>) => {
    selfEditRef.current = true;
    const html = (e.currentTarget as HTMLElement).innerHTML;
    // Update shadow model only
    const cur = modelRef.current;
    const blocks = cur.blocks.map(b => b.id === id ? { ...b, text: html } : b);
    const next = { ...cur, blocks };
    modelRef.current = next;
    // If this input is a native history undo/redo, immediately commit so app-level history captures it as a granular step
    const ne = (e.nativeEvent as any);
    const it: string | undefined = ne && typeof ne.inputType === 'string' ? ne.inputType : undefined;
    if (it === 'historyUndo' || it === 'historyRedo') {
      commitEdits();
    }
    // otherwise, do not notify parent during typing; flushed on blur
  };

  const onPasteAsPlain = (e: React.ClipboardEvent<HTMLElement>) => {
    const text = e.clipboardData.getData('text/plain');
    e.preventDefault();
    document.execCommand('insertText', false, text);
  };

  // Uncontrolled editable wrappers to avoid caret jumps
  const EditableBlock: React.FC<{ b: PostBlock; idx: number }> = ({ b, idx }) => {
    const elRef = useRef<HTMLDivElement | null>(null);
    const localRef = useRef(false);
    const [saveHint, setSaveHint] = useState<{ x: number; y: number; visible: boolean }>({ x: 0, y: 0, visible: false });
    const saveHintTimer = useRef<number | null>(null);
    const prevWordCountRef = useRef<number>(0);
    const [stackOpen, setStackOpen] = useState<boolean>(!!stackPersistRef.current[b.id]);
    const [undoMenuOpen, setUndoMenuOpen] = useState(false);
    const [stackPos, setStackPos] = useState<{x:number;y:number}>(stackPosPersistRef.current[b.id] ?? { x: -4, y: -36 });
    const dragRef = useRef<{startX:number; startY:number; origX:number; origY:number; moving:boolean}>({ startX:0, startY:0, origX:0, origY:0, moving:false });
    const onStartDrag = (e: React.MouseEvent) => {
      e.preventDefault(); e.stopPropagation();
      dragRef.current = { startX: e.clientX, startY: e.clientY, origX: stackPos.x, origY: stackPos.y, moving: true };
      const onMove = (ev: MouseEvent) => {
        if (!dragRef.current.moving) return;
        const dx = ev.clientX - dragRef.current.startX;
        const dy = ev.clientY - dragRef.current.startY;
        setStackPos({ x: dragRef.current.origX + dx, y: dragRef.current.origY + dy });
      };
      const onUp = () => {
        dragRef.current.moving = false;
        window.removeEventListener('mousemove', onMove);
        window.removeEventListener('mouseup', onUp);
      };
      window.addEventListener('mousemove', onMove);
      window.addEventListener('mouseup', onUp);
    };
    useEffect(() => {
      stackPersistRef.current[b.id] = stackOpen;
    }, [stackOpen, b.id]);
    useEffect(() => {
      stackPosPersistRef.current[b.id] = stackPos;
    }, [stackPos, b.id]);
    const [actionsVersion, setActionsVersion] = useState(0);
    const prevPlainRef = useRef<string>('');
    const countWords = (s: string) => {
      const t = s.replace(/\u00A0/g, ' ').trim();
      if (!t) return 0;
      const parts = t.split(/\s+/);
      // Count tokens that contain at least one ASCII letter or number. This avoids unicode property escapes.
      let n = 0;
      for (const p of parts) {
        if (/[A-Za-z0-9]/.test(p)) n += 1;
      }
      return n;
    };
    useEffect(() => {
      return () => { if (saveHintTimer.current) { window.clearTimeout(saveHintTimer.current); saveHintTimer.current = null; } };
    }, []);
    // Sync external html only if different and not during local edit
    useEffect(() => {
      const el = elRef.current; if (!el) return;
      if (localRef.current) return;
      if (document.activeElement === el) return; // do not clobber while typing
      const html = b.text || '';
      if (el.innerHTML !== html) el.innerHTML = html;
      // Initialize previous word count from current content
      try { prevWordCountRef.current = countWords(el.innerText || ''); } catch {}
      // Initialize token baseline and stack storage for this block
      try {
        const textNow = (el.innerText || '').replace(/\u00A0/g, ' ').trim();
        prevTokensRef.current[b.id] = textNow ? textNow.split(/\s+/) : [];
        if (!wordStacksRef.current[b.id]) wordStacksRef.current[b.id] = [];
        const fullNow = (el.innerText || '').replace(/\u00A0/g, ' ');
        prevEndedBoundaryRef.current[b.id] = /[\s.,!?:;]$/.test(fullNow);
        prevPlainRef.current = fullNow;
      } catch {}
    }, [b.id, b.type, b.text]);
    const handleInput = (e: React.FormEvent<HTMLDivElement>) => {
      localRef.current = true;
      onBlockInput(b.id, e as any);
      // After the DOM reflects the input, detect word completion on boundaries and maybe show hint
      // Capture native inputType and data BEFORE RAF to avoid React event pooling
      const nev: any = (e.nativeEvent as any);
      const itCaptured: string | undefined = nev && typeof nev.inputType === 'string' ? nev.inputType : undefined;
      const dataCaptured: string | undefined = nev && typeof nev.data === 'string' ? nev.data : undefined;
      requestAnimationFrame(() => {
        localRef.current = false;
        const el = elRef.current; if (!el) return;
        const text = el.innerText || '';
        const it: string | undefined = itCaptured;
        const data: string | undefined = dataCaptured;
        const boundaryChars = [' ', '.', ',', '!', '?', ';', ':', '\n'];
        const isDeletion = it ? it.startsWith('delete') : false;
        const isHistory = it === 'historyUndo' || it === 'historyRedo';
        let preBreakWordPushed = false;
        const isBoundary =
          (it === 'insertParagraph' || it === 'insertLineBreak') ||
          (it === 'insertText' && !!data && (boundaryChars.includes(data) || /\s/.test(data)));
        const plain = text.replace(/\u00A0/g, ' ');
        const endsWithBoundary = /[\s.,!?:;]$/.test(plain);
        // If we are performing a programmatic edit (undo/reapply), do not record new actions
        if (!suppressActionsRef.current) {
        // Detect deletion by diffing previous and current plain text
        if (!isHistory && (isDeletion || it === 'deleteByCut')) {
          const prevPlain = prevPlainRef.current || '';
          if (prevPlain.length > plain.length) {
            let i = 0;
            while (i < prevPlain.length && i < plain.length && prevPlain[i] === plain[i]) i++;
            let j = 0;
            while (j < prevPlain.length - i && j < plain.length - i && prevPlain[prevPlain.length - 1 - j] === plain[plain.length - 1 - j]) j++;
            const removed = prevPlain.slice(i, prevPlain.length - j);
            if (removed.length > 0) {
              (actionsRef.current[b.id] || (actionsRef.current[b.id] = [])).push({ type: 'delete', text: removed, start: i, end: i + removed.length } as any);
              setActionsVersion(v=>v+1);
            }
          }
        }
        // Handle explicit line break actions, but if we just finished a word, record the word BEFORE the break
        if (!isDeletion && !isHistory && (it === 'insertParagraph' || it === 'insertLineBreak')) {
          const stack = wordStacksRef.current[b.id] || (wordStacksRef.current[b.id] = []);
          // If the previous state did NOT end with a boundary, we just finished typing a word then pressed Enter.
          // Record that word first so ordering in the action stack is [word] then [↵].
          if (!prevEndedBoundaryRef.current[b.id]) {
            const tokensPreBreak = (plain.trim() || '').split(/\s+/).filter(Boolean);
            const lastWord = tokensPreBreak[tokensPreBreak.length - 1] || '';
            if (lastWord && /[A-Za-z0-9]/.test(lastWord)) {
              stack.push(lastWord);
              let startIdx = -1;
              try { startIdx = plain.lastIndexOf(lastWord); } catch {}
              const endIdx = startIdx >= 0 ? startIdx + lastWord.length : -1;
              (actionsRef.current[b.id] || (actionsRef.current[b.id] = [])).push({ type: 'word', text: lastWord, start: Math.max(0, startIdx), end: Math.max(0, endIdx) } as any);
              preBreakWordPushed = true;
            }
          }
          stack.push('__BR__');
          // Determine insertion offset by diffing prev vs current plain text
          const prevPlain = prevPlainRef.current || '';
          let insStart = 0; let insText = '';
          if (plain.length >= prevPlain.length) {
            let i = 0; while (i < prevPlain.length && i < plain.length && prevPlain[i] === plain[i]) i++;
            let j = 0; while (j < prevPlain.length - i && j < plain.length - i && prevPlain[prevPlain.length - 1 - j] === plain[plain.length - 1 - j]) j++;
            insStart = i; insText = plain.slice(i, plain.length - j);
          }
          const brIndex = insText.indexOf('\n');
          const start = brIndex >= 0 ? insStart + brIndex : insStart;
          const nth = (plain.slice(0, start).match(/\n/g) || []).length;
          const ctxB = plain.slice(Math.max(0, start - 10), start);
          const isNewLine = (prevPlain.length === 0) || (/\n$/.test(prevPlain));
          (actionsRef.current[b.id] || (actionsRef.current[b.id] = [])).push({ type: 'br', start, end: start + 1, nth, ctxB, newLine: isNewLine });
          setActionsVersion(v=>v+1);
          const sel = window.getSelection();
          if (sel && sel.rangeCount > 0) {
            const r = sel.getRangeAt(0).cloneRange();
            let rect = r.getBoundingClientRect();
            if (rect.width === 0 && rect.height === 0) {
              const node = r.startContainer as any;
              const elNode: Element | null = node && node.nodeType === 3 ? node.parentElement : (node as Element);
              if (elNode) rect = elNode.getBoundingClientRect();
            }
            const host = elRef.current; if (host) {
              const hostRect = host.getBoundingClientRect();
              const x = Math.max(0, Math.min(rect.left - hostRect.left, host.clientWidth - 16));
              const y = Math.max(0, Math.min(rect.top - hostRect.top - 10, host.clientHeight - 16));
              setSaveHint({ x, y, visible: true });
              if (saveHintTimer.current) window.clearTimeout(saveHintTimer.current);
              saveHintTimer.current = window.setTimeout(() => { setSaveHint((s) => ({ ...s, visible: false })); }, 1200);
            }
          }
        }
        if (!isDeletion && !isHistory && isBoundary && endsWithBoundary && !prevEndedBoundaryRef.current[b.id] && !preBreakWordPushed) {
          const tokens = (plain.trim() || '').split(/\s+/).filter(Boolean);
          const completedWord = tokens[tokens.length - 1] || '';
          if (completedWord && /[A-Za-z0-9]/.test(completedWord)) {
            const stack = wordStacksRef.current[b.id] || (wordStacksRef.current[b.id] = []);
            stack.push(completedWord);
            // Compute start/end for this completed word (last occurrence)
            let startIdx = -1;
            try {
              startIdx = plain.lastIndexOf(completedWord);
            } catch {}
            const endIdx = startIdx >= 0 ? startIdx + completedWord.length : -1;
            (actionsRef.current[b.id] || (actionsRef.current[b.id] = [])).push({ type: 'word', text: completedWord, start: Math.max(0, startIdx), end: Math.max(0, endIdx) } as any);
            setActionsVersion(v=>v+1);
            const sel = window.getSelection();
            if (sel && sel.rangeCount > 0) {
              const r = sel.getRangeAt(0).cloneRange();
              let rect = r.getBoundingClientRect();
              if (rect.width === 0 && rect.height === 0) {
                const node = r.startContainer as any;
                const elNode: Element | null = node && node.nodeType === 3 ? node.parentElement : (node as Element);
                if (elNode) rect = elNode.getBoundingClientRect();
              }
              const host = elRef.current; if (!host) return;
              const hostRect = host.getBoundingClientRect();
              const x = Math.max(0, Math.min(rect.left - hostRect.left, host.clientWidth - 16));
              const y = Math.max(0, Math.min(rect.top - hostRect.top - 10, host.clientHeight - 16));
              setSaveHint({ x, y, visible: true });
              if (saveHintTimer.current) window.clearTimeout(saveHintTimer.current);
              saveHintTimer.current = window.setTimeout(() => {
                setSaveHint((s) => ({ ...s, visible: false }));
              }, 1200);
            }
          }
        }
        }
        // Update baselines
        const nt = (plain.trim() || '').split(/\s+/).filter(Boolean);
        prevTokensRef.current[b.id] = nt;
        prevWordCountRef.current = nt.length;
        prevEndedBoundaryRef.current[b.id] = endsWithBoundary;
        prevPlainRef.current = plain;
      });
    };
    const finalizePendingWord = () => {
      const el = elRef.current; if (!el) return;
      const plain = (el.innerText || '').replace(/\u00A0/g, ' ');
      const endsWithBoundary = /[\s.,!?:;]$/.test(plain);
      if (!endsWithBoundary) {
        const tokens = (plain.trim() || '').split(/\s+/).filter(Boolean);
        const last = tokens[tokens.length - 1] || '';
        if (last && /[A-Za-z0-9]/.test(last)) {
          const stack = wordStacksRef.current[b.id] || (wordStacksRef.current[b.id] = []);
          stack.push(last);
          const startIdx = Math.max(0, plain.lastIndexOf(last));
          const endIdx = startIdx + last.length;
          (actionsRef.current[b.id] || (actionsRef.current[b.id] = [])).push({ type: 'word', text: last, start: startIdx, end: endIdx } as any);
          prevEndedBoundaryRef.current[b.id] = true;
        }
      }
      const nt = (plain.trim() || '').split(/\s+/).filter(Boolean);
      prevTokensRef.current[b.id] = nt;
      prevWordCountRef.current = nt.length;
      prevPlainRef.current = plain;
    };
    const undoBrAction = (act: any) => {
      const el = elRef.current; if (!el) return;
      // Resolve which newline to delete using context or nth or start
      const plain = (el.innerText || '');
      const findNth = (n: number) => {
        let count = 0; for (let i = 0; i < plain.length; i++) { if (plain[i] === '\n') { if (count === n) return i; count++; } }
        return -1;
      };
      let idx = -1;
      if (act && typeof act.ctxB === 'string' && act.ctxB.length > 0) {
        const pos = plain.lastIndexOf(act.ctxB);
        if (pos >= 0) {
          const after = plain.indexOf('\n', pos + act.ctxB.length);
          if (after >= 0) idx = after;
        }
      }
      if (idx < 0 && typeof act.nth === 'number') {
        const nthIdx = findNth(act.nth);
        if (nthIdx >= 0) idx = nthIdx;
      }
      if (idx < 0 && typeof act.start === 'number') idx = Math.max(0, Math.min(act.start, plain.length - 1));
      // Place caret just before idx and delete forward one char
      const mapToDom = (pos: number): { node: Text | null; off: number } => {
        const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT, null);
        let acc = 0; let node = walker.nextNode();
        while (node) {
          const t = node as Text; const nextAcc = acc + t.data.length;
          if (pos <= nextAcc) {
            return { node: t, off: Math.max(0, t.data.length - (nextAcc - pos)) };
          }
          acc = nextAcc; node = walker.nextNode();
        }
        return { node: null, off: 0 };
      };
      const mapPos = Math.max(0, idx - 1);
      const { node: targetNode, off: targetOffset } = mapToDom(mapPos);
      const sel = window.getSelection();
      if (targetNode) {
        const r = document.createRange(); r.setStart(targetNode, targetOffset); r.collapse(true);
        if (sel) { sel.removeAllRanges(); sel.addRange(r); }
      } else {
        const r = document.createRange(); r.selectNodeContents(el); r.collapse(false); if (sel) { sel.removeAllRanges(); sel.addRange(r); }
      }
      // Extend forward one character, but delete ONLY if it's a newline; otherwise attempt to remove a <br> node.
      suppressActionsRef.current = true;
      if (sel && (sel as any).modify) {
        (sel as any).modify('extend','forward','character');
        const toDel = (sel && sel.rangeCount>0) ? sel.toString() : '';
        if (toDel === '\n') {
          document.execCommand('delete');
        } else {
          // Collapse and try to remove a BR element explicitly
          const cr = sel.getRangeAt(0).cloneRange();
          cr.collapse(true);
          sel.removeAllRanges(); sel.addRange(cr);
          const anchor = cr.startContainer as Node;
          const root = el;
          // Look for a br at or immediately after the caret
          let br: HTMLBRElement | null = null;
          if (anchor.nodeType === 1) {
            const elNode = anchor as Element;
            br = (elNode.childNodes[cr.startOffset] as any) instanceof HTMLBRElement ? (elNode.childNodes[cr.startOffset] as HTMLBRElement) : null;
          }
          if (!br) {
            // Fallback: query the closest br in the subtree and ensure it's adjacent
            const all = Array.from(root.querySelectorAll('br'));
            br = all.find(Boolean) || null;
          }
          if (br && br.parentNode) {
            br.parentNode.removeChild(br);
          }
        }
      }
      // If this BR represented a blank line, there may still be a second newline left.
      // Collapse any remaining double-newline and trim the stray space often left behind.
      try {
        let plainNow = (el.innerText || '').replace(/\u00A0/g, ' ');
        // Detect sequences around the original position
        const windowStart = Math.max(0, Math.min(plainNow.length, Math.max(0, (idx || 0) - 1)));
        const windowSlice = plainNow.slice(windowStart, Math.min(plainNow.length, windowStart + 3));
        const hasDoubleNl = /\n\n/.test(windowSlice);
        if (act && act.newLine && hasDoubleNl) {
          // Place caret before the second newline and delete it
          const secondIdx = plainNow.indexOf('\n\n', windowStart);
          const deletePos = secondIdx >= 0 ? secondIdx + 1 : (plainNow.indexOf('\n', windowStart));
          const map = (pos: number): { node: Text | null; off: number } => {
            const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT, null);
            let acc = 0; let node = walker.nextNode();
            while (node) {
              const t = node as Text; const nextAcc = acc + t.data.length;
              if (pos <= nextAcc) { return { node: t, off: Math.max(0, t.data.length - (nextAcc - pos)) }; }
              acc = nextAcc; node = walker.nextNode();
            }
            return { node: null, off: 0 };
          };
          const { node: n2, off: o2 } = map(Math.max(0, deletePos - 1));
          const s2 = window.getSelection();
          if (n2) {
            const r2 = document.createRange(); r2.setStart(n2, o2); r2.collapse(true);
            if (s2) { s2.removeAllRanges(); s2.addRange(r2); }
            if (s2 && (s2 as any).modify) {
              (s2 as any).modify('extend','forward','character');
              const txt = s2.toString();
              if (txt === '\n') {
                document.execCommand('delete');
              } else {
                // Try to remove an explicit BR element
                const br = el.querySelector('br');
                if (br && br.parentNode) br.parentNode.removeChild(br);
              }
            }
          }
          // Refresh plain text
          plainNow = (el.innerText || '').replace(/\u00A0/g, ' ');
        }
        // Remove a single trailing space that can remain after newline deletion
        if (/\s/.test(plainNow.charAt(Math.max(0, (idx || 0) - 1)))) {
          const r3 = document.createRange(); r3.selectNodeContents(el); r3.collapse(false);
          const s3 = window.getSelection(); if (s3) { s3.removeAllRanges(); s3.addRange(r3); }
          if (s3 && (s3 as any).modify) { (s3 as any).modify('extend','backward','character'); }
          document.execCommand('delete');
        }
      } catch {}
      onBlockInput(b.id, { currentTarget: el } as any);
      commitEdits();
      setActionsVersion(v=>v+1);
      try { el.focus({ preventScroll: true } as any); } catch {}
      suppressActionsRef.current = false;
    };
    const undoFontSizeAction = (act: any, revIndex: number) => {
      const el = elRef.current; if (!el) return;
      let start: number = (act && typeof act.start === 'number') ? act.start : 0;
      let end: number = (act && typeof act.end === 'number') ? act.end : 0;
      const pxNow: number | null = (act && typeof act.px === 'number') ? act.px : null;
      // Find previous font-size for this range from earlier actions in this block
      const acts = actionsRef.current[b.id] || [];
      const actualIndex = acts.length - 1 - revIndex;
      let fallbackPx: number | null = null;
      for (let i = actualIndex - 1; i >= 0; i--) {
        const a: any = acts[i];
        if (a && a.type === 'fontSize') {
          const overlap = Math.max(start, a.start ?? -1) < Math.min(end, a.end ?? -1);
          if (overlap) { fallbackPx = typeof a.px === 'number' ? a.px : null; break; }
        }
      }
      // If no overlapping size found, try by matching the same text payload
      if (fallbackPx == null && act && typeof act.text === 'string' && act.text.length > 0) {
        for (let i = actualIndex - 1; i >= 0; i--) {
          const a: any = acts[i];
          if (a && a.type === 'fontSize' && a.text && a.text === act.text) {
            fallbackPx = typeof a.px === 'number' ? a.px : null; break;
          }
        }
      }
      // Remap start/end to the closest occurrence of the text in current content (guards against shifted offsets)
      try {
        const plainNow = (el.innerText || '');
        if (act && typeof act.text === 'string' && act.text.length > 0) {
          const candidates: number[] = [];
          let idx = plainNow.indexOf(act.text);
          while (idx >= 0) { candidates.push(idx); idx = plainNow.indexOf(act.text, idx + 1); }
          if (candidates.length > 0) {
            let best = candidates[0];
            let bestDist = Math.abs(best - start);
            for (let j = 1; j < candidates.length; j++) {
              const d = Math.abs(candidates[j] - start);
              if (d < bestDist) { best = candidates[j]; bestDist = d; }
            }
            start = best; end = best + act.text.length;
          }
        }
      } catch {}
      suppressActionsRef.current = true;
      // Build live range from plain-text offsets
      const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT, null);
      let acc = 0;
      let startNode: Text | null = null, endNode: Text | null = null;
      let startOffset = 0, endOffset = 0;
      let n = walker.nextNode();
      while (n) {
        const t = n as Text;
        const nextAcc = acc + t.data.length;
        if (!startNode && start <= nextAcc) {
          startNode = t; startOffset = Math.max(0, t.data.length - (nextAcc - start));
        }
        if (!endNode && end <= nextAcc) {
          endNode = t; endOffset = Math.max(0, t.data.length - (nextAcc - end));
          break;
        }
        acc = nextAcc; n = walker.nextNode();
      }
      if (!startNode || !endNode) { suppressActionsRef.current = false; return; }
      const range = document.createRange();
      range.setStart(startNode, startOffset);
      range.setEnd(endNode, endOffset);
      // Apply previous size or clear
      const sel = window.getSelection();
      if (sel) { sel.removeAllRanges(); sel.addRange(range.cloneRange()); }
      if (fallbackPx != null) {
        try {
          try { document.execCommand('fontSize', false, '7'); } catch {}
          // Normalize any <font size="7"> created by execCommand into <span style="font-size:px">
          const container = el;
          const pxStr = String(fallbackPx).replace(/[^0-9]/g, '');
          const fonts = container.querySelectorAll('font[size="7"]');
          fonts.forEach((f) => {
            const span = document.createElement('span');
            span.style.fontSize = `${pxStr}px`;
            span.style.lineHeight = '1.3';
            while (f.firstChild) span.appendChild(f.firstChild);
            f.replaceWith(span);
          });
        } catch {}
      } else {
        // No previous size -> clear inline font-size inside the range
        const visited = new Set<HTMLElement>();
        const all = document.createTreeWalker(el, NodeFilter.SHOW_ELEMENT, null);
        let en = all.nextNode();
        while (en) {
          const elem = en as HTMLElement;
          try {
            const intersects = (range as any).intersectsNode ? (range as any).intersectsNode(elem) : (()=>{ const r = document.createRange(); r.selectNodeContents(elem); return !(range.compareBoundaryPoints(Range.END_TO_START, r) <= 0 || range.compareBoundaryPoints(Range.START_TO_END, r) >= 0); })();
            if (intersects) {
              if (elem.style && elem.style.fontSize && !visited.has(elem)) { elem.style.fontSize = ''; visited.add(elem); }
            }
          } catch {}
          en = all.nextNode();
        }
      }
      // Update model and remove this action from stack
      onBlockInput(b.id, { currentTarget: el } as any);
      commitEdits();
      try { (el as any).focus({ preventScroll: true }); } catch {}
      const acts2 = actionsRef.current[b.id] || [];
      const idxToRemove = acts2.length - 1 - revIndex;
      if (idxToRemove >= 0 && idxToRemove < acts2.length && (acts2[idxToRemove] as any) === act) {
        acts2.splice(idxToRemove, 1);
      }
      setActionsVersion(v=>v+1);
      suppressActionsRef.current = false;
    };
    const undoWordAction = (start: number, end: number) => {
      const el = elRef.current; if (!el) return;
      suppressActionsRef.current = true;
      // Map block offsets to a live selection and delete the word (including one trailing space when present)
      const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT, null);
      let acc = 0;
      let sNode: Text | null = null, eNode: Text | null = null;
      let sOff = 0, eOff = 0;
      let n = walker.nextNode();
      while (n) {
        const t = n as Text;
        const nextAcc = acc + t.data.length;
        if (!sNode && start <= nextAcc) { sNode = t; sOff = Math.max(0, t.data.length - (nextAcc - start)); }
        if (!eNode && end <= nextAcc) { eNode = t; eOff = Math.max(0, t.data.length - (nextAcc - end)); break; }
        acc = nextAcc; n = walker.nextNode();
      }
      const plainNow = (el.innerText || '');
      const includeSpace = plainNow[end] === ' ' ? 1 : 0;
      if (!eNode && sNode) { eNode = sNode; eOff = sOff; }
      if (eNode) { eOff = Math.min((eNode as Text).data.length, eOff + includeSpace); }
      const sel = window.getSelection();
      if (sNode && eNode) {
        const r = document.createRange();
        r.setStart(sNode, sOff);
        r.setEnd(eNode, eOff);
        if (sel) { sel.removeAllRanges(); sel.addRange(r); }
        document.execCommand('delete');
      }
      onBlockInput(b.id, { currentTarget: el } as any);
      commitEdits();
      try { el.focus({ preventScroll: true } as any); } catch {}
      suppressActionsRef.current = false;
    };
    const undoDeleteAction = (text: string, start: number) => {
      const el = elRef.current; if (!el) return;
      suppressActionsRef.current = true;
      // Find insertion point by offsets across text nodes
      const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT, null);
      let acc = 0; let targetNode: Text | null = null; let targetOffset = 0;
      let node = walker.nextNode();
      while (node) {
        const t = node as Text; const nextAcc = acc + t.data.length;
        if (start <= nextAcc) { targetNode = t; targetOffset = Math.max(0, t.data.length - (nextAcc - start)); break; }
        acc = nextAcc; node = walker.nextNode();
      }
      if (!targetNode) {
        // fallback to end
        const r = document.createRange(); r.selectNodeContents(el); r.collapse(false);
        const s = window.getSelection(); if (s) { s.removeAllRanges(); s.addRange(r); }
      } else {
        const r = document.createRange(); r.setStart(targetNode, targetOffset); r.collapse(true);
        const s = window.getSelection(); if (s) { s.removeAllRanges(); s.addRange(r); }
      }
      document.execCommand('insertText', false, text);
      onBlockInput(b.id, { currentTarget: el } as any);
      commitEdits();
      try { el.focus({ preventScroll: true } as any); } catch {}
      suppressActionsRef.current = false;
    };
    const undoColorAction = (hex: string, start: number, end: number) => {
      const el = elRef.current; if (!el) return;
      suppressActionsRef.current = true;
      try {
        // Find the current color action index and the previous overlapping color
        const actions = actionsRef.current[b.id] || [];
        let currentIdx = -1;
        for (let i = actions.length - 1; i >= 0; i--) {
          const a = actions[i] as any;
          if (a && a.type === 'color' && a.hex === hex && a.start === start && a.end === end) { currentIdx = i; break; }
        }
        let prevHex: string | null = null;
        if (currentIdx !== -1) {
          for (let i = currentIdx - 1; i >= 0; i--) {
            const a = actions[i] as any;
            if (a && a.type === 'color') {
              const overlaps = a.start < end && a.end > start;
              if (overlaps) { prevHex = a.hex; break; }
            }
          }
        } else {
          // Fallback: choose the last overlapping color before end
          for (let i = actions.length - 1; i >= 0; i--) {
            const a = actions[i] as any;
            if (a && a.type === 'color') { const overlaps = a.start < end && a.end > start; if (overlaps) { prevHex = a.hex; break; } }
          }
        }
        // Remap offsets to nearest current occurrence to handle drift
        const plain = (el.innerText || '');
        const snippet = plain.slice(Math.max(0, start), Math.max(0, end));
        if (snippet) {
          let nearestStart = -1; let bestDist = Infinity; let from = 0; let idx = -1;
          while ((idx = plain.indexOf(snippet, from)) !== -1) {
            const dist = Math.abs(idx - start);
            if (dist < bestDist) { bestDist = dist; nearestStart = idx; }
            from = idx + 1;
          }
          if (nearestStart !== -1) { start = nearestStart; end = nearestStart + snippet.length; }
        }
        // Map to live DOM range
        const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT, null);
        let acc = 0;
        let startNode: Text | null = null, endNode: Text | null = null;
        let startOffset = 0, endOffset = 0;
        let n = walker.nextNode();
        while (n) {
          const t = n as Text; const nextAcc = acc + t.data.length;
          if (!startNode && start <= nextAcc) { startNode = t; startOffset = Math.max(0, t.data.length - (nextAcc - start)); }
          if (!endNode && end <= nextAcc) { endNode = t; endOffset = Math.max(0, t.data.length - (nextAcc - end)); break; }
          acc = nextAcc; n = walker.nextNode();
        }
        if (!startNode || !endNode) return;
        const range = document.createRange();
        range.setStart(startNode, startOffset);
        range.setEnd(endNode, endOffset);
        const sel = window.getSelection();
        if (sel) { sel.removeAllRanges(); sel.addRange(range); }
        // Apply previous color or default
        if (prevHex) {
          document.execCommand('foreColor', false, prevHex);
        } else {
          document.execCommand('foreColor', false, '#111827');
        }
        onBlockInput(b.id, { currentTarget: el } as any);
        commitEdits();
        setActionsVersion(v=>v+1);
        try { el.focus({ preventScroll: true } as any); } catch {}
      } finally {
        suppressActionsRef.current = false;
      }
    };

    const undoAlignAction = (act: any) => {
      const el = elRef.current; if (!el) return;
      suppressActionsRef.current = true;
      try {
        const actions = actionsRef.current[b.id] || [];
        const currentIdx = actions.lastIndexOf(act);
        let prevMode: 'left'|'center'|'right'|'justify' | null = null;
        if (currentIdx > 0) {
          for (let i = currentIdx - 1; i >= 0; i--) {
            const a: any = actions[i];
            if (a && a.type === 'align') {
              const overlaps = (a.start < (act.end ?? 0)) && (a.end > (act.start ?? 0));
              if (overlaps) { prevMode = a.mode; break; }
            }
          }
        }
        const mode = prevMode || 'left';
        const targetHost: HTMLElement = (el.matches('[contenteditable="true"]') ? el : (el.querySelector('[contenteditable="true"]') as HTMLElement)) || el;
        const sel = window.getSelection();
        if (act.block) {
          const r = document.createRange(); r.selectNodeContents(targetHost);
          if (sel) { sel.removeAllRanges(); sel.addRange(r); }
        } else {
          let start = Number(act.start) || 0; let end = Number(act.end) || 0;
          const walker = document.createTreeWalker(targetHost, NodeFilter.SHOW_TEXT, null);
          let acc = 0; let sNode: Text | null = null; let eNode: Text | null = null; let sOff = 0; let eOff = 0; let n = walker.nextNode();
          while (n) {
            const t = n as Text; const nextAcc = acc + t.data.length;
            if (!sNode && start <= nextAcc) { sNode = t; sOff = Math.max(0, t.data.length - (nextAcc - start)); }
            if (!eNode && end <= nextAcc) { eNode = t; eOff = Math.max(0, t.data.length - (nextAcc - end)); break; }
            acc = nextAcc; n = walker.nextNode();
          }
          if (sNode && eNode) {
            const r = document.createRange(); r.setStart(sNode, sOff); r.setEnd(eNode, eOff);
            if (sel) { sel.removeAllRanges(); sel.addRange(r); }
          } else {
            const r = document.createRange(); r.selectNodeContents(targetHost); if (sel) { sel.removeAllRanges(); sel.addRange(r); }
          }
        }
        if (mode === 'left') document.execCommand('justifyLeft');
        else if (mode === 'center') document.execCommand('justifyCenter');
        else if (mode === 'right') document.execCommand('justifyRight');
        else document.execCommand('justifyFull');
        onBlockInput(b.id, { currentTarget: el } as any);
        commitEdits();
        const acts = actionsRef.current[b.id] || [];
        const idx = acts.lastIndexOf(act);
        if (idx >= 0) acts.splice(idx, 1);
        setActionsVersion(v=>v+1);
        try { el.focus({ preventScroll: true } as any); } catch {}
      } finally {
        suppressActionsRef.current = false;
      }
    };
    const undoFontFamilyAction = (family: string, start: number, end: number) => {
      const el = elRef.current; if (!el) return;
      suppressActionsRef.current = true;
      try {
        // Determine previous font family from action history
        const actions = actionsRef.current[b.id] || [];
        let currentIdx = -1;
        for (let i = actions.length - 1; i >= 0; i--) {
          const a = actions[i] as any;
          if (a && a.type === 'fontFamily' && a.family === family && a.start === start && a.end === end) { currentIdx = i; break; }
        }
        let prevFamily: string | null = null;
        if (currentIdx !== -1) {
          for (let i = currentIdx - 1; i >= 0; i--) {
            const a = actions[i] as any;
            if (a && a.type === 'fontFamily') {
              const overlaps = a.start < end && a.end > start;
              if (overlaps) { prevFamily = a.family; break; }
            }
          }
        } else {
          for (let i = actions.length - 1; i >= 0; i--) {
            const a = actions[i] as any;
            if (a && a.type === 'fontFamily') { const overlaps = a.start < end && a.end > start; if (overlaps) { prevFamily = a.family; break; } }
          }
        }
        // Remap offsets using current plain text
        const plain = (el.innerText || '');
        const snippet = plain.slice(Math.max(0, start), Math.max(0, end));
        if (snippet) {
          let nearestStart = -1; let bestDist = Infinity; let from = 0; let idx = -1;
          while ((idx = plain.indexOf(snippet, from)) !== -1) {
            const dist = Math.abs(idx - start);
            if (dist < bestDist) { bestDist = dist; nearestStart = idx; }
            from = idx + 1;
          }
          if (nearestStart !== -1) { start = nearestStart; end = nearestStart + snippet.length; }
        }
        // Build live range
        const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT, null);
        let acc = 0; let sNode: Text | null = null; let eNode: Text | null = null; let sOff = 0; let eOff = 0;
        let n = walker.nextNode();
        while (n) {
          const t = n as Text; const nextAcc = acc + t.data.length;
          if (!sNode && start <= nextAcc) { sNode = t; sOff = Math.max(0, t.data.length - (nextAcc - start)); }
          if (!eNode && end <= nextAcc) { eNode = t; eOff = Math.max(0, t.data.length - (nextAcc - end)); break; }
          acc = nextAcc; n = walker.nextNode();
        }
        if (!sNode || !eNode) return;
        const r = document.createRange(); r.setStart(sNode, sOff); r.setEnd(eNode, eOff);
        const sel = window.getSelection(); if (sel) { sel.removeAllRanges(); sel.addRange(r); }
        if (prevFamily) {
          // Apply legacy fontName then normalize to span with fontFamily
          try { document.execCommand('fontName', false, prevFamily); } catch {}
          try {
            const fonts = (el as HTMLElement).querySelectorAll('font[face]');
            fonts.forEach((f) => {
              const fam = (f as any).getAttribute && (f as any).getAttribute('face');
              const span = document.createElement('span');
              if (fam) span.style.fontFamily = fam;
              while (f.firstChild) span.appendChild(f.firstChild);
              (f as any).replaceWith(span);
            });
          } catch {}
        } else {
          // Clear font-family inline styles inside the selection
          try {
            const nodeIter = document.createNodeIterator(el, NodeFilter.SHOW_ELEMENT);
            let node: Node | null;
            while ((node = nodeIter.nextNode())) {
              const elem = node as HTMLElement;
              if (elem && (elem as any).style && r.intersectsNode(elem)) {
                try { (elem as any).style.fontFamily = ''; } catch {}
              }
            }
          } catch {}
        }
        onBlockInput(b.id, { currentTarget: el } as any);
        commitEdits();
        try { el.focus({ preventScroll: true } as any); } catch {}
        setActionsVersion(v=>v+1);
      } finally {
        suppressActionsRef.current = false;
      }
    };

    const removeLastWords = (count: number) => {
      const el = elRef.current; if (!el) return;
      const stack = wordStacksRef.current[b.id] || [];
      if (stack.length === 0 || count <= 0) return;
      // Limit to available words in stack
      let remaining = Math.min(count, stack.length);
      // Place caret at the end of the block
      const range = document.createRange();
      range.selectNodeContents(el);
      range.collapse(false);
      const sel = window.getSelection();
      if (sel) { sel.removeAllRanges(); sel.addRange(range); }
      // Helpers to simulate backward delete reliably
      const ensureCaretAtEnd = () => {
        const r = document.createRange();
        r.selectNodeContents(el);
        r.collapse(false);
        const s = window.getSelection();
        if (s) { s.removeAllRanges(); s.addRange(r); }
      };
      const backspaceOnce = () => {
        const s = window.getSelection();
        if (s && (s as any).modify) {
          // Non-standard but works in modern browsers
          (s as any).modify('extend','backward','character');
          document.execCommand('delete');
        } else {
          // Fallback: select previous character in the last text node
          const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT, null);
          let lastText: Text | null = null; let n = walker.nextNode();
          while (n) { lastText = n as Text; n = walker.nextNode(); }
          if (lastText && lastText.data.length > 0) {
            const r = document.createRange();
            r.setStart(lastText, Math.max(0, lastText.data.length - 1));
            r.setEnd(lastText, lastText.data.length);
            const s2 = window.getSelection();
            if (s2) { s2.removeAllRanges(); s2.addRange(r); }
            document.execCommand('delete');
          }
        }
      };
      // Iteratively delete actions from the end
      const getPlain = () => (el.innerText || '').replace(/\u00A0/g, ' ');
      const getTokens = () => (getPlain().trim() || '').split(/\s+/).filter(Boolean);
      let tokens = getTokens();
      while (remaining > 0 && (tokens.length > 0 || (stack[stack.length - 1] === '__BR__'))) {
        const top = stack[stack.length - 1];
        if (top === '__BR__') {
          // Remove trailing line break(s)
          let plain = getPlain();
          ensureCaretAtEnd();
          suppressActionsRef.current = true;
          if (/[\n\r]$/.test(plain)) {
            backspaceOnce();
            plain = getPlain();
          }
          // Trim trailing single space left by browsers after newline
          if (/\s$/.test(plain)) {
            backspaceOnce();
            plain = getPlain();
          }
          stack.pop();
          // Also drop the last br action from actionsRef
          const acts = actionsRef.current[b.id] || [];
          for (let k = acts.length - 1; k >= 0; k--) { if ((acts[k] as any).type === 'br') { acts.splice(k, 1); break; } }
          setActionsVersion(v=>v+1);
          remaining -= 1;
          tokens = getTokens();
          suppressActionsRef.current = false;
        } else {
          const beforeLen = tokens.length;
          // Delete backwards until token count reduces by one
          let guard = 1000;
          ensureCaretAtEnd();
          suppressActionsRef.current = true;
          while (guard-- > 0) {
            backspaceOnce();
            const cur = getTokens();
            if (cur.length < beforeLen) { tokens = cur; break; }
          }
          if (tokens.length < beforeLen) {
            stack.pop();
            // Also drop the last word action from actionsRef
            const acts = actionsRef.current[b.id] || [];
            for (let k = acts.length - 1; k >= 0; k--) { if ((acts[k] as any).type === 'word') { acts.splice(k, 1); break; } }
            setActionsVersion(v=>v+1);
            remaining -= 1;
          } else {
            break;
          }
          suppressActionsRef.current = false;
        }
      }
      onBlockInput(b.id, { currentTarget: el } as any);
      commitEdits();
      try { el.focus({ preventScroll: true } as any); } catch {}
      setActionsVersion(v=>v+1);
    };
    const reapplyActions = (actions: string[]) => {
      const el = elRef.current; if (!el) return;
      const stack = wordStacksRef.current[b.id] || (wordStacksRef.current[b.id] = []);
      const ensureCaretAtEnd = () => {
        const r = document.createRange();
        r.selectNodeContents(el);
        r.collapse(false);
        const s = window.getSelection();
        if (s) { s.removeAllRanges(); s.addRange(r); }
      };
      suppressActionsRef.current = true;
      for (const a of actions) {
        ensureCaretAtEnd();
        if (a === '__BR__') {
          document.execCommand('insertLineBreak');
          stack.push('__BR__');
        } else {
          document.execCommand('insertText', false, a + ' ');
          stack.push(a);
        }
        onBlockInput(b.id, { currentTarget: el } as any);
      }
      commitEdits();
      try { el.focus({ preventScroll: true } as any); } catch {}
      suppressActionsRef.current = false;
    };
    const deleteActionAtReversedIndex = (revIndex: number) => {
      const stack = wordStacksRef.current[b.id] || [];
      const n = stack.length; if (n === 0) return;
      const targetIndex = n - 1 - revIndex;
      if (targetIndex < 0 || targetIndex >= n) return;
      const newer = stack.slice(targetIndex + 1);
      // Remove newer + target from the text and stack
      removeLastWords(newer.length + 1);
      // Reapply newer in their original order to preserve subsequent actions
      reapplyActions(newer);
      // Also remove the corresponding word action from actionsRef
      const acts = actionsRef.current[b.id] || [];
      const actsIdx = acts.length - 1 - revIndex;
      if (actsIdx >= 0 && actsIdx < acts.length && (acts[actsIdx] as any).type === 'word') {
        acts.splice(actsIdx, 1);
      }
      setActionsVersion(v=>v+1);
    };
    const undoLastAction = () => {
      const acts = actionsRef.current[b.id] || [];
      if (acts.length === 0) return;
      const last = acts[acts.length - 1] as any;
      // push to redo stack first
      (redoStacksRef.current[b.id] ||= []).push({ ...last });
      if (last.type === 'word') {
        deleteActionAtReversedIndex(0);
      } else if (last.type === 'br') {
        undoBrAction(last);
        acts.pop();
        setActionsVersion(v=>v+1);
      } else if (last.type === 'color') {
        undoColorAction(last.hex, last.start, last.end);
        acts.pop();
        setActionsVersion(v=>v+1);
      } else if (last.type === 'fontSize') {
        if (typeof undoFontSizeAction === 'function') {
          undoFontSizeAction(last, 0);
        }
        // undoFontSizeAction handles removing the corresponding action entry itself
        setActionsVersion(v=>v+1);
      } else if (last.type === 'fontFamily') {
        if (typeof undoFontFamilyAction === 'function') {
          undoFontFamilyAction(last.family, last.start, last.end);
        }
        acts.pop();
        setActionsVersion(v=>v+1);
      } else if (last.type === 'align') {
        undoAlignAction(last);
      } else if (last.type === 'delete') {
        undoDeleteAction(last.text, last.start);
        acts.pop();
        setActionsVersion(v=>v+1);
      }
      setStackOpen(true);
    };
    const redoActionAtReversedIndex = (revIndex: number) => {
      const redo = redoStacksRef.current[b.id] || [];
      if (redo.length === 0) return;
      const targetIndex = redo.length - 1 - revIndex;
      if (targetIndex < 0 || targetIndex >= redo.length) return;
      const act = redo.splice(targetIndex, 1)[0];
      const el = elRef.current; if (!el) return;
      const applyRange = (start: number, end: number, applier: (range: Range)=>void) => {
        let s = start, e = end;
        const plain = (el.innerText || '');
        const snippet = plain.slice(Math.max(0, s), Math.max(0, e));
        if (snippet) {
          let nearestStart = -1; let bestDist = Infinity; let from = 0; let idx = -1;
          while ((idx = plain.indexOf(snippet, from)) !== -1) {
            const dist = Math.abs(idx - s);
            if (dist < bestDist) { bestDist = dist; nearestStart = idx; }
            from = idx + 1;
          }
          if (nearestStart !== -1) { s = nearestStart; e = nearestStart + snippet.length; }
        }
        const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT, null);
        let acc = 0; let sNode: Text | null = null; let eNode: Text | null = null; let sOff = 0; let eOff = 0;
        let n = walker.nextNode();
        while (n) {
          const t = n as Text; const nextAcc = acc + t.data.length;
          if (!sNode && s <= nextAcc) { sNode = t; sOff = Math.max(0, t.data.length - (nextAcc - s)); }
          if (!eNode && e <= nextAcc) { eNode = t; eOff = Math.max(0, t.data.length - (nextAcc - e)); break; }
          acc = nextAcc; n = walker.nextNode();
        }
        if (!sNode || !eNode) return;
        const r = document.createRange(); r.setStart(sNode, sOff); r.setEnd(eNode, eOff);
        applier(r);
      };
      suppressActionsRef.current = true;
      try {
        if (act.type === 'word') {
          reapplyActions([act.text]);
          (actionsRef.current[b.id] ||= []).push(act);
        } else if (act.type === 'br') {
          reapplyActions(['__BR__']);
          (actionsRef.current[b.id] ||= []).push(act);
        } else if (act.type === 'color') {
          applyRange(act.start, act.end, (r) => {
            const sel = window.getSelection(); if (sel) { sel.removeAllRanges(); sel.addRange(r); }
            document.execCommand('foreColor', false, act.hex);
          });
          (actionsRef.current[b.id] ||= []).push(act);
        } else if (act.type === 'fontFamily') {
          applyRange(act.start, act.end, (r) => {
            const sel = window.getSelection(); if (sel) { sel.removeAllRanges(); sel.addRange(r); }
            try { document.execCommand('fontName', false, act.family); } catch {}
            try {
              const fonts = (el as HTMLElement).querySelectorAll('font[face]');
              fonts.forEach((f) => {
                const fam = (f as any).getAttribute && (f as any).getAttribute('face');
                const span = document.createElement('span');
                if (fam) span.style.fontFamily = fam;
                while (f.firstChild) span.appendChild(f.firstChild);
                (f as any).replaceWith(span);
              });
            } catch {}
          });
          (actionsRef.current[b.id] ||= []).push(act);
        } else if (act.type === 'align') {
          applyRange(act.start, act.end, (r) => {
            const sel = window.getSelection(); if (sel) { sel.removeAllRanges(); sel.addRange(r); }
            if (act.mode === 'left') document.execCommand('justifyLeft');
            else if (act.mode === 'center') document.execCommand('justifyCenter');
            else if (act.mode === 'right') document.execCommand('justifyRight');
            else document.execCommand('justifyFull');
          });
          (actionsRef.current[b.id] ||= []).push(act);
        } else if (act.type === 'fontSize') {
          applyRange(act.start, act.end, (r) => {
            const walker = document.createNodeIterator(el, NodeFilter.SHOW_ELEMENT);
            let node: Node | null;
            while ((node = walker.nextNode())) {
              const elem = node as HTMLElement;
              if (elem && (elem as any).style && r.intersectsNode(elem)) {
                try { (elem as any).style.fontSize = `${act.px}px`; (elem as any).style.lineHeight = '1.4'; } catch {}
              }
            }
          });
          (actionsRef.current[b.id] ||= []).push(act);
        } else if (act.type === 'delete') {
          applyRange(act.start, act.end, (r) => {
            r.deleteContents();
          });
          (actionsRef.current[b.id] ||= []).push(act);
        }
        const el2 = elRef.current; if (el2) { onBlockInput(b.id, { currentTarget: el2 } as any); commitEdits(); try { el2.focus({ preventScroll: true } as any); } catch {} }
        setActionsVersion(v=>v+1);
      } finally {
        suppressActionsRef.current = false;
      }
      setStackOpen(true);
    };
    const redoLastAction = () => {
      const redo = redoStacksRef.current[b.id] || [];
      if (redo.length === 0) return;
      const act = redo.pop();
      const el = elRef.current; if (!el) return;
      const applyRange = (start: number, end: number, applier: (range: Range)=>void) => {
        // Remap offsets similar to undo functions
        let s = start, e = end;
        const plain = (el.innerText || '');
        const snippet = plain.slice(Math.max(0, s), Math.max(0, e));
        if (snippet) {
          let nearestStart = -1; let bestDist = Infinity; let from = 0; let idx = -1;
          while ((idx = plain.indexOf(snippet, from)) !== -1) {
            const dist = Math.abs(idx - s);
            if (dist < bestDist) { bestDist = dist; nearestStart = idx; }
            from = idx + 1;
          }
          if (nearestStart !== -1) { s = nearestStart; e = nearestStart + snippet.length; }
        }
        const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT, null);
        let acc = 0; let sNode: Text | null = null; let eNode: Text | null = null; let sOff = 0; let eOff = 0;
        let n = walker.nextNode();
        while (n) {
          const t = n as Text; const nextAcc = acc + t.data.length;
          if (!sNode && s <= nextAcc) { sNode = t; sOff = Math.max(0, t.data.length - (nextAcc - s)); }
          if (!eNode && e <= nextAcc) { eNode = t; eOff = Math.max(0, t.data.length - (nextAcc - e)); break; }
          acc = nextAcc; n = walker.nextNode();
        }
        if (!sNode || !eNode) return;
        const r = document.createRange(); r.setStart(sNode, sOff); r.setEnd(eNode, eOff);
        applier(r);
      };
      suppressActionsRef.current = true;
      try {
        if (act.type === 'word') {
          reapplyActions([act.text]);
          (actionsRef.current[b.id] ||= []).push(act);
        } else if (act.type === 'br') {
          reapplyActions(['__BR__']);
          (actionsRef.current[b.id] ||= []).push(act);
        } else if (act.type === 'color') {
          applyRange(act.start, act.end, (r) => {
            const sel = window.getSelection(); if (sel) { sel.removeAllRanges(); sel.addRange(r); }
            document.execCommand('foreColor', false, act.hex);
          });
          (actionsRef.current[b.id] ||= []).push(act);
        } else if (act.type === 'fontFamily') {
          applyRange(act.start, act.end, (r) => {
            const sel = window.getSelection(); if (sel) { sel.removeAllRanges(); sel.addRange(r); }
            try { document.execCommand('fontName', false, act.family); } catch {}
            try {
              const fonts = (el as HTMLElement).querySelectorAll('font[face]');
              fonts.forEach((f) => {
                const fam = (f as any).getAttribute && (f as any).getAttribute('face');
                const span = document.createElement('span');
                if (fam) span.style.fontFamily = fam;
                while (f.firstChild) span.appendChild(f.firstChild);
                (f as any).replaceWith(span);
              });
            } catch {}
          });
          (actionsRef.current[b.id] ||= []).push(act);
        } else if (act.type === 'align') {
          applyRange(act.start, act.end, (r) => {
            const sel = window.getSelection(); if (sel) { sel.removeAllRanges(); sel.addRange(r); }
            if (act.mode === 'left') document.execCommand('justifyLeft');
            else if (act.mode === 'center') document.execCommand('justifyCenter');
            else if (act.mode === 'right') document.execCommand('justifyRight');
            else document.execCommand('justifyFull');
          });
          (actionsRef.current[b.id] ||= []).push(act);
        } else if (act.type === 'fontSize') {
          applyRange(act.start, act.end, (r) => {
            // apply px font-size on elements intersecting range
            const walker = document.createNodeIterator(el, NodeFilter.SHOW_ELEMENT);
            let node: Node | null;
            while ((node = walker.nextNode())) {
              const elem = node as HTMLElement;
              if (elem && (elem as any).style && r.intersectsNode(elem)) {
                try { (elem as any).style.fontSize = `${act.px}px`; (elem as any).style.lineHeight = '1.4'; } catch {}
              }
            }
          });
          (actionsRef.current[b.id] ||= []).push(act);
        } else if (act.type === 'delete') {
          applyRange(act.start, act.end, (r) => {
            r.deleteContents();
          });
          (actionsRef.current[b.id] ||= []).push(act);
        }
        const el2 = elRef.current; if (el2) { onBlockInput(b.id, { currentTarget: el2 } as any); commitEdits(); try { el2.focus({ preventScroll: true } as any); } catch {} }
        setActionsVersion(v=>v+1);
      } finally {
        suppressActionsRef.current = false;
      }
      setStackOpen(true);
    };
    const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
      // ESC exits typing mode (keep block selected)
      if (e.key === 'Escape') {
        e.preventDefault(); e.stopPropagation();
        isEditingRef.current = false;
        // keep this block selected while exiting typing mode
        setSelectedId(b.id);
        try { (e.currentTarget as HTMLElement).blur(); } catch {}
        // reassert selection after blur
        requestAnimationFrame(() => setSelectedId(b.id));
        return;
      }
      // Movement shortcuts: plain Arrow keys when this block is active and NOT typing (only in 'move' mode)
      if (selectionArrowMode === 'move' && selectedId === b.id && !isEditingRef.current) {
        if (e.key === 'ArrowUp') { e.preventDefault(); e.stopPropagation(); moveBlockUp(b.id); return; }
        if (e.key === 'ArrowDown') { e.preventDefault(); e.stopPropagation(); moveBlockDown(b.id); return; }
        if (e.key === 'ArrowLeft') { e.preventDefault(); e.stopPropagation(); moveBlockLeft(b.id); return; }
        if (e.key === 'ArrowRight') { e.preventDefault(); e.stopPropagation(); moveBlockRight(b.id); return; }
      }
      if (e.key === 'Enter') {
        // If auto list mode is ON, prefer native list behavior
        try {
          const sel = window.getSelection();
          const inAutoMode = autoBulletRef.current || autoNumberRef.current;
          if (sel && sel.rangeCount > 0 && inAutoMode) {
            let node: Node | null = sel.getRangeAt(0).startContainer as Node;
            if (node.nodeType === 3) node = (node as Text).parentElement as any;
            const li = (node as Element)?.closest?.('li') as HTMLElement | null;
            if (li) {
              // Inside a list: if current li is empty, insert a ZWSP so Enter doesn't exit the list
              try {
                const isEmpty = (li.innerText || li.textContent || '').trim().length === 0;
                if (isEmpty) {
                  try { document.execCommand('insertText', false, '\u200B'); } catch {}
                }
              } catch {}
              // Allow default so browser adds the next li
              return;
            } else {
              // Not in list yet -> create UL/OL first, then allow default to add the new item
              const host = e.currentTarget as HTMLElement;
              if (autoBulletRef.current) {
                try { document.execCommand('insertUnorderedList'); } catch {}
              } else if (autoNumberRef.current) {
                try { document.execCommand('insertOrderedList'); } catch {}
              }
              // Normalize list appearance
              try {
                const selAfter = window.getSelection();
                const anchorEl = selAfter && selAfter.anchorNode ? (selAfter.anchorNode.nodeType===1 ? selAfter.anchorNode as Element : (selAfter.anchorNode.parentElement as Element)) : null;
                const listEl = anchorEl ? (anchorEl.closest('ul,ol') as HTMLElement | null) : null;
                if (listEl) {
                  listEl.style.listStylePosition = 'outside';
                  if (listEl.tagName.toLowerCase() === 'ul') listEl.style.listStyleType = 'disc';
                  if (listEl.tagName.toLowerCase() === 'ol') listEl.style.listStyleType = 'decimal';
                  listEl.style.paddingLeft = '1.25rem';
                  listEl.style.marginTop = '0.25rem';
                  listEl.style.marginBottom = '0.25rem';
                }
                try { host.dispatchEvent(new Event('input', { bubbles: true })); } catch {}
              } catch {}
              // Ensure current li is not considered empty so the next Enter doesn't exit the list
              try { document.execCommand('insertText', false, '\u200B'); } catch {}
              return; // let default Enter run now that we're inside a list
            }
          }
        } catch {}

        // Fallback for non-auto mode: manual line break + optional prefix logic (number/bullet disabled)
        e.preventDefault();
        let broke = false;
        try { broke = document.execCommand('insertLineBreak'); } catch {}
        if (!broke) {
          try { document.execCommand('insertHTML', false, '<br>'); broke = true; } catch {}
        }
        // Enter typically ends a word as well; word-count logic in onInput will handle the indicator next tick
      }
    };
    const isDeletableText = b.type === 'paragraph' || b.type === 'heading1' || b.type === 'heading2' || b.type === 'quote';
    const stackLen = (wordStacksRef.current[b.id] || []).length;
    return (
      <div className={`relative group ${selectedId===b.id ? 'z-50' : 'z-0'}`} data-block-wrapper={b.id}>
        <div
          ref={elRef}
          data-block-id={b.id}
          className={
            (b.type === 'heading1' ? 'text-2xl md:text-3xl font-bold text-gray-900' :
            b.type === 'heading2' ? 'text-xl md:text-2xl font-semibold text-gray-900' :
            b.type === 'quote' ? 'text-lg md:text-xl text-gray-700 pl-4 italic' :
            'text-base md:text-lg text-gray-800') +
            ' border border-transparent rounded-lg transition-colors ' + (editable ? 'group-hover:border-gray-300 group-hover:border-dashed' : '') +
            (b.type === 'quote' ? ' border-l-4 border-l-gray-300' : '') +
            (selectedId===b.id ? ' ring-2 ring-purple-500 ring-offset-2 ring-offset-white' : '')
          }
          role="textbox"
          aria-multiline="true"
          spellCheck={true}
          style={{ whiteSpace: 'pre-wrap', outline: 'none', overflowWrap: 'anywhere', wordBreak: 'break-word', paddingBottom: Math.max(0, dragExtraById[b.id] || 0) }}
          contentEditable={editable}
          suppressContentEditableWarning
          onInput={handleInput}
          onPaste={onPasteAsPlain as any}
          onKeyDown={handleKeyDown}
          onKeyUp={(e) => e.stopPropagation()}
          onKeyPress={(e) => e.stopPropagation()}
          onFocus={() => { if (!editable) return; isEditingRef.current = true; setSelectedId(b.id); }}
          onMouseDown={(e) => { if (!editable) { e.stopPropagation(); return; } setSelectedId(b.id); }}
          onBlur={(e) => { isEditingRef.current = false; onBlockInput(b.id, e as any); finalizePendingWord(); commitEdits(); }}
        />

        {editable && saveHint.visible && (
          <span
            className="pointer-events-none absolute z-10"
            style={{ left: saveHint.x, top: saveHint.y }}
          >
            <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-green-600 text-white shadow ring-2 ring-white/60">
              <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="20 6 9 17 4 12" />
              </svg>
            </span>
          </span>
        )}

        {editable && (selectedId===b.id || stackOpen) && (
          <>
            <button
              title="View action stack"
              className="absolute -top-3 -left-3 h-7 w-7 rounded-full bg-white border shadow-sm text-gray-700 inline-flex items-center justify-center hover:scale-105 z-50"
              onMouseDown={(e)=> { e.preventDefault(); e.stopPropagation(); }}
              onClick={()=> setStackOpen(s => !s)}
            >
              <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7-11-7-11-7Z"/><circle cx="12" cy="12" r="3"/></svg>
            </button>
            <div className="absolute -top-3 left-6 z-50" data-editor-action>
              <button
                title="Undo last action"
                className="h-7 w-7 rounded-full bg-white border shadow-sm text-purple-600 inline-flex items-center justify-center hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
                onMouseDown={(e)=> { e.preventDefault(); e.stopPropagation(); }}
                onClick={() => { if ((actionsRef.current[b.id]||[]).length > 0) { undoLastAction(); } }}
                disabled={(actionsRef.current[b.id]||[]).length === 0}
              >
                <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="1 4 1 10 7 10" />
                  <path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10" />
                </svg>
              </button>
              <button
                title="Redo last action"
                className="ml-2 h-7 w-7 rounded-full bg-white border shadow-sm text-purple-600 inline-flex items-center justify-center hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
                onMouseDown={(e)=> { e.preventDefault(); e.stopPropagation(); }}
                onClick={() => { if ((redoStacksRef.current[b.id]||[]).length > 0) { redoLastAction(); } }}
                disabled={(redoStacksRef.current[b.id]||[]).length === 0}
              >
                <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="23 4 23 10 17 10" />
                  <path d="M20.49 15a9 9 0 1 1-2.13-9.36L23 10" />
                </svg>
              </button>
            </div>
            {/* Directional move arrows for text/heading/quote blocks */}
            {selectedId===b.id && (
              <>
                <button title="Move up" className="absolute left-1/2 -translate-x-1/2 -translate-y-1/2 top-0 ml-8 z-50 inline-flex items-center justify-center h-7 w-7 rounded-full bg-white border shadow-sm text-gray-700 transform scale-50 hover:scale-100 opacity-70 hover:opacity-100" onMouseDown={(e)=>{e.preventDefault(); e.stopPropagation();}} onClick={()=> moveBlockUp(b.id)}>
                  <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 19V5"/><path d="M5 12l7-7 7 7"/></svg>
                </button>
                <button title="Move down" className="absolute left-1/2 -translate-x-1/2 translate-y-1/2 bottom-0 ml-8 z-50 inline-flex items-center justify-center h-7 w-7 rounded-full bg-white border shadow-sm text-gray-700 transform scale-50 hover:scale-100 opacity-70 hover:opacity-100" onMouseDown={(e)=>{e.preventDefault(); e.stopPropagation();}} onClick={()=> moveBlockDown(b.id)}>
                  <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 5v14"/><path d="M19 12l-7 7-7-7"/></svg>
                </button>
              </>
            )}
            {stackOpen && (
              <div
                className="absolute z-20 rounded-lg border border-gray-200 bg-white shadow-sm text-xs text-gray-700 w-[360px] max-w-[420px]"
                style={{ left: stackPos.x, top: stackPos.y }}
                data-editor-action
              >
                <button
                  className="absolute top-1 right-1 inline-flex h-5 w-5 items-center justify-center rounded hover:bg-gray-100 text-gray-500"
                  title="Close"
                  onMouseDown={(e)=>{ e.preventDefault(); e.stopPropagation(); }}
                  onClick={()=> setStackOpen(false)}
                >
                  <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                </button>
                <div className="font-medium text-gray-800 px-2 pr-8 py-1 border-b border-gray-100 flex items-center gap-2 cursor-grab select-none rounded-t-lg" onMouseDown={onStartDrag}>
                  <svg viewBox="0 0 24 24" className="h-4 w-4 text-gray-600" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 11V8a3 3 0 0 1 6 0v3"/><path d="M7 11h10l-1 8H8l-1-8Z"/></svg>
                  <span>
                    Action stack — {(
                      b.type === 'heading1' ? 'Heading 1' :
                      b.type === 'heading2' ? 'Heading 2' :
                      b.type === 'quote' ? 'Quote' :
                      b.type === 'image' ? 'Image' :
                      b.type === 'code' ? 'Code' : 'Paragraph'
                    )}
                  </span>
                  <button
                    className="ml-auto mr-5 inline-flex items-center gap-1 px-2 py-0.5 rounded border border-gray-200 hover:bg-gray-50 text-purple-600 disabled:opacity-50 disabled:cursor-not-allowed"
                    title="Undo last action"
                    onMouseDown={(e)=>{ e.preventDefault(); e.stopPropagation(); }}
                    onClick={()=>{ if ((actionsRef.current[b.id]||[]).length > 0) { undoLastAction(); } }}
                    disabled={(actionsRef.current[b.id]||[]).length === 0}
                  >
                    <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="9 14 4 9 9 4"/><path d="M20 20a8 8 0 0 0-8-8H4"/></svg>
                    <span>Undo</span>
                  </button>
                  <button
                    className="ml-2 mr-5 inline-flex items-center gap-1 px-2 py-0.5 rounded border border-gray-200 hover:bg-gray-50 text-purple-600 disabled:opacity-50 disabled:cursor-not-allowed"
                    title="Redo last action"
                    onMouseDown={(e)=>{ e.preventDefault(); e.stopPropagation(); }}
                    onClick={()=>{ if ((redoStacksRef.current[b.id]||[]).length > 0) { redoLastAction(); } }}
                    disabled={(redoStacksRef.current[b.id]||[]).length === 0}
                  >
                    <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="15 10 20 15 15 20"/><path d="M4 4v7a8 8 0 0 0 8 8h8"/></svg>
                    <span>Redo</span>
                  </button>
                </div>
                <div className="border-t border-gray-100 mt-1 pt-1">
                  <div className="text-[11px] text-gray-500 px-2 pb-1">Ghost realm — redo</div>
                  <div className="flex flex-wrap gap-1 max-h-24 overflow-auto px-2 pb-1">
                    {(redoStacksRef.current[b.id] || []).slice().reverse().map((act: any, i: number) => (
                      act.type === 'br' ? (
                        <span key={i} className="relative px-1.5 py-0.5 rounded bg-gray-100 text-gray-500 border border-gray-200 inline-flex items-center gap-1" title="Line break (redo)">
                          ↵
                          <button
                            className="absolute -top-1 -right-1 h-4 w-4 rounded-full bg-white border border-gray-300 text-gray-600 inline-flex items-center justify-center hover:bg-gray-100"
                            title="Redo this action"
                            onMouseDown={(e)=>{ e.preventDefault(); e.stopPropagation(); }}
                            onClick={()=>{ redoActionAtReversedIndex(i); setStackOpen(true); }}
                          >
                            <svg viewBox="0 0 24 24" className="h-3 w-3" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="23 4 23 10 17 10" /><path d="M20.49 15a9 9 0 1 1-2.13-9.36L23 10" /></svg>
                          </button>
                        </span>
                      ) : act.type === 'word' ? (
                        <span key={i} className="relative px-1.5 py-0.5 rounded bg-gray-100 text-gray-500 border border-gray-200 inline-flex items-center gap-1">
                          {act.text}
                          <button className="absolute -top-1 -right-1 h-4 w-4 rounded-full bg-white border border-gray-300 text-gray-600 inline-flex items-center justify-center hover:bg-gray-100" title="Redo this action" onMouseDown={(e)=>{ e.preventDefault(); e.stopPropagation(); }} onClick={()=>{ redoActionAtReversedIndex(i); setStackOpen(true); }}>
                            <svg viewBox="0 0 24 24" className="h-3 w-3" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="23 4 23 10 17 10" /><path d="M20.49 15a9 9 0 1 1-2.13-9.36L23 10" /></svg>
                          </button>
                        </span>
                      ) : act.type === 'align' ? (
                        <span key={i} className="relative px-1.5 py-0.5 rounded bg-gray-100 text-gray-600 border border-gray-200 inline-flex items-center gap-1" title={`Align ${act.mode} (redo)`}>
                          <span className="inline-flex items-center gap-1"><span className="text-[11px]">≡</span> {act.mode}</span>
                          <span className="max-w-[120px] truncate">{act.text || ''}</span>
                          <button className="absolute -top-1 -right-1 h-4 w-4 rounded-full bg-white border border-gray-300 text-gray-600 inline-flex items-center justify-center hover:bg-gray-100" title="Redo this action" onMouseDown={(e)=>{ e.preventDefault(); e.stopPropagation(); }} onClick={()=>{ redoActionAtReversedIndex(i); setStackOpen(true); }}>
                            <svg viewBox="0 0 24 24" className="h-3 w-3" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="23 4 23 10 17 10" /><path d="M20.49 15a9 9 0 1 1-2.13-9.36L23 10" /></svg>
                          </button>
                        </span>
                      ) : act.type === 'delete' ? (
                        <span key={i} className="relative px-1.5 py-0.5 rounded bg-gray-100 text-gray-500 border border-gray-200 inline-flex items-center gap-1" title="Deletion (redo)">
                          <span className="max-w-[140px] truncate" title={String((act as any).text)}>{String((act as any).text)}</span>
                          <button className="absolute -top-1 -right-1 h-4 w-4 rounded-full bg-white border border-gray-300 text-gray-600 inline-flex items-center justify-center hover:bg-gray-100" title="Redo this action" onMouseDown={(e)=>{ e.preventDefault(); e.stopPropagation(); }} onClick={()=>{ redoActionAtReversedIndex(i); setStackOpen(true); }}>
                            <svg viewBox="0 0 24 24" className="h-3 w-3" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="23 4 23 10 17 10" /><path d="M20.49 15a9 9 0 1 1-2.13-9.36L23 10" /></svg>
                          </button>
                        </span>
                      ) : act.type === 'color' ? (
                        <span key={i} className="relative px-1.5 py-0.5 rounded bg-gray-100 text-gray-500 border border-gray-200 inline-flex items-center gap-1" title={`Color ${act.hex}`}>
                          <span className="inline-block h-3 w-3 rounded" style={{ backgroundColor: act.hex }} />
                          <span className="max-w-[120px] truncate">{act.text || ''}</span>
                          <button className="absolute -top-1 -right-1 h-4 w-4 rounded-full bg-white border border-gray-300 text-gray-600 inline-flex items-center justify-center hover:bg-gray-100" title="Redo this action" onMouseDown={(e)=>{ e.preventDefault(); e.stopPropagation(); }} onClick={()=>{ redoActionAtReversedIndex(i); setStackOpen(true); }}>
                            <svg viewBox="0 0 24 24" className="h-3 w-3" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="23 4 23 10 17 10" /><path d="M20.49 15a9 9 0 1 1-2.13-9.36L23 10" /></svg>
                          </button>
                        </span>
                      ) : act.type === 'fontSize' ? (
                        <span key={i} className="relative px-1.5 py-0.5 rounded bg-gray-100 text-gray-500 border border-gray-200 inline-flex items-center gap-1" title={`Font size ${act.px}px`}>
                          <span className="inline-flex items-center gap-1"><span className="text-[11px]">λ</span> {act.px} px</span>
                          <span className="max-w-[120px] truncate">{act.text || ''}</span>
                          <button className="absolute -top-1 -right-1 h-4 w-4 rounded-full bg-white border border-gray-300 text-gray-600 inline-flex items-center justify-center hover:bg-gray-100" title="Redo this action" onMouseDown={(e)=>{ e.preventDefault(); e.stopPropagation(); }} onClick={()=>{ redoActionAtReversedIndex(i); setStackOpen(true); }}>
                            <svg viewBox="0 0 24 24" className="h-3 w-3" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="23 4 23 10 17 10" /><path d="M20.49 15a9 9 0 1 1-2.13-9.36L23 10" /></svg>
                          </button>
                        </span>
                      ) : act.type === 'fontFamily' ? (
                        <span key={i} className="relative px-1.5 py-0.5 rounded bg-gray-100 text-gray-500 border border-gray-200 inline-flex items-center gap-1" title={`Font ${act.family}`}>
                          <span className="inline-flex items-center gap-1"><span className="text-[11px]">A</span> {act.family}</span>
                          <span className="max-w-[120px] truncate">{act.text || ''}</span>
                          <button className="absolute -top-1 -right-1 h-4 w-4 rounded-full bg-white border border-gray-300 text-gray-600 inline-flex items-center justify-center hover:bg-gray-100" title="Redo this action" onMouseDown={(e)=>{ e.preventDefault(); e.stopPropagation(); }} onClick={()=>{ redoActionAtReversedIndex(i); setStackOpen(true); }}>
                            <svg viewBox="0 0 24 24" className="h-3 w-3" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="23 4 23 10 17 10" /><path d="M20.49 15a9 9 0 1 1-2.13-9.36L23 10" /></svg>
                          </button>
                        </span>
                      ) : null
                    ))}
                    {((redoStacksRef.current[b.id] || []).length === 0) && (
                      <div className="text-[11px] text-gray-400">Empty</div>
                    )}
                  </div>
                </div>
                <div className="flex flex-wrap gap-1 max-h-32 overflow-auto px-2 py-1">
                  {(actionsRef.current[b.id] || []).slice().reverse().map((act: any, i: number) => (
                    act.type === 'br' ? (
                      <span key={i} className="relative px-1.5 py-0.5 rounded bg-blue-100 text-blue-800 border border-blue-200 inline-flex items-center gap-1" title={act.newLine ? 'Line break (new line)' : 'Line break'}>
                        <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="2">
                          <polyline points="9 10 4 15 9 20" />
                          <path d="M20 4v7a4 4 0 0 1-4 4H4" />
                        </svg>
                        ↵
                        {act.newLine && (
                          <span className="text-[10px] px-1 py-0.5 rounded bg-white/60 text-blue-900">(new line)</span>
                        )}
                        <button
                          className="absolute -top-1 -right-1 h-4 w-4 rounded-full bg-white border border-gray-300 text-gray-600 inline-flex items-center justify-center hover:bg-gray-100"
                          title="Delete this action"
                          onMouseDown={(e)=>{ e.preventDefault(); e.stopPropagation(); }}
                          onClick={()=>{ (redoStacksRef.current[b.id] ||= []).push({ ...act }); undoBrAction(act); (actionsRef.current[b.id] ||= []).splice(((actionsRef.current[b.id]||[]).length-1)-i,1); setActionsVersion(v=>v+1); setStackOpen(true); }}
                        >
                          <svg viewBox="0 0 24 24" className="h-3 w-3" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                        </button>
                      </span>
                    ) : act.type === 'word' ? (
                      <span key={i} className="relative px-1.5 py-0.5 rounded bg-gray-100 border border-gray-200 inline-flex items-center gap-1">
                        {act.text}
                        <button
                          className="absolute -top-1 -right-1 h-4 w-4 rounded-full bg-white border border-gray-300 text-gray-600 inline-flex items-center justify-center hover:bg-gray-100"
                          title="Delete this action"
                          onMouseDown={(e)=>{ e.preventDefault(); e.stopPropagation(); }}
                          onClick={()=>{ (redoStacksRef.current[b.id] ||= []).push({ ...act }); undoWordAction((act as any).start ?? 0, (act as any).end ?? 0); (actionsRef.current[b.id] ||= []).splice(((actionsRef.current[b.id]||[]).length-1)-i,1); setActionsVersion(v=>v+1); setStackOpen(true); }}
                        >
                          <svg viewBox="0 0 24 24" className="h-3 w-3" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                        </button>
                      </span>
                    ) : act.type === 'align' ? (
                      <span key={i} className="relative px-1.5 py-0.5 rounded bg-indigo-100 text-indigo-900 border border-indigo-200 inline-flex items-center gap-1" title={`Align ${act.mode}`}>
                        <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 6h18"/><path d="M6 12h12"/><path d="M3 18h18"/></svg>
                        <span className="capitalize font-medium">{act.mode}</span>
                        <span className="ml-1 max-w-[140px] truncate px-1 rounded bg-white/60 text-indigo-900" title={(() => { const t = (act as any).text; if (t) return t; const el = elRef.current; const plain = el ? (el.innerText || '') : ''; return plain.slice((act as any).start ?? 0, (act as any).end ?? 0); })()}>
                          {(() => { const t = (act as any).text; if (t && t.length) return t; const el = elRef.current; const plain = el ? (el.innerText || '') : ''; const slice = plain.slice((act as any).start ?? 0, (act as any).end ?? 0); return slice || '(selection)'; })()}
                        </span>
                        <button
                          className="absolute -top-1 -right-1 h-4 w-4 rounded-full bg-white border border-gray-300 text-gray-600 inline-flex items-center justify-center hover:bg-gray-100"
                          title="Undo alignment"
                          onMouseDown={(e)=>{ e.preventDefault(); e.stopPropagation(); }}
                          onClick={()=>{ (redoStacksRef.current[b.id] ||= []).push({ ...act }); undoAlignAction(act); setStackOpen(true); }}
                        >
                          <svg viewBox="0 0 24 24" className="h-3 w-3" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                        </button>
                      </span>
                    ) : act.type === 'delete' ? (
                      <span key={i} className="relative px-1.5 py-0.5 rounded bg-red-100 text-red-800 border border-red-200 inline-flex items-center gap-1" title="Deletion">
                        <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 6h18M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2m-1 0l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"/></svg>
                        <span className="max-w-[140px] truncate" title={String((act as any).text)}>{String((act as any).text)}</span>
                        <button
                          className="absolute -top-1 -right-1 h-4 w-4 rounded-full bg-white border border-gray-300 text-gray-600 inline-flex items-center justify-center hover:bg-gray-100"
                          title="Undo deletion"
                          onMouseDown={(e)=>{ e.preventDefault(); e.stopPropagation(); }}
                          onClick={()=>{ (redoStacksRef.current[b.id] ||= []).push({ ...act }); undoDeleteAction((act as any).text, (act as any).start); (actionsRef.current[b.id] ||= []).splice(((actionsRef.current[b.id]||[]).length-1)-i,1); setActionsVersion(v=>v+1); setStackOpen(true); }}
                        >
                          <svg viewBox="0 0 24 24" className="h-3 w-3" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                        </button>
                      </span>
                    ) : act.type === 'fontSize' ? (
                      <span key={i} className="relative px-1.5 py-0.5 rounded bg-amber-100 text-amber-900 border border-amber-200 inline-flex items-center gap-1" title={`Text size ${act.px}px`}>
                        <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 20h4l3-16h2l3 16h4"/></svg>
                        <span className="font-medium">{act.px} px</span>
                        <span className="ml-1 max-w-[140px] truncate px-1 rounded bg-white/60 text-amber-900" title={(() => { const t = (act as any).text; if (t) return t; const el = elRef.current; const plain = el ? (el.innerText || '') : ''; return plain.slice((act as any).start ?? 0, (act as any).end ?? 0); })()}>
                          {(() => { const t = (act as any).text; if (t && t.length) return t; const el = elRef.current; const plain = el ? (el.innerText || '') : ''; const slice = plain.slice((act as any).start ?? 0, (act as any).end ?? 0); return slice || '(selection)'; })()}
                        </span>
                        <button
                          className="absolute -top-1 -right-1 h-4 w-4 rounded-full bg-white border border-gray-300 text-gray-600 inline-flex items-center justify-center hover:bg-gray-100"
                          title="Undo text size"
                          onMouseDown={(e)=>{ e.preventDefault(); e.stopPropagation(); }}
                          onClick={()=>{ (redoStacksRef.current[b.id] ||= []).push({ ...act }); undoFontSizeAction(act, i); setStackOpen(true); }}
                        >
                          <svg viewBox="0 0 24 24" className="h-3 w-3" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                        </button>
                      </span>
                    ) : act.type === 'fontFamily' ? (
                      <span key={i} className="relative px-1.5 py-0.5 rounded bg-teal-100 text-teal-900 border border-teal-200 inline-flex items-center gap-1" title={`Font ${act.family}`}>
                        <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 20h16M6 20l6-16 6 16M8 14h8"/></svg>
                        <span className="font-medium" style={{ fontFamily: act.family }}>{act.family}</span>
                        <span className="ml-1 max-w-[140px] truncate px-1 rounded bg-white/60 text-teal-900" title={(() => { const t = (act as any).text; if (t) return t; const el = elRef.current; const plain = el ? (el.innerText || '') : ''; return plain.slice((act as any).start ?? 0, (act as any).end ?? 0); })()}>
                          {(() => { const t = (act as any).text; if (t && t.length) return t; const el = elRef.current; const plain = el ? (el.innerText || '') : ''; const slice = plain.slice((act as any).start ?? 0, (act as any).end ?? 0); return slice || '(selection)'; })()}
                        </span>
                        <button
                          className="absolute -top-1 -right-1 h-4 w-4 rounded-full bg-white border border-gray-300 text-gray-600 inline-flex items-center justify-center hover:bg-gray-100"
                          title="Undo font"
                          onMouseDown={(e)=>{ e.preventDefault(); e.stopPropagation(); }}
                          onClick={()=>{ (redoStacksRef.current[b.id] ||= []).push({ ...act }); undoFontFamilyAction((act as any).family, (act as any).start, (act as any).end); (actionsRef.current[b.id] ||= []).splice(((actionsRef.current[b.id]||[]).length-1)-i,1); setActionsVersion(v=>v+1); setStackOpen(true); }}
                        >
                          <svg viewBox="0 0 24 24" className="h-3 w-3" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                        </button>
                      </span>
                    ) : (
                      <span key={i} className="relative px-1.5 py-0.5 rounded bg-purple-100 text-purple-800 border border-purple-200 inline-flex items-center gap-1" title={`Color ${act.hex}`}>
                        <span className="inline-block h-3 w-3 rounded-full border" style={{ backgroundColor: (act as any).hex }} />
                        {(act as any).hex}
                        <span className="ml-1 max-w-[140px] truncate px-1 rounded bg-white/50 text-purple-900" title={(() => { const t = (act as any).text; if (t) return t; const el = elRef.current; const plain = el ? (el.innerText || '') : ''; return plain.slice((act as any).start ?? 0, (act as any).end ?? 0); })()}>
                          {(() => { const t = (act as any).text; if (t && t.length) return t; const el = elRef.current; const plain = el ? (el.innerText || '') : ''; const slice = plain.slice((act as any).start ?? 0, (act as any).end ?? 0); return slice || '(selection)'; })()}
                        </span>
                        <button
                          className="absolute -top-1 -right-1 h-4 w-4 rounded-full bg-white border border-gray-300 text-gray-600 inline-flex items-center justify-center hover:bg-gray-100"
                          title="Undo color"
                          onMouseDown={(e)=>{ e.preventDefault(); e.stopPropagation(); }}
                          onClick={()=>{ (redoStacksRef.current[b.id] ||= []).push({ ...act }); undoColorAction((act as any).hex, (act as any).start, (act as any).end); (actionsRef.current[b.id] ||= []).splice(((actionsRef.current[b.id]||[]).length-1)-i,1); setActionsVersion(v=>v+1); setStackOpen(true); }}
                        >
                          <svg viewBox="0 0 24 24" className="h-3 w-3" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                        </button>
                      </span>
                    )
                  ))}
                  {(!(actionsRef.current[b.id] && actionsRef.current[b.id].length>0)) && (
                    <span className="text-gray-400">Empty</span>
                  )}
                </div>
              </div>
            )}
            <button
              title="Add above"
              className="absolute left-1/2 -translate-x-1/2 -translate-y-1/2 top-0 transition inline-flex items-center justify-center h-7 w-7 rounded-full bg-white border shadow-sm text-purple-600 transform scale-50 hover:scale-100 opacity-70 hover:opacity-100 z-50"
              onMouseDown={(e)=> { e.preventDefault(); e.stopPropagation(); }}
              onClick={()=> { setInsertIndex(idx); setInsertGroupTargetId(null); setInsertSide(null); setShowAdd(true); }}
            >
              <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 5v14M5 12h14"/></svg>
            </button>
            <button
              title="Add below"
              className="absolute left-1/2 -translate-x-1/2 translate-y-1/2 bottom-0 transition inline-flex items-center justify-center h-7 w-7 rounded-full bg-white border shadow-sm text-purple-600 transform scale-50 hover:scale-100 opacity-70 hover:opacity-100 z-50"
              onMouseDown={(e)=> { e.preventDefault(); e.stopPropagation(); }}
              onClick={()=> { setInsertIndex(idx+1); setInsertGroupTargetId(null); setInsertSide(null); setShowAdd(true); }}
            >
              <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 5v14M5 12h14"/></svg>
            </button>
            {selectedId===b.id && (
            <div className="absolute top-1/2 -translate-y-1/2 left-[-3.5rem] flex flex-col items-center gap-2">
              <button
                title="Add left"
                className="inline-flex items-center justify-center h-7 w-7 rounded-full bg-white border shadow-sm text-purple-600 transform scale-50 hover:scale-100 opacity-70 hover:opacity-100"
                onMouseDown={(e)=> { e.preventDefault(); e.stopPropagation(); }}
                onClick={()=> { setInsertIndex(idx); setInsertGroupTargetId(b.id); setInsertSide('left'); setShowAdd(true); }}
              >
                <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 5v14M5 12h14"/></svg>
              </button>
              {b.group && (
                <button title="Move left" className="inline-flex items-center justify-center h-7 w-7 rounded-full bg-white border shadow-sm text-gray-700 transform scale-50 hover:scale-100 opacity-70 hover:opacity-100" onMouseDown={(e)=>{e.preventDefault(); e.stopPropagation();}} onClick={()=> moveBlockLeft(b.id)}>
                  <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2"><path d="M19 12H5"/><path d="M12 5l-7 7 7 7"/></svg>
                </button>
              )}
            </div>
            )}
            <div className={`absolute top-1/2 -translate-y-1/2 right-[-3.5rem] flex flex-col items-center gap-2`}>
              {isDeletableText && (
                <button
                  title="Delete paragraph"
                  className={`inline-flex items-center justify-center h-7 w-7 rounded-full border border-red-200 bg-white text-red-600 shadow-sm transform scale-50 hover:scale-100 opacity-70 hover:opacity-100`}
                  onMouseDown={(e)=>{ e.preventDefault(); e.stopPropagation(); setSelectedId(b.id); removeBlock(b.id); }}
                >
                  <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2"/></svg>
                </button>
              )}
              <button
                title="Add right"
                className="inline-flex items-center justify-center h-7 w-7 rounded-full bg-white border shadow-sm text-purple-600 transform scale-50 hover:scale-100 opacity-70 hover:opacity-100"
                onMouseDown={(e)=> { e.preventDefault(); e.stopPropagation(); }}
                onClick={()=> { setInsertIndex(idx+1); setInsertGroupTargetId(b.id); setInsertSide('right'); setShowAdd(true); }}
              >
                <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 5v14M5 12h14"/></svg>
              </button>
              {b.group && (
                <button
                  title="Move right"
                  className="inline-flex items-center justify-center h-7 w-7 rounded-full bg-white border shadow-sm text-gray-700 transform scale-50 hover:scale-100 opacity-70 hover:opacity-100"
                  onMouseDown={(e)=> { e.preventDefault(); e.stopPropagation(); }}
                  onClick={()=> moveBlockRight(b.id)}
                >
                  <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2"><path d="M5 12h14"/><path d="M12 19l7-7-7-7"/></svg>
                </button>
              )}
            </div>
          </>
        )}
      </div>
    );
  };

  const EditableCode: React.FC<{ b: PostBlock; idx: number }> = ({ b, idx }) => {
    const codeRef = useRef<HTMLElement | null>(null);
    const localRef = useRef(false);
    useEffect(() => {
      const el = codeRef.current; if (!el) return;
      if (localRef.current) return;
      if (document.activeElement === el) return;
      const html = b.text || '';
      if (el.innerHTML !== html) el.innerHTML = html;
    }, [b.id, b.type, b.text]);
    const handleInput = (e: React.FormEvent<HTMLElement>) => {
      localRef.current = true;
      onBlockInput(b.id, e as any);
      requestAnimationFrame(() => { localRef.current = false; });
    };
    const handleCodeKeyDown = (e: React.KeyboardEvent<HTMLElement>) => {
      if (e.key === 'Escape') {
        e.preventDefault(); e.stopPropagation();
        isEditingRef.current = false;
        setSelectedId(b.id);
        try { (e.currentTarget as HTMLElement).blur(); } catch {}
        requestAnimationFrame(() => setSelectedId(b.id));
        return;
      }
      if (selectionArrowMode === 'move' && selectedId === b.id && !isEditingRef.current) {
        if (e.key === 'ArrowUp') { e.preventDefault(); e.stopPropagation(); moveBlockUp(b.id); return; }
        if (e.key === 'ArrowDown') { e.preventDefault(); e.stopPropagation(); moveBlockDown(b.id); return; }
        if (e.key === 'ArrowLeft') { e.preventDefault(); e.stopPropagation(); moveBlockLeft(b.id); return; }
        if (e.key === 'ArrowRight') { e.preventDefault(); e.stopPropagation(); moveBlockRight(b.id); return; }
      }
    };
    return (
      <div className={`relative group ${selectedId===b.id ? 'z-50' : 'z-0'}`} data-block-id={b.id}>
        <pre
          data-block-id={b.id}
          className={`rounded-xl border border-gray-200 bg-gray-900 text-gray-100 text-sm p-4 overflow-auto transition-colors group-hover:border-gray-300 group-hover:border-dashed${selectedId===b.id ? ' ring-2 ring-purple-500 ring-offset-2 ring-offset-white rounded-lg' : ''}`}
          style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', overflowWrap: 'anywhere', paddingBottom: Math.max(0, dragExtraById[b.id] || 0) }}
        >
          <code
            ref={codeRef as any}
            contentEditable={editable}
            suppressContentEditableWarning
            onKeyDown={handleCodeKeyDown as any}
            onInput={handleInput as any}
            onPaste={onPasteAsPlain as any}
            onFocus={() => { isEditingRef.current = true; setSelectedId(b.id); }}
            onBlur={(e) => { isEditingRef.current = false; onBlockInput(b.id, e as any); commitEdits(); }}
          />
        </pre>
        {editable && selectedId===b.id && (
          <>
            <button
              title="Add above"
              className="absolute left-1/2 -translate-x-1/2 -translate-y-1/2 top-0 transition inline-flex items-center justify-center h-8 w-8 rounded-full bg-white border shadow-sm text-purple-600 transform scale-50 hover:scale-100 opacity-70 hover:opacity-100"
              onMouseDown={(e)=> e.preventDefault()}
              onClick={()=> { setInsertIndex(idx); setInsertGroupTargetId(null); setInsertSide(null); setShowAdd(true); }}
            >
              <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 5v14M5 12h14"/></svg>
            </button>
            <button
              title="Add below"
              className="absolute left-1/2 -translate-x-1/2 translate-y-1/2 bottom-0 transition inline-flex items-center justify-center h-8 w-8 rounded-full bg-white border shadow-sm text-purple-600 transform scale-50 hover:scale-100 opacity-70 hover:opacity-100"
              onMouseDown={(e)=> e.preventDefault()}
              onClick={()=> { setInsertIndex(idx+1); setInsertGroupTargetId(null); setInsertSide(null); setShowAdd(true); }}
            >
              <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 5v14M5 12h14"/></svg>
            </button>
            {selectedId===b.id && (
            <div className="absolute top-1/2 -translate-y-1/2 left-[-3.5rem] flex flex-col items-center gap-2">
              <button
                title="Add left"
                className="inline-flex items-center justify-center h-7 w-7 rounded-full bg-white border shadow-sm text-purple-600 transform scale-50 hover:scale-100 opacity-70 hover:opacity-100"
                onMouseDown={(e)=> e.preventDefault()}
                onClick={()=> { setInsertIndex(idx); setInsertGroupTargetId(b.id); setInsertSide('left'); setShowAdd(true); }}
              >
                <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 5v14M5 12h14"/></svg>
              </button>
              {b.group && (
                <button title="Move left" className="inline-flex items-center justify-center h-7 w-7 rounded-full bg-white border shadow-sm text-gray-700 transform scale-50 hover:scale-100 opacity-70 hover:opacity-100" onMouseDown={(e)=>{e.preventDefault(); e.stopPropagation();}} onClick={()=> moveBlockLeft(b.id)}>
                  <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2"><path d="M19 12H5"/><path d="M12 5l-7 7 7 7"/></svg>
                </button>
              )}
            </div>
            )}
            <div className={`absolute top-1/2 -translate-y-1/2 right-[-3.5rem] flex flex-col items-center gap-2`}>
              <button
                title="Delete code block"
                className={`inline-flex items-center justify-center h-7 w-7 rounded-full border border-red-200 bg-white text-red-600 shadow-sm transform scale-50 hover:scale-100 opacity-70 hover:opacity-100`}
                onMouseDown={(e)=>{ e.preventDefault(); e.stopPropagation(); setSelectedId(b.id); removeBlock(b.id); }}
              >
                <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2"/></svg>
              </button>
              <button
                title="Add right"
                className="inline-flex items-center justify-center h-7 w-7 rounded-full bg-white border shadow-sm text-purple-600 transform scale-50 hover:scale-100 opacity-70 hover:opacity-100"
                onMouseDown={(e)=> e.preventDefault()}
                onClick={()=> { setInsertIndex(idx+1); setInsertGroupTargetId(b.id); setInsertSide('right'); setShowAdd(true); }}
              >
                <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 5v14M5 12h14"/></svg>
              </button>
              {b.group && (
                <button
                  title="Move right"
                  className="inline-flex items-center justify-center h-7 w-7 rounded-full bg-white border shadow-sm text-gray-700 transform scale-50 hover:scale-100 opacity-70 hover:opacity-100"
                  onMouseDown={(e)=> e.preventDefault()}
                  onClick={()=> moveBlockRight(b.id)}
                >
                  <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2"><path d="M5 12h14"/><path d="M12 19l7-7-7-7"/></svg>
                </button>
              )}
            </div>
          </>
        )}
      </div>
    );
  };

  return (
    <div className="w-full">
      <div className="mx-auto max-w-[788px] pr-4 sm:pr-6 pl-2 sm:pl-3" ref={containerRef}
        onMouseUp={() => {
          const sel = window.getSelection();
          if (sel && sel.rangeCount > 0) {
            const r = sel.getRangeAt(0);
            if (containerRef.current && containerRef.current.contains(r.startContainer)) {
              lastSelRangeRef.current = r.cloneRange();
            }
          }
        }}
        onMouseDown={(e) => {
          // If clicking on the whitespace/background (not on a block), clear selection
          const t = e.target as HTMLElement;
          const host = containerRef.current;
          if (!host) return;
          // climb up to see if we are inside an element with data-block-id
          let el: HTMLElement | null = t;
          let insideBlock = false;
          while (el && el !== host) {
            if (el.dataset && el.dataset.blockId != null) { insideBlock = true; break; }
            el = el.parentElement;
          }
          if (!insideBlock) {
            // Commit any pending edits and remove focus from active editable
            const active = document.activeElement as HTMLElement | null;
            if (active && host.contains(active)) {
              try { active.blur(); } catch {}
            }
            commitEdits();
            setSelectedId(null);
          }
        }}
        onKeyUp={() => {
          const sel = window.getSelection();
          if (sel && sel.rangeCount > 0) {
            const r = sel.getRangeAt(0);
            if (containerRef.current && containerRef.current.contains(r.startContainer)) {
              lastSelRangeRef.current = r.cloneRange();
            }
          }
        }}
        onBlur={(e) => {
          const next = e.relatedTarget as Element | null;
          // If focus moves to a toolbar control, do not clear active block
          if (next && (next as Element).closest && (next as Element).closest('[data-editor-action]')) {
            commitEdits();
            return;
          }
          // If focus leaves the whole editor, commit pending edits and clear selection
          if (containerRef.current && !containerRef.current.contains(e.relatedTarget as Node)) {
            commitEdits();
            setSelectedId(null);
          }
        }}
      >
        {showInnerTitle && (
          <div className="mb-6">
            <input
              value={model.title}
              onChange={(e) => update({ title: e.target.value })}
              disabled={!editable}
              placeholder="Post title"
              className="w-full text-3xl md:text-4xl font-semibold tracking-tight bg-transparent outline-none border-0 focus:ring-0 placeholder-gray-400"
            />
          </div>
        )}
        {model.blocks.length === 0 && editable && (
          <div className="mt-12 mb-16 flex items-center justify-center">
            <button
              className="group relative inline-flex h-14 px-6 items-center gap-3 rounded-full bg-gradient-to-r from-purple-500 to-pink-500 text-white shadow-lg hover:shadow-xl transition-shadow"
              onMouseDown={(e)=> e.preventDefault()}
              onClick={()=> { setInsertIndex(0); setShowAdd(true); }}
              title="Add a block"
            >
              <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-white/15">
                <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 5v14M5 12h14"/></svg>
              </span>
              <span className="font-medium">Add your first block</span>
            </button>
          </div>
        )}
        <div className="mt-2 space-y-6">
          {(() => {
            const rows: PostBlock[][] = [];
            const blocks = model.blocks;
            let i = 0;
            while (i < blocks.length) {
              const cur = blocks[i];
              if (!cur.group) {
                rows.push([cur]);
                i += 1;
                continue;
              }
              const g = cur.group;
              const row: PostBlock[] = [cur];
              let j = i + 1;
              while (j < blocks.length && blocks[j].group === g) {
                row.push(blocks[j]);
                j += 1;
              }
              rows.push(row);
              i = j;
            }
            return rows.map((row, rowIdx) => {
              const cols = row.length;
              if (cols === 1) {
                const b = row[0];
                const idx = model.blocks.findIndex(x => x.id === b.id);
                return (
                  <div
                    key={`row-${rowIdx}-${b.id}`}
                    className="relative min-w-0"
                    data-row-group={(b as any).group || b.id}
                    style={{ paddingLeft: BASE_LEFT_PAD + Math.max(0, rowLeftPadByGroup[((b as any).group as string) || b.id] || 0), paddingRight: BASE_RIGHT_PAD + Math.max(0, rowRightPadByGroup[((b as any).group as string) || b.id] || 0) }}
                  >
                    {b.type === 'image' ? (
                      <div className={`relative group ${selectedId===b.id ? 'z-[200]' : 'z-10'}`} data-block-id={b.id} data-block-type="image" onMouseDown={() => { if (!editable) return; isEditingRef.current = false; setSelectedId(b.id); }}>
                        <div className={`overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm transition-colors group-hover:border-gray-300 group-hover:border-dashed${selectedId===b.id ? ' ring-2 ring-purple-500 ring-offset-2 ring-offset-white' : ''}`}>
                          <img src={b.src || ''} alt="" className="w-full h-auto max-h-[250px] object-contain" />
                        </div>
                        {selectedId===b.id && (
                        <div className={`absolute top-1/2 -translate-y-1/2 right-[-3.5rem] flex flex-col items-center gap-2`}>
                          <button
                            title="Delete image"
                            className={`inline-flex items-center justify-center h-7 w-7 rounded-full border border-red-200 bg-white text-red-600 shadow-sm`}
                            onMouseDown={(e)=>{ e.preventDefault(); e.stopPropagation(); setSelectedId(b.id); removeBlock(b.id); }}
                          >
                            <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2"/></svg>
                          </button>
                          <button
                            title="Add right"
                            className="inline-flex items-center justify-center h-7 w-7 rounded-full bg-white border shadow-sm text-purple-600"
                            onMouseDown={(e)=> e.preventDefault()}
                            onClick={()=> { setInsertIndex(idx+1); setInsertGroupTargetId(b.id); setInsertSide('right'); setShowAdd(true); }}
                          >
                            <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 5v14M5 12h14"/></svg>
                          </button>
                        </div>
                        )}
                        {editable && (
                          <>
                            {selectedId===b.id && (
                            <button
                              title="Add above"
                              className="absolute left-1/2 -translate-x-1/2 -translate-y-1/2 top-0 transition inline-flex items-center justify-center h-7 w-7 rounded-full bg-white border shadow-sm text-purple-600 transform scale-50 hover:scale-100 opacity-70 hover:opacity-100"
                              onMouseDown={(e)=> e.preventDefault()}
                              onClick={()=> { setInsertIndex(idx); setInsertGroupTargetId(null); setInsertSide(null); setShowAdd(true); }}
                            >
                              <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 5v14M5 12h14"/></svg>
                            </button>
                            )}
                            {/* Directional move arrows for image (multi-col row) */}
                            {selectedId===b.id && (
                              <>
                                <button title="Move up" className="absolute left-1/2 -translate-x-1/2 -translate-y-1/2 top-0 ml-8 z-50 inline-flex items-center justify-center h-7 w-7 rounded-full bg-white border shadow-sm text-gray-700 transform scale-50 hover:scale-100 opacity-70 hover:opacity-100" onMouseDown={(e)=>{e.preventDefault(); e.stopPropagation();}} onClick={()=> moveBlockUp(b.id)}>
                                  <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 19V5"/><path d="M5 12l7-7 7 7"/></svg>
                                </button>
                                <button title="Move down" className="absolute left-1/2 -translate-x-1/2 translate-y-1/2 bottom-0 ml-8 z-50 inline-flex items-center justify-center h-7 w-7 rounded-full bg-white border shadow-sm text-gray-700 transform scale-50 hover:scale-100 opacity-70 hover:opacity-100" onMouseDown={(e)=>{e.preventDefault(); e.stopPropagation();}} onClick={()=> moveBlockDown(b.id)}>
                                  <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 5v14"/><path d="M19 12l-7 7-7-7"/></svg>
                                </button>
                                {/** Left/Right move hidden in single-column rows for image */}
                              </>
                            )}
                            {selectedId===b.id && (
                            <button
                              title="Add below"
                              className="absolute left-1/2 -translate-x-1/2 translate-y-1/2 bottom-0 transition inline-flex items-center justify-center h-7 w-7 rounded-full bg-white border shadow-sm text-purple-600 transform scale-50 hover:scale-100 opacity-70 hover:opacity-100"
                              onMouseDown={(e)=> e.preventDefault()}
                              onClick={()=> { setInsertIndex(idx+1); setInsertGroupTargetId(null); setInsertSide(null); setShowAdd(true); }}
                            >
                              <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 5v14M5 12h14"/></svg>
                            </button>
                            )}
                            {selectedId===b.id && (
                            <div className="absolute top-1/2 -translate-y-1/2 left-0 -translate-x-full z-50 flex flex-col items-center gap-2">
                              <button
                                title="Add left"
                                className="inline-flex items-center justify-center h-7 w-7 rounded-full bg-white border shadow-sm text-purple-600 transform scale-50 hover:scale-100 opacity-70 hover:opacity-100"
                                onMouseDown={(e)=> e.preventDefault()}
                                onClick={()=> { setInsertIndex(idx); setInsertGroupTargetId(b.id); setInsertSide('left'); setShowAdd(true); }}
                              >
                                <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 5v14M5 12h14"/></svg>
                              </button>
                              <button title="Move left" className="inline-flex items-center justify-center h-7 w-7 rounded-full bg-white border shadow-sm text-gray-700 transform scale-50 hover:scale-100 opacity-70 hover:opacity-100" onMouseDown={(e)=>{e.preventDefault(); e.stopPropagation();}} onClick={()=> moveBlockLeft(b.id)}>
                                <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2"><path d="M19 12H5"/><path d="M12 5l-7 7 7 7"/></svg>
                              </button>
                            </div>
                            )}
                            {selectedId===b.id && (
                              null
                            )}
                          </>
                        )}
                      </div>
                    ) : b.type === 'timeline' ? (
                      <div className={`relative group ${selectedId===b.id ? 'z-[200]' : 'z-10'}`} data-block-id={b.id} onMouseDown={() => { isEditingRef.current = false; setSelectedId(b.id); }}>
                        <div className={`relative rounded-xl border border-gray-200 bg-white p-4 shadow-sm transition-colors group-hover:border-gray-300 group-hover:border-dashed${selectedId===b.id ? ' ring-2 ring-purple-500 ring-offset-2 ring-offset-white' : ''}`}
                          style={{ paddingBottom: Math.max(0, dragExtraById[b.id] || 0) }}>
                          {editable && (
                            <div className="absolute top-2 right-2 z-50" data-editor-action>
                              <button
                                title="Block settings"
                                className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-gray-200 bg-white text-gray-700 hover:bg-gray-50 shadow-sm z-50"
                                onMouseDown={(e)=> { e.preventDefault(); e.stopPropagation(); }}
                                onClick={()=> { setSelectedId(b.id); setBlockSettings({ id: b.id, type: 'timeline' }); }}
                              >
                                <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09A1.65 1.65 0 0 0 8 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 3.6 15a1.65 1.65 0 0 0-1.51-1H2a2 2 0 1 1 0-4h.09A1.65 1.65 0 0 0 3.6 8a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 8 3.6a1.65 1.65 0 0 0 1-1.51V2a2 2 0 1 1 4 0v.09A1.65 1.65 0 0 0 15 3.6a1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 20.4 8c0 .57.22 1.12.6 1.51.39.39.91.6 1.51.6H23a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1Z"/></svg>
                              </button>
                            </div>
                          )}
                          <Timeline
                            key={`${b.id}:${JSON.stringify({v:(b as any).timelineVariant,s:(b as any).timelineScrollable,e:(b as any).timelineExpandable,fx:(b as any).timelineEffects,n:(b.items||[]).length})}`}
                            items={b.items || []}
                            editable={false}
                            onChange={(items)=> updateBlock(b.id, { items } as any)}
                            variant={(b as any).timelineVariant || 'classic'}
                            scrollable={(b as any).timelineScrollable || false}
                            expandable={(b as any).timelineExpandable || false}
                            effects={(b as any).timelineEffects}
                          />
                          {editable && selectedId===b.id && (
                            <>
                              {/* Add above */}
                              <button
                                title="Add above"
                                className="absolute left-1/2 -translate-x-1/2 -translate-y-1/2 top-0 transition inline-flex items-center justify-center h-7 w-7 rounded-full bg-white border shadow-sm text-purple-600 transform scale-50 hover:scale-100 opacity-70 hover:opacity-100"
                                onMouseDown={(e)=> e.preventDefault()}
                                onClick={()=> { setInsertIndex(idx); setInsertGroupTargetId(null); setInsertSide(null); setShowAdd(true); }}
                              >
                                <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 5v14M5 12h14"/></svg>
                              </button>
                              {/* Move up/down */}
                              <button title="Move up" className="absolute left-1/2 -translate-x-1/2 -translate-y-1/2 top-0 ml-8 z-50 inline-flex items-center justify-center h-7 w-7 rounded-full bg-white border shadow-sm text-gray-700 transform scale-50 hover:scale-100 opacity-70 hover:opacity-100" onMouseDown={(e)=>{e.preventDefault(); e.stopPropagation();}} onClick={()=> moveBlockUp(b.id)}>
                                <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 19V5"/><path d="M5 12l7-7 7 7"/></svg>
                              </button>
                              <button title="Move down" className="absolute left-1/2 -translate-x-1/2 translate-y-1/2 bottom-0 ml-8 z-50 inline-flex items-center justify-center h-7 w-7 rounded-full bg-white border shadow-sm text-gray-700 transform scale-50 hover:scale-100 opacity-70 hover:opacity-100" onMouseDown={(e)=>{e.preventDefault(); e.stopPropagation();}} onClick={()=> moveBlockDown(b.id)}>
                                <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 5v14"/><path d="M19 12l-7 7-7-7"/></svg>
                              </button>
                              {/* Add below */}
                              <button
                                title="Add below"
                                className="absolute left-1/2 -translate-x-1/2 translate-y-1/2 bottom-0 transition inline-flex items-center justify-center h-7 w-7 rounded-full bg-white border shadow-sm text-purple-600 transform scale-50 hover:scale-100 opacity-70 hover:opacity-100"
                                onMouseDown={(e)=> e.preventDefault()}
                                onClick={()=> { setInsertIndex(idx+1); setInsertGroupTargetId(null); setInsertSide(null); setShowAdd(true); }}
                              >
                                <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 5v14M5 12h14"/></svg>
                              </button>
                              {/* Left add + move */}
                              <div className="absolute top-1/2 -translate-y-1/2 left-0 -translate-x-full z-50 flex flex-col items-center gap-2">
                                <button
                                  title="Add left"
                                  className="inline-flex items-center justify-center h-7 w-7 rounded-full bg-white border shadow-sm text-purple-600 transform scale-50 hover:scale-100 opacity-70 hover:opacity-100"
                                  onMouseDown={(e)=> e.preventDefault()}
                                  onClick={()=> { setInsertIndex(idx); setInsertGroupTargetId(b.id); setInsertSide('left'); setShowAdd(true); }}
                                >
                                  <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 5v14M5 12h14"/></svg>
                                </button>
                                <button title="Move left" className="inline-flex items-center justify-center h-7 w-7 rounded-full bg-white border shadow-sm text-gray-700 transform scale-50 hover:scale-100 opacity-70 hover:opacity-100" onMouseDown={(e)=>{e.preventDefault(); e.stopPropagation();}} onClick={()=> moveBlockLeft(b.id)}>
                                  <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2"><path d="M19 12H5"/><path d="M12 5l-7 7 7 7"/></svg>
                                </button>
                              </div>
                              {/* Right delete + add */}
                              <div className="absolute top-1/2 -translate-y-1/2 right-[-3.5rem] flex flex-col items-center gap-2">
                                <button
                                  title="Delete block"
                                  className={`inline-flex items-center justify-center h-7 w-7 rounded-full border border-red-200 bg-white text-red-600 shadow-sm`}
                                  onMouseDown={(e)=>{ e.preventDefault(); e.stopPropagation(); setSelectedId(b.id); removeBlock(b.id); }}
                                >
                                  <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2"/></svg>
                                </button>
                                <button
                                  title="Add right"
                                  className="inline-flex items-center justify-center h-7 w-7 rounded-full bg-white border shadow-sm text-purple-600"
                                  onMouseDown={(e)=> e.preventDefault()}
                                  onClick={()=> { setInsertIndex(idx+1); setInsertGroupTargetId(b.id); setInsertSide('right'); setShowAdd(true); }}
                                >
                                  <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 5v14M5 12h14"/></svg>
                                </button>
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                    ) : b.type === 'carousel' ? (
                      <div className={`relative group ${selectedId===b.id ? 'z-[200]' : 'z-10'}`} data-block-id={b.id} onMouseDown={() => { isEditingRef.current = false; setSelectedId(b.id); }}>
                        <div className={`relative rounded-xl border border-gray-200 bg-white p-4 shadow-sm transition-colors group-hover:border-gray-300 group-hover:border-dashed${selectedId===b.id ? ' ring-2 ring-purple-500 ring-offset-2 ring-offset-white' : ''}`}
                          style={{ paddingBottom: Math.max(0, dragExtraById[b.id] || 0) }}>
                          {editable && (
                            <div className="absolute top-2 right-2 z-50" data-editor-action>
                              <button
                                title="Block settings"
                                className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-gray-200 bg-white text-gray-700 hover:bg-gray-50 shadow-sm z-50"
                                onMouseDown={(e)=> { e.preventDefault(); e.stopPropagation(); }}
                                onClick={()=> { setSelectedId(b.id); setBlockSettings({ id: b.id, type: 'carousel' }); }}
                              >
                                <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09A1.65 1.65 0 0 0 8 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 3.6 15a1.65 1.65 0 0 0-1.51-1H2a2 2 0 1 1 0-4h.09A1.65 1.65 0 0 0 3.6 8a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 8 3.6a1.65 1.65 0 0 0 1-1.51V2a2 2 0 1 1 4 0v.09A1.65 1.65 0 0 0 15 3.6a1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 20.4 8c0 .57.22 1.12.6 1.51.39.39.91.6 1.51.6H23a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1Z"/></svg>
                              </button>
                            </div>
                          )}
                          <Carousel
                            slides={(b as any).slides || []}
                            editable={false}
                            onChange={(slides)=> updateBlock(b.id, { slides } as any)}
                            showDots={(b as any).carouselShowDots ?? true}
                            showArrows={(b as any).carouselShowArrows ?? true}
                          />
                          {editable && selectedId===b.id && (
                            <>
                              {/* Add above */}
                              <button
                                title="Add above"
                                className="absolute left-1/2 -translate-x-1/2 -translate-y-1/2 top-0 transition inline-flex items-center justify-center h-7 w-7 rounded-full bg-white border shadow-sm text-purple-600 transform scale-50 hover:scale-100 opacity-70 hover:opacity-100"
                                onMouseDown={(e)=> e.preventDefault()}
                                onClick={()=> { setInsertIndex(idx); setInsertGroupTargetId(null); setInsertSide(null); setShowAdd(true); }}
                              >
                                <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 5v14M5 12h14"/></svg>
                              </button>
                              {/* Move up/down */}
                              <button title="Move up" className="absolute left-1/2 -translate-x-1/2 -translate-y-1/2 top-0 ml-8 z-50 inline-flex items-center justify-center h-7 w-7 rounded-full bg-white border shadow-sm text-gray-700 transform scale-50 hover:scale-100 opacity-70 hover:opacity-100" onMouseDown={(e)=>{e.preventDefault(); e.stopPropagation();}} onClick={()=> moveBlockUp(b.id)}>
                                <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 19V5"/><path d="M5 12l7-7 7 7"/></svg>
                              </button>
                              <button title="Move down" className="absolute left-1/2 -translate-x-1/2 translate-y-1/2 bottom-0 ml-8 z-50 inline-flex items-center justify-center h-7 w-7 rounded-full bg-white border shadow-sm text-gray-700 transform scale-50 hover:scale-100 opacity-70 hover:opacity-100" onMouseDown={(e)=>{e.preventDefault(); e.stopPropagation();}} onClick={()=> moveBlockDown(b.id)}>
                                <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 5v14"/><path d="M19 12l-7 7-7-7"/></svg>
                              </button>
                              {/* Add below */}
                              <button
                                title="Add below"
                                className="absolute left-1/2 -translate-x-1/2 translate-y-1/2 bottom-0 transition inline-flex items-center justify-center h-7 w-7 rounded-full bg-white border shadow-sm text-purple-600 transform scale-50 hover:scale-100 opacity-70 hover:opacity-100"
                                onMouseDown={(e)=> e.preventDefault()}
                                onClick={()=> { setInsertIndex(idx+1); setInsertGroupTargetId(null); setInsertSide(null); setShowAdd(true); }}
                              >
                                <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 5v14M5 12h14"/></svg>
                              </button>
                              {/* Left add + move */}
                              <div className="absolute top-1/2 -translate-y-1/2 left-0 -translate-x-full z-50 flex flex-col items-center gap-2">
                                <button
                                  title="Add left"
                                  className="inline-flex items-center justify-center h-7 w-7 rounded-full bg-white border shadow-sm text-purple-600 transform scale-50 hover:scale-100 opacity-70 hover:opacity-100"
                                  onMouseDown={(e)=> e.preventDefault()}
                                  onClick={()=> { setInsertIndex(idx); setInsertGroupTargetId(b.id); setInsertSide('left'); setShowAdd(true); }}
                                >
                                  <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 5v14M5 12h14"/></svg>
                                </button>
                                <button title="Move left" className="inline-flex items-center justify-center h-7 w-7 rounded-full bg-white border shadow-sm text-gray-700 transform scale-50 hover:scale-100 opacity-70 hover:opacity-100" onMouseDown={(e)=>{e.preventDefault(); e.stopPropagation();}} onClick={()=> moveBlockLeft(b.id)}>
                                  <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2"><path d="M19 12H5"/><path d="M12 5l-7 7 7 7"/></svg>
                                </button>
                              </div>
                              {/* Right delete + add */}
                              <div className="absolute top-1/2 -translate-y-1/2 right-[-3.5rem] flex flex-col items-center gap-2">
                                <button
                                  title="Delete block"
                                  className={`inline-flex items-center justify-center h-7 w-7 rounded-full border border-red-200 bg-white text-red-600 shadow-sm`}
                                  onMouseDown={(e)=>{ e.preventDefault(); e.stopPropagation(); setSelectedId(b.id); removeBlock(b.id); }}
                                >
                                  <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/></svg>
                                </button>
                                <button
                                  title="Add right"
                                  className="inline-flex items-center justify-center h-7 w-7 rounded-full bg-white border shadow-sm text-purple-600"
                                  onMouseDown={(e)=> e.preventDefault()}
                                  onClick={()=> { setInsertIndex(idx+1); setInsertGroupTargetId(b.id); setInsertSide('right'); setShowAdd(true); }}
                                >
                                  <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 5v14M5 12h14"/></svg>
                                </button>
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                    ) : b.type === 'faq' ? (
                      <div className={`relative group ${selectedId===b.id ? 'z-[200]' : 'z-10'}`} data-block-id={b.id} onMouseDown={() => { isEditingRef.current = false; setSelectedId(b.id); }}>
                        <div className={`relative rounded-xl border border-gray-200 bg-white p-4 shadow-sm transition-colors group-hover:border-gray-300 group-hover:border-dashed${selectedId===b.id ? ' ring-2 ring-purple-500 ring-offset-2 ring-offset-white' : ''}`}>
                          {editable && (
                            <div className="absolute top-2 right-2 z-50" data-editor-action>
                              <button
                                title="Block settings"
                                className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-gray-200 bg-white text-gray-700 hover:bg-gray-50 shadow-sm z-50"
                                onMouseDown={(e)=> { e.preventDefault(); e.stopPropagation(); }}
                                onClick={()=> { setSelectedId(b.id); setBlockSettings({ id: b.id, type: 'faq' }); }}
                              >
                                <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09A1.65 1.65 0 0 0 8 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 3.6 15a1.65 1.65 0 0 0-1.51-1H2a2 2 0 1 1 0-4h.09A1.65 1.65 0 0 0 3.6 8a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 8 3.6a1.65 1.65 0 0 0 1-1.51V2a2 2 0 1 1 4 0v.09A1.65 1.65 0 0 0 15 3.6a1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 20.4 8c0 .57.22 1.12.6 1.51.39.39.91.6 1.51.6H23a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1Z"/></svg>
                              </button>
                            </div>
                          )}
                          <FAQ items={(b as any).faqItems || []} palette={(b as any).faqPalette} style={(b as any).faqStyle} showIcons={(b as any).faqShowIcons} icon={(b as any).faqIcon} />
                          {editable && selectedId===b.id && (
                            <>
                              {/* Add above */}
                              <button
                                title="Add above"
                                className="absolute left-1/2 -translate-x-1/2 -translate-y-1/2 top-0 transition inline-flex items-center justify-center h-7 w-7 rounded-full bg-white border shadow-sm text-purple-600 transform scale-50 hover:scale-100 opacity-70 hover:opacity-100"
                                onMouseDown={(e)=> e.preventDefault()}
                                onClick={()=> { setInsertIndex(idx); setInsertGroupTargetId(null); setInsertSide(null); setShowAdd(true); }}
                              >
                                <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 5v14M5 12h14"/></svg>
                              </button>
                              {/* Move up/down */}
                              <button title="Move up" className="absolute left-1/2 -translate-x-1/2 -translate-y-1/2 top-0 ml-8 z-50 inline-flex items-center justify-center h-7 w-7 rounded-full bg-white border shadow-sm text-gray-700 transform scale-50 hover:scale-100 opacity-70 hover:opacity-100" onMouseDown={(e)=>{e.preventDefault(); e.stopPropagation();}} onClick={()=> moveBlockUp(b.id)}>
                                <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 19V5"/><path d="M5 12l7-7 7 7"/></svg>
                              </button>
                              <button title="Move down" className="absolute left-1/2 -translate-x-1/2 translate-y-1/2 bottom-0 ml-8 z-50 inline-flex items-center justify-center h-7 w-7 rounded-full bg-white border shadow-sm text-gray-700 transform scale-50 hover:scale-100 opacity-70 hover:opacity-100" onMouseDown={(e)=>{e.preventDefault(); e.stopPropagation();}} onClick={()=> moveBlockDown(b.id)}>
                                <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 5v14"/><path d="M19 12l-7 7-7-7"/></svg>
                              </button>
                              {/* Add below */}
                              <button
                                title="Add below"
                                className="absolute left-1/2 -translate-x-1/2 translate-y-1/2 bottom-0 transition inline-flex items-center justify-center h-7 w-7 rounded-full bg-white border shadow-sm text-purple-600 transform scale-50 hover:scale-100 opacity-70 hover:opacity-100"
                                onMouseDown={(e)=> e.preventDefault()}
                                onClick={()=> { setInsertIndex(idx+1); setInsertGroupTargetId(null); setInsertSide(null); setShowAdd(true); }}
                              >
                                <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 5v14M5 12h14"/></svg>
                              </button>
                              {/* Right delete + add */}
                              <div className="absolute top-1/2 -translate-y-1/2 right-[-3.5rem] flex flex-col items-center gap-2">
                                <button
                                  title="Delete block"
                                  className={`inline-flex items-center justify-center h-7 w-7 rounded-full border border-red-200 bg-white text-red-600 shadow-sm`}
                                  onMouseDown={(e)=>{ e.preventDefault(); e.stopPropagation(); setSelectedId(b.id); removeBlock(b.id); }}
                                >
                                  <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2"/></svg>
                                </button>
                                <button
                                  title="Add right"
                                  className="inline-flex items-center justify-center h-7 w-7 rounded-full bg-white border shadow-sm text-purple-600"
                                  onMouseDown={(e)=> e.preventDefault()}
                                  onClick={()=> { setInsertIndex(idx+1); setInsertGroupTargetId(b.id); setInsertSide('right'); setShowAdd(true); }}
                                >
                                  <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 5v14M5 12h14"/></svg>
                                </button>
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                    ) : b.type === 'table' ? (
                      <div className={`relative group ${selectedId===b.id ? 'z-[200]' : 'z-10'}`} data-block-id={b.id} data-block-type="table" onMouseDown={() => { if (!editable) return; isEditingRef.current = false; setSelectedId(b.id); }}>
                        <div className={`relative rounded-xl${(b as any).hoverOutline !== false ? ' border border-gray-200' : ''} bg-white p-3${(b as any).hoverOutline !== false ? ' shadow-sm' : ''} transition-colors ${(b as any).hoverOutline !== false ? 'group-hover:border-gray-300 group-hover:border-dashed' : ''}${selectedId===b.id ? ' ring-2 ring-purple-500 ring-offset-2 ring-offset-white' : ''}`
                        } style={{ paddingBottom: Math.max(0, dragExtraById[b.id] || 0) }}>
                          {editable && (
                            <div className="absolute top-2 right-2 z-50" data-editor-action>
                              <button
                                title="Block settings"
                                className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-gray-200 bg-white text-gray-700 hover:bg-gray-50 shadow-sm z-50"
                                onMouseDown={(e)=> { e.preventDefault(); e.stopPropagation(); }}
                                onClick={()=> { setSelectedId(b.id); setBlockSettings({ id: b.id, type: 'table' }); }}
                              >
                                <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09A1.65 1.65 0 0 0 8 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 3.6 15a1.65 1.65 0 0 0-1.51-1H2a2 2 0 1 1 0-4h.09A1.65 1.65 0 0 0 3.6 8a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 8 3.6a1.65 1.65 0 0 0 1-1.51V2a2 2 0 1 1 4 0v.09A1.65 1.65 0 0 0 15 3.6a1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 20.4 8c0 .57.22 1.12.6 1.51.39.39.91.6 1.51.6H23a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1Z"/></svg>
                              </button>
                            </div>
                          )}
                          <Table
                            rows={(b as any).rows || 0}
                            cols={(b as any).cols || 0}
                            data={(b as any).data || []}
                            colWidths={(b as any).colWidths || []}
                            rowHeights={(b as any).rowHeights || []}
                            dense={(b as any).dense || false}
                            bordered={(b as any).bordered ?? true}
                            fitCols={(b as any).fitCols || false}
                            fitRows={(b as any).fitRows || false}
                            scrollable={(b as any).scrollable || false}
                            hoverOutline={(b as any).hoverOutline !== false}
                            editable={editable && selectedId===b.id}
                            showResizeHandles={editable && selectedId===b.id && !((b as any).fitCols || (b as any).fitRows)}
                            onChange={(next)=> updateBlock(b.id, { rows: next.rows, cols: next.cols, data: next.data, colWidths: next.colWidths, rowHeights: next.rowHeights, dense: next.dense, bordered: next.bordered, fitCols: (next as any).fitCols, fitRows: (next as any).fitRows, scrollable: (next as any).scrollable, hoverOutline: (next as any).hoverOutline } as any)}
                          />
                          {editable && selectedId===b.id && (
                            <>
                              {/* Add above */}
                              <button
                                title="Add above"
                                className="absolute left-1/2 -translate-x-1/2 -translate-y-1/2 top-0 transition inline-flex items-center justify-center h-7 w-7 rounded-full bg-white border shadow-sm text-purple-600 transform scale-50 hover:scale-100 opacity-70 hover:opacity-100"
                                onMouseDown={(e)=> e.preventDefault()}
                                onClick={()=> { setInsertIndex(idx); setInsertGroupTargetId(null); setInsertSide(null); setShowAdd(true); }}
                              >
                                <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 5v14M5 12h14"/></svg>
                              </button>
                              {/* Move up/down */}
                              <button title="Move up" className="absolute left-1/2 -translate-x-1/2 -translate-y-1/2 top-0 ml-8 z-50 inline-flex items-center justify-center h-7 w-7 rounded-full bg-white border shadow-sm text-gray-700 transform scale-50 hover:scale-100 opacity-70 hover:opacity-100" onMouseDown={(e)=>{e.preventDefault(); e.stopPropagation();}} onClick={()=> moveBlockUp(b.id)}>
                                <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 19V5"/><path d="M5 12l7-7 7 7"/></svg>
                              </button>
                              <button title="Move down" className="absolute left-1/2 -translate-x-1/2 translate-y-1/2 bottom-0 ml-8 z-50 inline-flex items-center justify-center h-7 w-7 rounded-full bg-white border shadow-sm text-gray-700 transform scale-50 hover:scale-100 opacity-70 hover:opacity-100" onMouseDown={(e)=>{e.preventDefault(); e.stopPropagation();}} onClick={()=> moveBlockDown(b.id)}>
                                <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 5v14"/><path d="M19 12l-7 7-7-7"/></svg>
                              </button>
                              {/* Add below */}
                              <button
                                title="Add below"
                                className="absolute left-1/2 -translate-x-1/2 translate-y-1/2 bottom-0 transition inline-flex items-center justify-center h-7 w-7 rounded-full bg-white border shadow-sm text-purple-600 transform scale-50 hover:scale-100 opacity-70 hover:opacity-100"
                                onMouseDown={(e)=> e.preventDefault()}
                                onClick={()=> { setInsertIndex(idx+1); setInsertGroupTargetId(null); setInsertSide(null); setShowAdd(true); }}
                              >
                                <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 5v14M5 12h14"/></svg>
                              </button>
                              {/* Right delete + add */}
                              <div className="absolute top-1/2 -translate-y-1/2 right-[-3.5rem] flex flex-col items-center gap-2">
                                <button
                                  title="Delete block"
                                  className={`inline-flex items-center justify-center h-7 w-7 rounded-full border border-red-200 bg-white text-red-600 shadow-sm`}
                                  onMouseDown={(e)=>{ e.preventDefault(); e.stopPropagation(); setSelectedId(b.id); removeBlock(b.id); }}
                                >
                                  <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/></svg>
                                </button>
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                    ) : b.type === 'embed' ? (
                      <div className={`relative group ${selectedId===b.id ? 'z-20' : 'z-10'}`} data-block-id={b.id} data-block-type="embed" onMouseDown={() => { if (!editable) return; isEditingRef.current = false; setSelectedId(b.id); }}>
                        <div className={`relative rounded-xl border border-gray-200 bg-white p-3 shadow-sm transition-colors group-hover:border-gray-300 group-hover:border-dashed${selectedId===b.id ? ' ring-2 ring-purple-500 ring-offset-2 ring-offset-white' : ''}`}
                          style={{ paddingBottom: Math.max(0, dragExtraById[b.id] || 0) }}>
                          {editable && (
                            <div className="absolute top-2 right-2 z-50" data-editor-action>
                              <button
                                title={embedPlaceMode===b.id ? 'Confirm position' : 'Position in blog'}
                                className={`inline-flex h-8 w-8 items-center justify-center rounded-md border border-gray-200 bg-white ${embedPlaceMode===b.id ? 'text-green-600' : 'text-gray-700'} hover:bg-gray-50 shadow-sm mr-2 ${selectedId===b.id ? '' : 'opacity-50 grayscale pointer-events-none cursor-default text-gray-400'}`}
                                onMouseDown={(e)=> { e.preventDefault(); e.stopPropagation(); }}
                                ref={(el)=> { if (embedPlaceMode===b.id) quickPortalAnchorRef.current = (el as unknown as HTMLElement) || null; }}
                                onClick={(e)=> {
                                  if (selectedId!==b.id) return;
                                  setSelectedId(b.id);
                                  if (embedPlaceMode===b.id) {
                                    setEmbedPlaceMode(null);
                                    setEmbedPlaceTargetIndex(null);
                                    quickPortalAnchorRef.current = null;
                                    setQuickPortalPos(null);
                                  } else {
                                    setEmbedPlaceMode(b.id);
                                    setEmbedPlaceTargetIndex(model.blocks.findIndex(x=>x.id===b.id));
                                    quickPortalAnchorRef.current = e.currentTarget as unknown as HTMLElement;
                                    requestAnimationFrame(() => updateQuickPortalFromEl(quickPortalAnchorRef.current));
                                  }
                                }}
                              >
                                <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  <path d="M12 2v5"/>
                                  <path d="M12 17v5"/>
                                  <path d="M2 12h5"/>
                                  <path d="M17 12h5"/>
                                  <path d="M12 2l-2 2 2-2 2 2-2-2z" opacity="0"/>
                                  <path d="M8 6l4-4 4 4"/>
                                  <path d="M8 18l4 4 4-4"/>
                                  <path d="M6 8l-4 4 4 4"/>
                                  <path d="M18 8l4 4-4 4"/>
                                </svg>
                              </button>
                              {/* Quick sliders for X/Y offsets when positioning mode is active */}
                              {embedPlaceMode===b.id && quickPortalPos && createPortal(
                                <div
                                  style={{ position: 'fixed', left: quickPortalPos.left, top: quickPortalPos.top, zIndex: 1000 }}
                                  className="w-64 rounded-md border border-gray-200 bg-white p-3 shadow-sm"
                                  data-quick-portal
                                  onMouseDown={(e)=>{ e.stopPropagation(); }}
                                >
                                  <div className="text-xs font-medium text-gray-700 mb-2">Quick adjust</div>
                                  <label className="block text-[11px] text-gray-600 mb-1">Horizontal (px)</label>
                                  <input
                                    type="range"
                                    min={-300}
                                    max={300}
                                    step={1}
                                    value={(quickOffsetById[b.id]?.x ?? (b as any).embedOffsetX ?? 0)}
                                    onChange={(e)=> {
                                      const n = parseInt(e.target.value, 10) || 0;
                                      setQuickOffset(b.id, { x: n });
                                      if (blockSettings && blockSettings.type==='embed' && blockSettings.id===b.id) {
                                        setEmbedPreview(prev => (prev && prev.id===b.id ? { ...prev, offsetX: n } : prev));
                                      }
                                    }}
                                    onMouseUp={(e)=>{ const n = parseInt((e.target as HTMLInputElement).value,10)||0; updateBlock(b.id,{ embedOffsetX: n } as any); setQuickOffsetById(prev=> ({...prev, [b.id]: { ...(prev[b.id]||{}), x: undefined }})); }}
                                    onTouchEnd={(e)=>{ const el = e.currentTarget as HTMLInputElement; const n = parseInt(el.value,10)||0; updateBlock(b.id,{ embedOffsetX: n } as any); setQuickOffsetById(prev=> ({...prev, [b.id]: { ...(prev[b.id]||{}), x: undefined }})); }}
                                    className="w-full accent-purple-600"
                                  />
                                  <div className="flex justify-between text-[11px] text-gray-500 mb-2"><span>-300</span><span>0</span><span>+300</span></div>
                                  <label className="block text-[11px] text-gray-600 mb-1">Vertical (px)</label>
                                  <input
                                    type="range"
                                    min={-300}
                                    max={300}
                                    step={1}
                                    value={(quickOffsetById[b.id]?.y ?? (b as any).embedOffsetY ?? 0)}
                                    onChange={(e)=> {
                                      const n = parseInt(e.target.value, 10) || 0;
                                      setQuickOffset(b.id, { y: n });
                                      if (blockSettings && blockSettings.type==='embed' && blockSettings.id===b.id) {
                                        setEmbedPreview(prev => (prev && prev.id===b.id ? { ...prev, offsetY: n } : prev));
                                      }
                                    }}
                                    onMouseUp={(e)=>{ const n = parseInt((e.target as HTMLInputElement).value,10)||0; updateBlock(b.id,{ embedOffsetY: n } as any); setQuickOffsetById(prev=> ({...prev, [b.id]: { ...(prev[b.id]||{}), y: undefined }})); }}
                                    onTouchEnd={(e)=>{ const el = e.currentTarget as HTMLInputElement; const n = parseInt(el.value,10)||0; updateBlock(b.id,{ embedOffsetY: n } as any); setQuickOffsetById(prev=> ({...prev, [b.id]: { ...(prev[b.id]||{}), y: undefined }})); }}
                                    className="w-full accent-purple-600"
                                  />
                                  <div className="flex justify-between text-[11px] text-gray-500"><span>-300</span><span>0</span><span>+300</span></div>
                                  <div className="mt-2 text-[11px] text-gray-500">
                                    Hold <span className="font-medium">Shift</span> to move vertical only. Hold <span className="font-medium">Ctrl</span> to move horizontal only.
                                  </div>
                                </div>,
                                document.body
                              )}
                              <button
                                title="Block settings"
                                className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-gray-200 bg-white text-gray-700 hover:bg-gray-50 shadow-sm z-50"
                                onMouseDown={(e)=> { e.preventDefault(); e.stopPropagation(); }}
                                onClick={()=> { setSelectedId(b.id); setBlockSettings({ id: b.id, type: 'embed' }); }}
                              >
                                <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09A1.65 1.65 0 0 0 8 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 3.6 15a1.65 1.65 0 0 0-1.51-1H2a2 2 0 1 1 0-4h.09A1.65 1.65 0 0 0 3.6 8a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 8 3.6a1.65 1.65 0 0 0 1-1.51V2a2 2 0 1 1 4 0v.09A1.65 1.65 0 0 0 15 3.6a1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 20.4 8c0 .57.22 1.12.6 1.51.39.39.91.6 1.51.6H23a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1Z"/></svg>
                              </button>
                            </div>
                          )}
                          {embedPlaceMode===b.id && (
                            <div
                              className="absolute inset-0 z-40 cursor-move"
                              data-embed-drag-overlay
                              onMouseDown={(e)=>{
                                if (!editable) return;
                                const startX = e.clientX;
                                const startY = e.clientY;
                                const curOffX = (quickOffsetById[b.id]?.x ?? (b as any).embedOffsetX ?? 0) as number;
                                const curOffY = (quickOffsetById[b.id]?.y ?? (b as any).embedOffsetY ?? 0) as number;
                                embedDragActiveRef.current = true;
                                embedDragStartRef.current = { id: b.id, x: startX, y: startY, offX: curOffX, offY: curOffY };
                                e.preventDefault();
                                e.stopPropagation();
                              }}
                            />
                          )}
                          <Embed
                            html={(b as any).embedHtml}
                            heightMode={(b as any).embedHeightMode || 'auto'}
                            fixedHeight={(b as any).embedFixedHeight || 360}
                            bordered={(b as any).embedBordered ?? true}
                            radius={(b as any).embedRadius ?? 12}
                            shadow={(b as any).embedShadow ?? false}
                            allowScripts={(b as any).embedAllowScripts ?? true}
                            allowSameOrigin={(b as any).embedAllowSameOrigin ?? true}
                            allowPopups={(b as any).embedAllowPopups ?? true}
                            allowScroll={(b as any).embedAllowScroll ?? false}
                            removeBg={(b as any).embedRemoveBg ?? false}
                            responsiveFit={(b as any).embedResponsiveFit ?? false}
                            offsetX={(quickOffsetById[b.id]?.x ?? (b as any).embedOffsetX) ?? 0}
                            offsetY={(quickOffsetById[b.id]?.y ?? (b as any).embedOffsetY) ?? 0}
                            interactive={!editable}
                            onApplyInsert={({ ratio, zoom }) => {
                              try {
                                const cur = modelRef.current;
                                const idxHere = cur.blocks.findIndex(x => x.id === b.id);
                                if (idxHere === -1) return;
                                const clone = { id: newId(), type: 'embeddims', embedHtml: (b as any).embedHtml, embedDimRatio: Number(ratio) || (16/9), embedDimZoom: Number(zoom) || 1 } as PostBlock;
                                const nextBlocks = [
                                  ...cur.blocks.slice(0, idxHere + 1),
                                  clone,
                                  ...cur.blocks.slice(idxHere + 1),
                                ];
                                const next = { ...cur, blocks: nextBlocks };
                                modelRef.current = next;
                                setModel(next);
                                onChange && onChange(next);
                                setSelectedId(clone.id);
                              } catch {}
                            }}
                          />
                          {editable && selectedId===b.id && (
                            <>
                              {/* Add above */}
                              <button
                                title="Add above"
                                className="absolute left-1/2 -translate-x-1/2 -translate-y-1/2 top-0 transition inline-flex items-center justify-center h-7 w-7 rounded-full bg-white border shadow-sm text-purple-600 transform scale-50 hover:scale-100 opacity-70 hover:opacity-100"
                                onMouseDown={(e)=> e.preventDefault()}
                                onClick={()=> { setInsertIndex(idx); setInsertGroupTargetId(null); setInsertSide(null); setShowAdd(true); }}
                              >
                                <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 5v14M5 12h14"/></svg>
                              </button>
                              {/* Move up/down */}
                              <button title="Move up" className="absolute left-1/2 -translate-x-1/2 -translate-y-1/2 top-0 ml-8 z-50 inline-flex items-center justify-center h-7 w-7 rounded-full bg-white border shadow-sm text-gray-700 transform scale-50 hover:scale-100 opacity-70 hover:opacity-100" onMouseDown={(e)=>{e.preventDefault(); e.stopPropagation();}} onClick={()=> moveBlockUp(b.id)}>
                                <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 19V5"/><path d="M5 12l7-7 7 7"/></svg>
                              </button>
                              <button title="Move down" className="absolute left-1/2 -translate-x-1/2 translate-y-1/2 bottom-0 ml-8 z-50 inline-flex items-center justify-center h-7 w-7 rounded-full bg-white border shadow-sm text-gray-700 transform scale-50 hover:scale-100 opacity-70 hover:opacity-100" onMouseDown={(e)=>{e.preventDefault(); e.stopPropagation();}} onClick={()=> moveBlockDown(b.id)}>
                                <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 5v14"/><path d="M19 12l-7 7-7-7"/></svg>
                              </button>
                              {/* Add below */}
                              <button
                                title="Add below"
                                className="absolute left-1/2 -translate-x-1/2 translate-y-1/2 bottom-0 transition inline-flex items-center justify-center h-7 w-7 rounded-full bg-white border shadow-sm text-purple-600 transform scale-50 hover:scale-100 opacity-70 hover:opacity-100"
                                onMouseDown={(e)=> e.preventDefault()}
                                onClick={()=> { setInsertIndex(idx+1); setInsertGroupTargetId(null); setInsertSide(null); setShowAdd(true); }}
                              >
                                <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 5v14M5 12h14"/></svg>
                              </button>
                              {/* Left add + move */}
                              <div className="absolute top-1/2 -translate-y-1/2 left-0 -translate-x-full z-50 flex flex-col items-center gap-2">
                                <button
                                  title="Add left"
                                  className="inline-flex items-center justify-center h-7 w-7 rounded-full bg-white border shadow-sm text-purple-600 transform scale-50 hover:scale-100 opacity-70 hover:opacity-100"
                                  onMouseDown={(e)=> e.preventDefault()}
                                  onClick={()=> { setInsertIndex(idx); setInsertGroupTargetId(b.id); setInsertSide('left'); setShowAdd(true); }}
                                >
                                  <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 5v14M5 12h14"/></svg>
                                </button>
                                <button title="Move left" className="inline-flex items-center justify-center h-7 w-7 rounded-full bg-white border shadow-sm text-gray-700 transform scale-50 hover:scale-100 opacity-70 hover:opacity-100" onMouseDown={(e)=>{e.preventDefault(); e.stopPropagation();}} onClick={()=> moveBlockLeft(b.id)}>
                                  <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2"><path d="M19 12H5"/><path d="M12 5l-7 7 7 7"/></svg>
                                </button>
                              </div>
                              {/* Right delete + add */}
                              <div className="absolute top-1/2 -translate-y-1/2 right-[-3.5rem] flex flex-col items-center gap-2">
                                <button
                                  title="Delete block"
                                  className={`inline-flex items-center justify-center h-7 w-7 rounded-full border border-red-200 bg-white text-red-600 shadow-sm`}
                                  onMouseDown={(e)=>{ e.preventDefault(); e.stopPropagation(); setSelectedId(b.id); removeBlock(b.id); }}
                                >
                                  <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/></svg>
                                </button>
                                <button
                                  title="Add right"
                                  className="inline-flex items-center justify-center h-7 w-7 rounded-full bg-white border shadow-sm text-purple-600"
                                  onMouseDown={(e)=> e.preventDefault()}
                                  onClick={()=> { setInsertIndex(idx+1); setInsertGroupTargetId(b.id); setInsertSide('right'); setShowAdd(true); }}
                                >
                                  <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 5v14M5 12h14"/></svg>
                                </button>
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                    ) : b.type === 'embeddims' ? (
                      <div className="relative" data-block-id={b.id} onMouseDown={() => { if (!editable) return; isEditingRef.current = false; setSelectedId(b.id); }}>
                        <div className={`overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm transition-colors${selectedId===b.id ? ' ring-2 ring-purple-500 ring-offset-2 ring-offset-white' : ''}`}>
                          {/* overlay to capture clicks inside iframe while editing to select the block */}
                          {editable && (
                            <div
                              className="absolute z-30"
                              style={{ pointerEvents: 'auto', left: 0, right: 0, top: 0, bottom: 24 }}
                              onMouseDown={(e)=>{ e.preventDefault(); e.stopPropagation(); setSelectedId(b.id); }}
                            />
                          )}
                          <EmbedDimsBlock
                            html={(b as any).embedHtml}
                            ratio={(b as any).embedDimRatio || (16/9)}
                            zoom={(b as any).embedDimZoom || 1}
                            extraDelta={(dragExtraById[b.id]||0)}
                            onApply={({ ratio, zoom }) => {
                              try {
                                const cur = modelRef.current;
                                const idx = cur.blocks.findIndex(x => x.id === b.id);
                                if (idx === -1) return;
                                const nextBlocks = cur.blocks.slice();
                                nextBlocks[idx] = { ...nextBlocks[idx], embedDimRatio: Number(ratio) || (16/9), embedDimZoom: Number(zoom) || 1 } as any;
                                const next = { ...cur, blocks: nextBlocks };
                                modelRef.current = next;
                                setModel(next);
                                onChange && onChange(next);
                              } catch {}
                            }}
                          />
                        </div>
                        {editable && selectedId===b.id && (
                          <>
                            <button
                              title="Add above"
                              className="absolute left-1/2 -translate-x-1/2 -translate-y-1/2 top-0 transition inline-flex items-center justify-center h-7 w-7 rounded-full bg-white border shadow-sm text-purple-600 transform scale-50 hover:scale-100 opacity-70 hover:opacity-100"
                              onMouseDown={(e)=> e.preventDefault()}
                              onClick={()=> { setInsertIndex(idx); setInsertGroupTargetId(null); setInsertSide(null); setShowAdd(true); }}
                            >
                              <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 5v14M5 12h14"/></svg>
                            </button>
                            <button title="Move up" className="absolute left-1/2 -translate-x-1/2 -translate-y-1/2 top-0 ml-8 z-50 inline-flex items-center justify-center h-7 w-7 rounded-full bg-white border shadow-sm text-gray-700 transform scale-50 hover:scale-100 opacity-70 hover:opacity-100" onMouseDown={(e)=>{e.preventDefault(); e.stopPropagation();}} onClick={()=> moveBlockUp(b.id)}>
                              <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 19V5"/><path d="M5 12l7-7 7 7"/></svg>
                            </button>
                            <button title="Move down" className="absolute left-1/2 -translate-x-1/2 translate-y-1/2 bottom-0 ml-8 z-50 inline-flex items-center justify-center h-7 w-7 rounded-full bg-white border shadow-sm text-gray-700 transform scale-50 hover:scale-100 opacity-70 hover:opacity-100" onMouseDown={(e)=>{e.preventDefault(); e.stopPropagation();}} onClick={()=> moveBlockDown(b.id)}>
                              <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 5v14"/><path d="M19 12l-7 7-7-7"/></svg>
                            </button>
                            <button
                              title="Add below"
                              className="absolute left-1/2 -translate-x-1/2 translate-y-1/2 bottom-0 transition inline-flex items-center justify-center h-7 w-7 rounded-full bg-white border shadow-sm text-purple-600 transform scale-50 hover:scale-100 opacity-70 hover:opacity-100"
                              onMouseDown={(e)=> e.preventDefault()}
                              onClick={()=> { setInsertIndex(idx+1); setInsertGroupTargetId(null); setInsertSide(null); setShowAdd(true); }}
                            >
                              <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 5v14M5 12h14"/></svg>
                            </button>
                            {/* Left add + move */}
                            <div className="absolute top-1/2 -translate-y-1/2 left-0 -translate-x-full z-50 flex flex-col items-center gap-2">
                              <button
                                title="Add left"
                                className="inline-flex items-center justify-center h-7 w-7 rounded-full bg-white border shadow-sm text-purple-600 transform scale-50 hover:scale-100 opacity-70 hover:opacity-100"
                                onMouseDown={(e)=> e.preventDefault()}
                                onClick={()=> { setInsertIndex(idx); setInsertGroupTargetId(b.id); setInsertSide('left'); setShowAdd(true); }}
                              >
                                <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 5v14M5 12h14"/></svg>
                              </button>
                              <button title="Move left" className="inline-flex items-center justify-center h-7 w-7 rounded-full bg-white border shadow-sm text-gray-700 transform scale-50 hover:scale-100 opacity-70 hover:opacity-100" onMouseDown={(e)=>{e.preventDefault(); e.stopPropagation();}} onClick={()=> moveBlockLeft(b.id)}>
                                <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2"><path d="M19 12H5"/><path d="M12 5l-7 7 7 7"/></svg>
                              </button>
                            </div>
                            <div className="absolute top-1/2 -translate-y-1/2 right-[-3.5rem] flex flex-col items-center gap-2">
                              <button
                                title="Delete block"
                                className={`inline-flex items-center justify-center h-7 w-7 rounded-full border border-red-200 bg-white text-red-600 shadow-sm`}
                                onMouseDown={(e)=>{ e.preventDefault(); e.stopPropagation(); setSelectedId(b.id); removeBlock(b.id); }}
                              >
                                <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/></svg>
                              </button>
                              <button
                                title="Add right"
                                className="inline-flex items-center justify-center h-7 w-7 rounded-full bg-white border shadow-sm text-purple-600"
                                onMouseDown={(e)=> e.preventDefault()}
                                onClick={()=> { setInsertIndex(idx+1); setInsertGroupTargetId(b.id); setInsertSide('right'); setShowAdd(true); }}
                              >
                                <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 5v14M5 12h14"/></svg>
                              </button>
                            </div>
                          </>
                        )}
                      </div>
                    ) : b.type === 'code' ? (
                      <EditableCode b={b} idx={idx} />
                    ) : (
                      <EditableBlock b={b} idx={idx} />
                    )}
                    {editable && (selectedId===b.id || (dragExtraById[b.id]||0) > 0) && (
                      <>
                        <div style={{ height: 0 }} />
                        {selectedId===b.id && (
                          <div
                            className="mt-2 cursor-row-resize select-none"
                            data-block-id={b.id}
                            onMouseDown={(e)=>{ isEditingRef.current = false; setSelectedId(b.id); startDragResize(e, b.id); }}
                          >
                            <DragLine />
                          </div>
                        )}
                      </>
                    )}
                    {editable && selectedId===b.id && (
                      <div
                        className="absolute top-0 left-full ml-3 h-full w-1 cursor-col-resize select-none z-50"
                        onMouseDown={(e)=> startRightPaddingResize(e, (((b as any).group as string) || b.id))}
                        title="Adjust right padding"
                      >
                        <div className="h-full w-full rounded-full bg-gradient-to-b from-purple-400 via-fuchsia-400 to-pink-400 shadow-sm" />
                      </div>
                    )}
                    {editable && selectedId===b.id && (
                      <div
                        className="absolute top-0 right-full mr-5 h-full w-1 cursor-col-resize select-none z-40"
                        onMouseDown={(e)=> startLeftPaddingResize(e, (((b as any).group as string) || b.id))}
                        title="Adjust left padding"
                      >
                        <div className="h-full w-full rounded-full bg-gradient-to-b from-purple-400 via-fuchsia-400 to-pink-400 shadow-sm" />
                      </div>
                    )}
                  </div>
                );
              }
              // multi-col row
              return (
                <div
                  key={`row-${rowIdx}`}
                  className={`grid gap-6`}
                  data-row-group={row[0]?.group || ''}
                  style={{
                    gridTemplateColumns: (() => {
                      const gid = row[0]?.group as string | undefined;
                      const fracs = gid ? rowColFractionsByGroup[gid] : undefined;
                      if (gid && fracs && fracs.length > 0) {
                        const s = fracs.reduce((a,b)=>a+b,0) || 1;
                        const norm = fracs.map(v=>Math.max(0.01, v / s));
                        const need = cols;
                        let arr = norm;
                        if (arr.length !== need) {
                          // Simple proportional resampler or pad/truncate to match columns
                          if (arr.length < need) {
                            const missing = need - arr.length;
                            arr = [...arr, ...Array(missing).fill(Math.max(0.01, 1/need))];
                            const sum = arr.reduce((a,b)=>a+b,0) || 1;
                            arr = arr.map(v=>v/sum);
                          } else if (arr.length > need) {
                            arr = arr.slice(0, need);
                            const sum = arr.reduce((a,b)=>a+b,0) || 1;
                            arr = arr.map(v=>v/sum);
                          }
                        }
                        return arr.map(f => `${(f * 100).toFixed(3)}%`).join(' ');
                      }
                      return `repeat(${cols}, minmax(0, 1fr))`;
                    })(),
                    paddingLeft: row[0]?.group ? BASE_LEFT_PAD + Math.max(0, rowLeftPadByGroup[row[0].group as string] || 0) : BASE_LEFT_PAD,
                    paddingRight: row[0]?.group ? BASE_RIGHT_PAD + Math.max(0, rowRightPadByGroup[row[0].group as string] || 0) : BASE_RIGHT_PAD
                  }}
                >
                  {row.map((b, colIdx) => {
                    const idx = model.blocks.findIndex(x => x.id === b.id);
                    const isLastInRow = row[row.length - 1]?.id === b.id;
                    return (
                      <div key={b.id} className="relative">
                        {b.type === 'image' ? (
                          <div className="relative group z-10" data-block-id={b.id} data-block-type="image" onMouseDown={() => { if (!editable) return; isEditingRef.current = false; setSelectedId(b.id); }}>
                            <div className={`overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm transition-colors group-hover:border-gray-300 group-hover:border-dashed${selectedId===b.id ? ' ring-2 ring-purple-500 ring-offset-2 ring-offset-white' : ''}`}
                              style={{ paddingBottom: Math.max(0, dragExtraById[b.id] || 0) }}>
                              <img src={b.src || ''} alt="" className="w-full h-auto max-h-[250px] object-contain" />
                            </div>
                            {selectedId===b.id && (
                            <div className={`absolute top-1/2 -translate-y-1/2 right-[-3.5rem] flex flex-col items-center gap-2`}>
                              <button
                                title="Delete image"
                                className={`inline-flex items-center justify-center h-7 w-7 rounded-full border border-red-200 bg-white text-red-600 shadow-sm`}
                                onMouseDown={(e)=>{ e.preventDefault(); e.stopPropagation(); setSelectedId(b.id); removeBlock(b.id); }}
                              >
                                <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2"/></svg>
                              </button>
                              <button
                                title="Add right"
                                className="inline-flex items-center justify-center h-7 w-7 rounded-full bg-white border shadow-sm text-purple-600"
                                onMouseDown={(e)=> e.preventDefault()}
                                onClick={()=> { setInsertIndex(idx+1); setInsertGroupTargetId(b.id); setInsertSide('right'); setShowAdd(true); }}
                              >
                                <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 5v14M5 12h14"/></svg>
                              </button>
                            </div>
                            )}
                            {editable && selectedId===b.id && (
                              <>
                                <button
                                  title="Add above"
                                  className="absolute left-1/2 -translate-x-1/2 -translate-y-1/2 top-0 transition inline-flex items-center justify-center h-7 w-7 rounded-full bg-white border shadow-sm text-purple-600 transform scale-50 hover:scale-100 opacity-70 hover:opacity-100"
                                  onMouseDown={(e)=> e.preventDefault()}
                                  onClick={()=> { setInsertIndex(idx); setInsertGroupTargetId(null); setInsertSide(null); setShowAdd(true); }}
                                >
                                  <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 5v14M5 12h14"/></svg>
                                </button>
                                <button title="Move up" className="absolute left-1/2 -translate-x-1/2 -translate-y-1/2 top-0 ml-8 z-50 inline-flex items-center justify-center h-7 w-7 rounded-full bg-white border shadow-sm text-gray-700 transform scale-50 hover:scale-100 opacity-70 hover:opacity-100" onMouseDown={(e)=>{e.preventDefault(); e.stopPropagation();}} onClick={()=> moveBlockUp(b.id)}>
                                  <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 19V5"/><path d="M5 12l7-7 7 7"/></svg>
                                </button>
                                <button
                                  title="Add below"
                                  className="absolute left-1/2 -translate-x-1/2 translate-y-1/2 bottom-0 transition inline-flex items-center justify-center h-7 w-7 rounded-full bg-white border shadow-sm text-purple-600 transform scale-50 hover:scale-100 opacity-70 hover:opacity-100"
                                  onMouseDown={(e)=> e.preventDefault()}
                                  onClick={()=> { setInsertIndex(idx+1); setInsertGroupTargetId(null); setInsertSide(null); setShowAdd(true); }}
                                >
                                  <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 5v14M5 12h14"/></svg>
                                </button>
                                <button title="Move down" className="absolute left-1/2 -translate-x-1/2 translate-y-1/2 bottom-0 ml-8 z-50 inline-flex items-center justify-center h-7 w-7 rounded-full bg-white border shadow-sm text-gray-700 transform scale-50 hover:scale-100 opacity-70 hover:opacity-100" onMouseDown={(e)=>{e.preventDefault(); e.stopPropagation();}} onClick={()=> moveBlockDown(b.id)}>
                                  <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 5v14"/><path d="M19 12l-7 7-7-7"/></svg>
                                </button>
                                {selectedId===b.id && (
                                <div className="absolute top-1/2 -translate-y-1/2 left-[-3.5rem] flex flex-col items-center gap-2">
                                  <button
                                    title="Add left"
                                    className="inline-flex items-center justify-center h-7 w-7 rounded-full bg-white border shadow-sm text-purple-600 transform scale-50 hover:scale-100 opacity-70 hover:opacity-100"
                                    onMouseDown={(e)=> e.preventDefault()}
                                    onClick={()=> { setInsertIndex(idx); setInsertGroupTargetId(b.id); setInsertSide('left'); setShowAdd(true); }}
                                  >
                                    <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 5v14M5 12h14"/></svg>
                                  </button>
                                  <button title="Move left" className="inline-flex items-center justify-center h-7 w-7 rounded-full bg-white border shadow-sm text-gray-700 transform scale-50 hover:scale-100 opacity-70 hover:opacity-100" onMouseDown={(e)=>{e.preventDefault(); e.stopPropagation();}} onClick={()=> moveBlockLeft(b.id)}>
                                    <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2"><path d="M19 12H5"/><path d="M12 5l-7 7 7 7"/></svg>
                                  </button>
                                </div>
                                )}
                                <div className={`absolute top-1/2 -translate-y-1/2 right-[-3.5rem] flex flex-col items-center gap-2`}>
                                  <button
                                    title="Move right"
                                    className="inline-flex items-center justify-center h-7 w-7 rounded-full bg-white border shadow-sm text-gray-700 transform scale-50 hover:scale-100 opacity-70 hover:opacity-100"
                                    onMouseDown={(e)=> { e.preventDefault(); e.stopPropagation(); }}
                                    onClick={()=> moveBlockRight(b.id)}
                                  >
                                    <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2"><path d="M5 12h14"/><path d="M12 19l7-7-7-7"/></svg>
                                  </button>
                                </div>
                              </>
                            )}
                          </div>
                        ) : b.type === 'timeline' ? (
                          <div className="relative group z-10" data-block-id={b.id} onMouseDown={() => { isEditingRef.current = false; setSelectedId(b.id); }}>
                            <div className={`relative rounded-xl border border-gray-200 bg-white p-4 shadow-sm transition-colors group-hover:border-gray-300 group-hover:border-dashed${selectedId===b.id ? ' ring-2 ring-purple-500 ring-offset-2 ring-offset-white' : ''}`}>
                              {editable && (
                                <div className="absolute top-2 right-2 z-50" data-editor-action>
                                  <button
                                    title="Block settings"
                                    className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-gray-200 bg-white text-gray-700 hover:bg-gray-50 shadow-sm z-50"
                                    onMouseDown={(e)=> { e.preventDefault(); e.stopPropagation(); }}
                                    onClick={()=> { setSelectedId(b.id); setBlockSettings({ id: b.id, type: 'timeline' }); }}
                                  >
                                    <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09A1.65 1.65 0 0 0 8 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 3.6 15a1.65 1.65 0 0 0-1.51-1H2a2 2 0 1 1 0-4h.09A1.65 1.65 0 0 0 3.6 8a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 8 3.6a1.65 1.65 0 0 0 1-1.51V2a2 2 0 1 1 4 0v.09A1.65 1.65 0 0 0 15 3.6a1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 20.4 8c0 .57.22 1.12.6 1.51.39.39.91.6 1.51.6H23a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1Z"/></svg>
                                  </button>
                                </div>
                              )}
                              <Timeline
                                key={`${b.id}:${JSON.stringify({v:(b as any).timelineVariant,s:(b as any).timelineScrollable,e:(b as any).timelineExpandable,fx:(b as any).timelineEffects,n:(b.items||[]).length})}`}
                                items={b.items || []}
                                editable={false}
                                onChange={(items)=> updateBlock(b.id, { items } as any)}
                                variant={(b as any).timelineVariant || 'classic'}
                                scrollable={(b as any).timelineScrollable || false}
                                expandable={(b as any).timelineExpandable || false}
                                effects={(b as any).timelineEffects}
                              />
                              {editable && selectedId===b.id && (
                                <>
                                  {/* Add above */}
                                  <button
                                    title="Add above"
                                    className="absolute left-1/2 -translate-x-1/2 -translate-y-1/2 top-0 transition inline-flex items-center justify-center h-7 w-7 rounded-full bg-white border shadow-sm text-purple-600 transform scale-50 hover:scale-100 opacity-70 hover:opacity-100"
                                    onMouseDown={(e)=> e.preventDefault()}
                                    onClick={()=> { setInsertIndex(idx); setInsertGroupTargetId(null); setInsertSide(null); setShowAdd(true); }}
                                  >
                                    <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 5v14M5 12h14"/></svg>
                                  </button>
                                  {/* Move up/down */}
                                  <button title="Move up" className="absolute left-1/2 -translate-x-1/2 -translate-y-1/2 top-0 ml-8 z-50 inline-flex items-center justify-center h-7 w-7 rounded-full bg-white border shadow-sm text-gray-700 transform scale-50 hover:scale-100 opacity-70 hover:opacity-100" onMouseDown={(e)=>{e.preventDefault(); e.stopPropagation();}} onClick={()=> moveBlockUp(b.id)}>
                                    <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 19V5"/><path d="M5 12l7-7 7 7"/></svg>
                                  </button>
                                  <button title="Move down" className="absolute left-1/2 -translate-x-1/2 translate-y-1/2 bottom-0 ml-8 z-50 inline-flex items-center justify-center h-7 w-7 rounded-full bg-white border shadow-sm text-gray-700 transform scale-50 hover:scale-100 opacity-70 hover:opacity-100" onMouseDown={(e)=>{e.preventDefault(); e.stopPropagation();}} onClick={()=> moveBlockDown(b.id)}>
                                    <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 5v14"/><path d="M19 12l-7 7-7-7"/></svg>
                                  </button>
                                  {/* Add below */}
                                  <button
                                    title="Add below"
                                    className="absolute left-1/2 -translate-x-1/2 translate-y-1/2 bottom-0 transition inline-flex items-center justify-center h-7 w-7 rounded-full bg-white border shadow-sm text-purple-600 transform scale-50 hover:scale-100 opacity-70 hover:opacity-100"
                                    onMouseDown={(e)=> e.preventDefault()}
                                    onClick={()=> { setInsertIndex(idx+1); setInsertGroupTargetId(null); setInsertSide(null); setShowAdd(true); }}
                                  >
                                    <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 5v14M5 12h14"/></svg>
                                  </button>
                                  {/* Left add + move */}
                                  <div className="absolute top-1/2 -translate-y-1/2 left-0 -translate-x-full z-50 flex flex-col items-center gap-2">
                                    <button
                                      title="Add left"
                                      className="inline-flex items-center justify-center h-7 w-7 rounded-full bg-white border shadow-sm text-purple-600 transform scale-50 hover:scale-100 opacity-70 hover:opacity-100"
                                      onMouseDown={(e)=> e.preventDefault()}
                                      onClick={()=> { setInsertIndex(idx); setInsertGroupTargetId(b.id); setInsertSide('left'); setShowAdd(true); }}
                                    >
                                      <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 5v14M5 12h14"/></svg>
                                    </button>
                                    <button title="Move left" className="inline-flex items-center justify-center h-7 w-7 rounded-full bg-white border shadow-sm text-gray-700 transform scale-50 hover:scale-100 opacity-70 hover:opacity-100" onMouseDown={(e)=>{e.preventDefault(); e.stopPropagation();}} onClick={()=> moveBlockLeft(b.id)}>
                                      <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2"><path d="M19 12H5"/><path d="M12 5l-7 7 7 7"/></svg>
                                    </button>
                                  </div>
                                  {/* Right delete + add + move right */}
                                  <div className="absolute top-1/2 -translate-y-1/2 right-[-3.5rem] flex flex-col items-center gap-2">
                                    <button
                                      title="Delete block"
                                      className={`inline-flex items-center justify-center h-7 w-7 rounded-full border border-red-200 bg-white text-red-600 shadow-sm`}
                                      onMouseDown={(e)=>{ e.preventDefault(); e.stopPropagation(); setSelectedId(b.id); removeBlock(b.id); }}
                                    >
                                      <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2"/></svg>
                                    </button>
                                    <button
                                      title="Add right"
                                      className="inline-flex items-center justify-center h-7 w-7 rounded-full bg-white border shadow-sm text-purple-600"
                                      onMouseDown={(e)=> e.preventDefault()}
                                      onClick={()=> { setInsertIndex(idx+1); setInsertGroupTargetId(b.id); setInsertSide('right'); setShowAdd(true); }}
                                    >
                                      <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 5v14M5 12h14"/></svg>
                                    </button>
                                    <button title="Move right" className="inline-flex items-center justify-center h-7 w-7 rounded-full bg-white border shadow-sm text-gray-700 transform scale-50 hover:scale-100 opacity-70 hover:opacity-100" onMouseDown={(e)=>{e.preventDefault(); e.stopPropagation();}} onClick={()=> moveBlockRight(b.id)}>
                                      <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2"><path d="M5 12h14"/><path d="M12 19l7-7-7-7"/></svg>
                                    </button>
                                  </div>
                                </>
                              )}
                            </div>
                          </div>
                        ) : b.type === 'carousel' ? (
                          <div className="relative group z-10" data-block-id={b.id} onMouseDown={() => { isEditingRef.current = false; setSelectedId(b.id); }}>
                            <div className={`relative rounded-xl border border-gray-200 bg-white p-4 shadow-sm transition-colors group-hover:border-gray-300 group-hover:border-dashed${selectedId===b.id ? ' ring-2 ring-purple-500 ring-offset-2 ring-offset-white' : ''}`}>
                              {editable && (
                                <div className="absolute top-2 right-2 z-50" data-editor-action>
                                  <button
                                    title="Block settings"
                                    className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-gray-200 bg-white text-gray-700 hover:bg-gray-50 shadow-sm z-50"
                                    onMouseDown={(e)=> { e.preventDefault(); e.stopPropagation(); }}
                                    onClick={()=> { setSelectedId(b.id); setBlockSettings({ id: b.id, type: 'carousel' }); }}
                                  >
                                    <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09A1.65 1.65 0 0 0 8 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 3.6 15a1.65 1.65 0 0 0-1.51-1H2a2 2 0 1 1 0-4h.09A1.65 1.65 0 0 0 3.6 8a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 8 3.6a1.65 1.65 0 0 0 1-1.51V2a2 2 0 1 1 4 0v.09A1.65 1.65 0 0 0 15 3.6a1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 20.4 8c0 .57.22 1.12.6 1.51.39.39.91.6 1.51.6H23a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1Z"/></svg>
                                  </button>
                                </div>
                              )}
                              <Carousel
                                slides={(b as any).slides || []}
                                editable={false}
                                onChange={(slides)=> updateBlock(b.id, { slides } as any)}
                                showDots={(b as any).carouselShowDots ?? true}
                                showArrows={(b as any).carouselShowArrows ?? true}
                              />
                              {editable && selectedId===b.id && (
                                <>
                                  {/* Add above */}
                                  <button
                                    title="Add above"
                                    className="absolute left-1/2 -translate-x-1/2 -translate-y-1/2 top-0 transition inline-flex items-center justify-center h-7 w-7 rounded-full bg-white border shadow-sm text-purple-600 transform scale-50 hover:scale-100 opacity-70 hover:opacity-100"
                                    onMouseDown={(e)=> e.preventDefault()}
                                    onClick={()=> { setInsertIndex(idx); setInsertGroupTargetId(null); setInsertSide(null); setShowAdd(true); }}
                                  >
                                    <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 5v14M5 12h14"/></svg>
                                  </button>
                                  {/* Move up/down */}
                                  <button title="Move up" className="absolute left-1/2 -translate-x-1/2 -translate-y-1/2 top-0 ml-8 z-50 inline-flex items-center justify-center h-7 w-7 rounded-full bg-white border shadow-sm text-gray-700 transform scale-50 hover:scale-100 opacity-70 hover:opacity-100" onMouseDown={(e)=>{e.preventDefault(); e.stopPropagation();}} onClick={()=> moveBlockUp(b.id)}>
                                    <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 19V5"/><path d="M5 12l7-7 7 7"/></svg>
                                  </button>
                                  <button title="Move down" className="absolute left-1/2 -translate-x-1/2 translate-y-1/2 bottom-0 ml-8 z-50 inline-flex items-center justify-center h-7 w-7 rounded-full bg-white border shadow-sm text-gray-700 transform scale-50 hover:scale-100 opacity-70 hover:opacity-100" onMouseDown={(e)=>{e.preventDefault(); e.stopPropagation();}} onClick={()=> moveBlockDown(b.id)}>
                                    <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 5v14"/><path d="M19 12l-7 7-7-7"/></svg>
                                  </button>
                                  {/* Add below */}
                                  <button
                                    title="Add below"
                                    className="absolute left-1/2 -translate-x-1/2 translate-y-1/2 bottom-0 transition inline-flex items-center justify-center h-7 w-7 rounded-full bg-white border shadow-sm text-purple-600 transform scale-50 hover:scale-100 opacity-70 hover:opacity-100"
                                    onMouseDown={(e)=> e.preventDefault()}
                                    onClick={()=> { setInsertIndex(idx+1); setInsertGroupTargetId(null); setInsertSide(null); setShowAdd(true); }}
                                  >
                                    <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 5v14M5 12h14"/></svg>
                                  </button>
                                  <div className={`absolute top-1/2 -translate-y-1/2 right-[-3.5rem] flex flex-col items-center gap-2`}>
                                    <button
                                      title="Delete block"
                                      className={`inline-flex items-center justify-center h-7 w-7 rounded-full border border-red-200 bg-white text-red-600 shadow-sm`}
                                      onMouseDown={(e)=>{ e.preventDefault(); e.stopPropagation(); setSelectedId(b.id); removeBlock(b.id); }}
                                    >
                                      <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/></svg>
                                    </button>
                                  </div>
                                </>
                              )}
                            </div>
                          </div>
                        ) : b.type === 'faq' ? (
                          <div className="relative group z-10" data-block-id={b.id} onMouseDown={() => { isEditingRef.current = false; setSelectedId(b.id); }}>
                            <div className={`relative rounded-xl border border-gray-200 bg-white p-4 shadow-sm transition-colors group-hover:border-gray-300 group-hover:border-dashed${selectedId===b.id ? ' ring-2 ring-purple-500 ring-offset-2 ring-offset-white' : ''}`}>
                              {editable && (
                                <div className="absolute top-2 right-2 z-50" data-editor-action>
                                  <button
                                    title="Block settings"
                                    className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-gray-200 bg-white text-gray-700 hover:bg-gray-50 shadow-sm z-50"
                                    onMouseDown={(e)=> { e.preventDefault(); e.stopPropagation(); }}
                                    onClick={()=> { setSelectedId(b.id); setBlockSettings({ id: b.id, type: 'faq' }); }}
                                  >
                                    <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09A1.65 1.65 0 0 0 8 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 3.6 15a1.65 1.65 0 0 0-1.51-1H2a2 2 0 1 1 0-4h.09A1.65 1.65 0 0 0 3.6 8a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 8 3.6a1.65 1.65 0 0 0 1-1.51V2a2 2 0 1 1 4 0v.09A1.65 1.65 0 0 0 15 3.6a1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 20.4 8c0 .57.22 1.12.6 1.51.39.39.91.6 1.51.6H23a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1Z"/></svg>
                                  </button>
                                </div>
                              )}
                              <FAQ items={(b as any).faqItems || []} palette={(b as any).faqPalette} style={(b as any).faqStyle} showIcons={(b as any).faqShowIcons} icon={(b as any).faqIcon} />
                              {editable && selectedId===b.id && (
                                <>
                                  {/* Add above */}
                                  <button
                                    title="Add above"
                                    className="absolute left-1/2 -translate-x-1/2 -translate-y-1/2 top-0 transition inline-flex items-center justify-center h-7 w-7 rounded-full bg-white border shadow-sm text-purple-600 transform scale-50 hover:scale-100 opacity-70 hover:opacity-100"
                                    onMouseDown={(e)=> e.preventDefault()}
                                    onClick={()=> { setInsertIndex(idx); setInsertGroupTargetId(null); setInsertSide(null); setShowAdd(true); }}
                                  >
                                    <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 5v14M5 12h14"/></svg>
                                  </button>
                                  {/* Move up/down */}
                                  <button title="Move up" className="absolute left-1/2 -translate-x-1/2 -translate-y-1/2 top-0 ml-8 z-50 inline-flex items-center justify-center h-7 w-7 rounded-full bg-white border shadow-sm text-gray-700 transform scale-50 hover:scale-100 opacity-70 hover:opacity-100" onMouseDown={(e)=>{e.preventDefault(); e.stopPropagation();}} onClick={()=> moveBlockUp(b.id)}>
                                    <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 19V5"/><path d="M5 12l7-7 7 7"/></svg>
                                  </button>
                                  {/* Add below */}
                                  <button
                                    title="Add below"
                                    className="absolute left-1/2 -translate-x-1/2 translate-y-1/2 bottom-0 transition inline-flex items-center justify-center h-7 w-7 rounded-full bg-white border shadow-sm text-purple-600 transform scale-50 hover:scale-100 opacity-70 hover:opacity-100"
                                    onMouseDown={(e)=> e.preventDefault()}
                                    onClick={()=> { setInsertIndex(idx+1); setInsertGroupTargetId(null); setInsertSide(null); setShowAdd(true); }}
                                  >
                                    <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 5v14M5 12h14"/></svg>
                                  </button>
                                  <div className={`absolute top-1/2 -translate-y-1/2 right-[-3.5rem] flex flex-col items-center gap-2`}>
                                    <button
                                      title="Delete block"
                                      className={`inline-flex items-center justify-center h-7 w-7 rounded-full border border-red-200 bg-white text-red-600 shadow-sm`}
                                      onMouseDown={(e)=>{ e.preventDefault(); e.stopPropagation(); setSelectedId(b.id); removeBlock(b.id); }}
                                    >
                                      <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/></svg>
                                    </button>
                                  </div>
                                </>
                              )}
                            </div>
                          </div>
                        ) : b.type === 'table' ? (
                          <div className="relative group z-10" data-block-id={b.id} data-block-type="table" onMouseDown={() => { if (!editable) return; isEditingRef.current = false; setSelectedId(b.id); }}>
                            <div className={`relative rounded-xl${(b as any).hoverOutline !== false ? ' border border-gray-200' : ''} bg-white p-3${(b as any).hoverOutline !== false ? ' shadow-sm' : ''} transition-colors ${(b as any).hoverOutline !== false ? 'group-hover:border-gray-300 group-hover:border-dashed' : ''}${selectedId===b.id ? ' ring-2 ring-purple-500 ring-offset-2 ring-offset-white' : ''}`
                            } style={{ paddingBottom: Math.max(0, dragExtraById[b.id] || 0) }}>
                              {editable && (
                                <div className="absolute top-2 right-2 z-50" data-editor-action>
                                  <button
                                    title="Block settings"
                                    className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-gray-200 bg-white text-gray-700 hover:bg-gray-50 shadow-sm z-50"
                                    onMouseDown={(e)=> { e.preventDefault(); e.stopPropagation(); }}
                                    onClick={()=> { setSelectedId(b.id); setBlockSettings({ id: b.id, type: 'table' }); }}
                                  >
                                    <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09A1.65 1.65 0 0 0 8 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 3.6 15a1.65 1.65 0 0 0-1.51-1H2a2 2 0 1 1 0-4h.09A1.65 1.65 0 0 0 3.6 8a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 8 3.6a1.65 1.65 0 0 0 1-1.51V2a2 2 0 1 1 4 0v.09A1.65 1.65 0 0 0 15 3.6a1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 20.4 8c0 .57.22 1.12.6 1.51.39.39.91.6 1.51.6H23a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1Z"/></svg>
                                  </button>
                                </div>
                              )}
                              <Table
                                rows={(b as any).rows || 0}
                                cols={(b as any).cols || 0}
                                data={(b as any).data || []}
                                colWidths={(b as any).colWidths || []}
                                rowHeights={(b as any).rowHeights || []}
                                dense={(b as any).dense || false}
                                bordered={(b as any).bordered ?? true}
                                fitCols={(b as any).fitCols || false}
                                fitRows={(b as any).fitRows || false}
                                scrollable={(b as any).scrollable || false}
                                hoverOutline={(b as any).hoverOutline !== false}
                                editable={editable && selectedId===b.id}
                                showResizeHandles={editable && selectedId===b.id && !((b as any).fitCols || (b as any).fitRows)}
                                onChange={(next)=> updateBlock(b.id, { rows: next.rows, cols: next.cols, data: next.data, colWidths: next.colWidths, rowHeights: next.rowHeights, dense: next.dense, bordered: next.bordered, fitCols: (next as any).fitCols, fitRows: (next as any).fitRows, scrollable: (next as any).scrollable, hoverOutline: (next as any).hoverOutline } as any)}
                              />
                              {editable && selectedId===b.id && (
                                <>
                                  {/* Add above */}
                                  <button
                                    title="Add above"
                                    className="absolute left-1/2 -translate-x-1/2 -translate-y-1/2 top-0 transition inline-flex items-center justify-center h-7 w-7 rounded-full bg-white border shadow-sm text-purple-600 transform scale-50 hover:scale-100 opacity-70 hover:opacity-100"
                                    onMouseDown={(e)=> e.preventDefault()}
                                    onClick={()=> { setInsertIndex(idx); setInsertGroupTargetId(null); setInsertSide(null); setShowAdd(true); }}
                                  >
                                    <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 5v14M5 12h14"/></svg>
                                  </button>
                                  {/* Move up/down */}
                                  <button title="Move up" className="absolute left-1/2 -translate-x-1/2 -translate-y-1/2 top-0 ml-8 z-50 inline-flex items-center justify-center h-7 w-7 rounded-full bg-white border shadow-sm text-gray-700 transform scale-50 hover:scale-100 opacity-70 hover:opacity-100" onMouseDown={(e)=>{e.preventDefault(); e.stopPropagation();}} onClick={()=> moveBlockUp(b.id)}>
                                    <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 19V5"/><path d="M5 12l7-7 7 7"/></svg>
                                  </button>
                                  <button title="Move down" className="absolute left-1/2 -translate-x-1/2 translate-y-1/2 bottom-0 ml-8 z-50 inline-flex items-center justify-center h-7 w-7 rounded-full bg-white border shadow-sm text-gray-700 transform scale-50 hover:scale-100 opacity-70 hover:opacity-100" onMouseDown={(e)=>{e.preventDefault(); e.stopPropagation();}} onClick={()=> moveBlockDown(b.id)}>
                                    <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 5v14"/><path d="M19 12l-7 7-7-7"/></svg>
                                  </button>
                                  {/* Add below */}
                                  <button
                                    title="Add below"
                                    className="absolute left-1/2 -translate-x-1/2 translate-y-1/2 bottom-0 transition inline-flex items-center justify-center h-7 w-7 rounded-full bg-white border shadow-sm text-purple-600 transform scale-50 hover:scale-100 opacity-70 hover:opacity-100"
                                    onMouseDown={(e)=> e.preventDefault()}
                                    onClick={()=> { setInsertIndex(idx+1); setInsertGroupTargetId(null); setInsertSide(null); setShowAdd(true); }}
                                  >
                                    <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 5v14M5 12h14"/></svg>
                                  </button>
                                  {/* Left add + move */}
                                  <div className="absolute top-1/2 -translate-y-1/2 left-0 -translate-x-full z-50 flex flex-col items-center gap-2">
                                    <button
                                      title="Add left"
                                      className="inline-flex items-center justify-center h-7 w-7 rounded-full bg-white border shadow-sm text-purple-600 transform scale-50 hover:scale-100 opacity-70 hover:opacity-100"
                                      onMouseDown={(e)=> e.preventDefault()}
                                      onClick={()=> { setInsertIndex(idx); setInsertGroupTargetId(b.id); setInsertSide('left'); setShowAdd(true); }}
                                    >
                                      <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 5v14M5 12h14"/></svg>
                                    </button>
                                    <button title="Move left" className="inline-flex items-center justify-center h-7 w-7 rounded-full bg-white border shadow-sm text-gray-700 transform scale-50 hover:scale-100 opacity-70 hover:opacity-100" onMouseDown={(e)=>{e.preventDefault(); e.stopPropagation();}} onClick={()=> moveBlockLeft(b.id)}>
                                      <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2"><path d="M19 12H5"/><path d="M12 5l-7 7 7 7"/></svg>
                                    </button>
                                  </div>
                                  {/* Right delete + add + move right */}
                                  <div className="absolute top-1/2 -translate-y-1/2 right-[-3.5rem] flex flex-col items-center gap-2">
                                    <button
                                      title="Delete block"
                                      className={`inline-flex items-center justify-center h-7 w-7 rounded-full border border-red-200 bg-white text-red-600 shadow-sm`}
                                      onMouseDown={(e)=>{ e.preventDefault(); e.stopPropagation(); setSelectedId(b.id); removeBlock(b.id); }}
                                    >
                                      <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>
                                    </button>
                                    <button
                                      title="Add right"
                                      className="inline-flex items-center justify-center h-7 w-7 rounded-full bg-white border shadow-sm text-purple-600"
                                      onMouseDown={(e)=> e.preventDefault()}
                                      onClick={()=> { setInsertIndex(idx+1); setInsertGroupTargetId(b.id); setInsertSide('right'); setShowAdd(true); }}
                                    >
                                      <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 5v14M5 12h14"/></svg>
                                    </button>
                                    <button title="Move right" className="inline-flex items-center justify-center h-7 w-7 rounded-full bg-white border shadow-sm text-gray-700 transform scale-50 hover:scale-100 opacity-70 hover:opacity-100" onMouseDown={(e)=>{e.preventDefault(); e.stopPropagation();}} onClick={()=> moveBlockRight(b.id)}>
                                      <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2"><path d="M5 12h14"/><path d="M12 19l7-7-7-7"/></svg>
                                    </button>
                                  </div>
                                </>
                              )}
                            </div>
                          </div>
                        ) : b.type === 'embed' ? (
                          <div className={`relative group ${selectedId===b.id ? 'z-20' : 'z-10'}`} data-block-id={b.id} data-block-type="embed" onMouseDown={() => { if (!editable) return; isEditingRef.current = false; setSelectedId(b.id); }}>
                            <div className={`relative rounded-xl border border-gray-200 bg-white p-3 shadow-sm transition-colors group-hover:border-gray-300 group-hover:border-dashed${selectedId===b.id ? ' ring-2 ring-purple-500 ring-offset-2 ring-offset-white' : ''}`}
                              style={{ paddingBottom: Math.max(0, dragExtraById[b.id] || 0) }}>
                              {editable && (
                                <div className="absolute top-2 right-2 z-50" data-editor-action>
                                  <button
                                    title={embedPlaceMode===b.id ? 'Confirm position' : 'Position in blog'}
                                    className={`inline-flex h-8 w-8 items-center justify-center rounded-md border border-gray-200 bg-white ${embedPlaceMode===b.id ? 'text-green-600' : 'text-gray-700'} hover:bg-gray-50 shadow-sm mr-2 ${selectedId===b.id ? '' : 'opacity-50 grayscale pointer-events-none cursor-default text-gray-400'}`}
                                    onMouseDown={(e)=> { e.preventDefault(); e.stopPropagation(); }}
                                    ref={(el)=> { if (embedPlaceMode===b.id) quickPortalAnchorRef.current = (el as unknown as HTMLElement) || null; }}
                                    onClick={(e)=> {
                                      if (selectedId!==b.id) return;
                                      setSelectedId(b.id);
                                      if (embedPlaceMode===b.id) {
                                        setEmbedPlaceMode(null);
                                        setEmbedPlaceTargetIndex(null);
                                        quickPortalAnchorRef.current = null;
                                        setQuickPortalPos(null);
                                      } else {
                                        setEmbedPlaceMode(b.id);
                                        setEmbedPlaceTargetIndex(model.blocks.findIndex(x=>x.id===b.id));
                                        quickPortalAnchorRef.current = e.currentTarget as unknown as HTMLElement;
                                        requestAnimationFrame(() => updateQuickPortalFromEl(quickPortalAnchorRef.current));
                                      }
                                    }}
                                  >
                                    <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                      <path d="M12 2v5"/>
                                      <path d="M12 17v5"/>
                                      <path d="M2 12h5"/>
                                      <path d="M17 12h5"/>
                                      <path d="M12 2l-2 2 2-2 2 2-2-2z" opacity="0"/>
                                      <path d="M8 6l4-4 4 4"/>
                                      <path d="M8 18l4 4 4-4"/>
                                      <path d="M6 8l-4 4 4 4"/>
                                      <path d="M18 8l4 4-4 4"/>
                                    </svg>
                                  </button>
                                  {embedPlaceMode===b.id && quickPortalPos && createPortal(
                                    <div
                                      style={{ position: 'fixed', left: quickPortalPos.left, top: quickPortalPos.top, zIndex: 1000 }}
                                      className="w-64 rounded-md border border-gray-200 bg-white p-3 shadow-sm"
                                      data-quick-portal
                                      onMouseDown={(e)=>{ e.stopPropagation(); }}
                                    >
                                      <div className="text-xs font-medium text-gray-700 mb-2">Quick adjust</div>
                                      <label className="block text-[11px] text-gray-600 mb-1">Horizontal (px)</label>
                                      <input
                                        type="range"
                                        min={-300}
                                        max={300}
                                        step={1}
                                        value={(quickOffsetById[b.id]?.x ?? (b as any).embedOffsetX ?? 0)}
                                        onChange={(e)=> {
                                          const n = parseInt(e.target.value, 10) || 0;
                                          setQuickOffset(b.id, { x: n });
                                          if (blockSettings && blockSettings.type==='embed' && blockSettings.id===b.id) {
                                            setEmbedPreview(prev => (prev && prev.id===b.id ? { ...prev, offsetX: n } : prev));
                                          }
                                        }}
                                        onMouseUp={(e)=>{ const n = parseInt((e.target as HTMLInputElement).value,10)||0; updateBlock(b.id,{ embedOffsetX: n } as any); setQuickOffsetById(prev=> ({...prev, [b.id]: { ...(prev[b.id]||{}), x: undefined }})); }}
                                        onTouchEnd={(e)=>{ const el = e.currentTarget as HTMLInputElement; const n = parseInt(el.value,10)||0; updateBlock(b.id,{ embedOffsetX: n } as any); setQuickOffsetById(prev=> ({...prev, [b.id]: { ...(prev[b.id]||{}), x: undefined }})); }}
                                        className="w-full accent-purple-600"
                                      />
                                      <div className="flex justify-between text-[11px] text-gray-500 mb-2"><span>-300</span><span>0</span><span>+300</span></div>
                                      <label className="block text-[11px] text-gray-600 mb-1">Vertical (px)</label>
                                      <input
                                        type="range"
                                        min={-300}
                                        max={300}
                                        step={1}
                                        value={(quickOffsetById[b.id]?.y ?? (b as any).embedOffsetY ?? 0)}
                                        onChange={(e)=> {
                                          const n = parseInt(e.target.value, 10) || 0;
                                          setQuickOffset(b.id, { y: n });
                                          if (blockSettings && blockSettings.type==='embed' && blockSettings.id===b.id) {
                                            setEmbedPreview(prev => (prev && prev.id===b.id ? { ...prev, offsetY: n } : prev));
                                          }
                                        }}
                                        onMouseUp={(e)=>{ const n = parseInt((e.target as HTMLInputElement).value,10)||0; updateBlock(b.id,{ embedOffsetY: n } as any); setQuickOffsetById(prev=> ({...prev, [b.id]: { ...(prev[b.id]||{}), y: undefined }})); }}
                                        onTouchEnd={(e)=>{ const el = e.currentTarget as HTMLInputElement; const n = parseInt(el.value,10)||0; updateBlock(b.id,{ embedOffsetY: n } as any); setQuickOffsetById(prev=> ({...prev, [b.id]: { ...(prev[b.id]||{}), y: undefined }})); }}
                                        className="w-full accent-purple-600"
                                      />
                                      <div className="flex justify-between text-[11px] text-gray-500"><span>-300</span><span>0</span><span>+300</span></div>
                                      <div className="mt-2 text-[11px] text-gray-500">
                                        Hold <span className="font-medium">Shift</span> to move vertical only. Hold <span className="font-medium">Ctrl</span> to move horizontal only.
                                      </div>
                                    </div>,
                                    document.body
                                  )}
                                  <button
                                    title="Block settings"
                                    className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-gray-200 bg-white text-gray-700 hover:bg-gray-50 shadow-sm z-50"
                                    onMouseDown={(e)=> { e.preventDefault(); e.stopPropagation(); }}
                                    onClick={()=> { setSelectedId(b.id); setBlockSettings({ id: b.id, type: 'embed' }); }}
                                  >
                                    <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09A1.65 1.65 0 0 0 8 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 3.6 15a1.65 1.65 0 0 0-1.51-1H2a2 2 0 1 1 0-4h.09A1.65 1.65 0 0 0 3.6 8a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 8 3.6a1.65 1.65 0 0 0 1-1.51V2a2 2 0 1 1 4 0v.09A1.65 1.65 0 0 0 15 3.6a1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 20.4 8c0 .57.22 1.12.6 1.51.39.39.91.6 1.51.6H23a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1Z"/></svg>
                                  </button>
                                </div>
                              )}
                              {embedPlaceMode===b.id && (
                                <div
                                  className="absolute inset-0 z-40 cursor-move"
                                  data-embed-drag-overlay
                                  onMouseDown={(e)=>{
                                    if (!editable) return;
                                    const startX = e.clientX;
                                    const startY = e.clientY;
                                    const curOffX = (quickOffsetById[b.id]?.x ?? (b as any).embedOffsetX ?? 0) as number;
                                    const curOffY = (quickOffsetById[b.id]?.y ?? (b as any).embedOffsetY ?? 0) as number;
                                    embedDragActiveRef.current = true;
                                    embedDragStartRef.current = { id: b.id, x: startX, y: startY, offX: curOffX, offY: curOffY };
                                    e.preventDefault();
                                    e.stopPropagation();
                                  }}
                                />
                              )}
                              <Embed
                                html={(b as any).embedHtml}
                                heightMode={(b as any).embedHeightMode || 'auto'}
                                fixedHeight={(b as any).embedFixedHeight || 360}
                                bordered={(b as any).embedBordered ?? true}
                                radius={(b as any).embedRadius ?? 12}
                                shadow={(b as any).embedShadow ?? false}
                                allowScripts={(b as any).embedAllowScripts ?? true}
                                allowSameOrigin={(b as any).embedAllowSameOrigin ?? true}
                                allowPopups={(b as any).embedAllowPopups ?? true}
                                allowScroll={(b as any).embedAllowScroll ?? false}
                                removeBg={(b as any).embedRemoveBg ?? false}
                                responsiveFit={(b as any).embedResponsiveFit ?? false}
                                offsetX={(quickOffsetById[b.id]?.x ?? (b as any).embedOffsetX) ?? 0}
                                offsetY={(quickOffsetById[b.id]?.y ?? (b as any).embedOffsetY) ?? 0}
                                interactive={!editable}
                                onApplyInsert={({ ratio, zoom }) => {
                                  try {
                                    const cur = modelRef.current;
                                    const idxHere = cur.blocks.findIndex(x => x.id === b.id);
                                    if (idxHere === -1) return;
                                    const clone = { id: newId(), type: 'embeddims', embedHtml: (b as any).embedHtml, embedDimRatio: Number(ratio) || (16/9), embedDimZoom: Number(zoom) || 1 } as PostBlock;
                                    const nextBlocks = [
                                      ...cur.blocks.slice(0, idxHere + 1),
                                      clone,
                                      ...cur.blocks.slice(idxHere + 1),
                                    ];
                                    const next = { ...cur, blocks: nextBlocks };
                                    modelRef.current = next;
                                    setModel(next);
                                    onChange && onChange(next);
                                    setSelectedId(clone.id);
                                  } catch {}
                                }}
                              />
                              {editable && selectedId===b.id && (
                                <>
                                  {/* Add above */}
                                  <button
                                    title="Add above"
                                    className="absolute left-1/2 -translate-x-1/2 -translate-y-1/2 top-0 transition inline-flex items-center justify-center h-7 w-7 rounded-full bg-white border shadow-sm text-purple-600 transform scale-50 hover:scale-100 opacity-70 hover:opacity-100"
                                    onMouseDown={(e)=> e.preventDefault()}
                                    onClick={()=> { setInsertIndex(idx); setInsertGroupTargetId(null); setInsertSide(null); setShowAdd(true); }}
                                  >
                                    <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 5v14M5 12h14"/></svg>
                                  </button>
                                  {/* Move up/down */}
                                  <button title="Move up" className="absolute left-1/2 -translate-x-1/2 -translate-y-1/2 top-0 ml-8 z-50 inline-flex items-center justify-center h-7 w-7 rounded-full bg-white border shadow-sm text-gray-700 transform scale-50 hover:scale-100 opacity-70 hover:opacity-100" onMouseDown={(e)=>{e.preventDefault(); e.stopPropagation();}} onClick={()=> moveBlockUp(b.id)}>
                                    <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 19V5"/><path d="M5 12l7-7 7 7"/></svg>
                                  </button>
                                  <button title="Move down" className="absolute left-1/2 -translate-x-1/2 translate-y-1/2 bottom-0 ml-8 z-50 inline-flex items-center justify-center h-7 w-7 rounded-full bg-white border shadow-sm text-gray-700 transform scale-50 hover:scale-100 opacity-70 hover:opacity-100" onMouseDown={(e)=>{e.preventDefault(); e.stopPropagation();}} onClick={()=> moveBlockDown(b.id)}>
                                    <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 5v14"/><path d="M19 12l-7 7-7-7"/></svg>
                                  </button>
                                  {/* Add below */}
                                  <button
                                    title="Add below"
                                    className="absolute left-1/2 -translate-x-1/2 translate-y-1/2 bottom-0 transition inline-flex items-center justify-center h-7 w-7 rounded-full bg-white border shadow-sm text-purple-600 transform scale-50 hover:scale-100 opacity-70 hover:opacity-100"
                                    onMouseDown={(e)=> e.preventDefault()}
                                    onClick={()=> { setInsertIndex(idx+1); setInsertGroupTargetId(null); setInsertSide(null); setShowAdd(true); }}
                                  >
                                    <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 5v14M5 12h14"/></svg>
                                  </button>
                                  {/* Left add + move */}
                                  <div className="absolute top-1/2 -translate-y-1/2 left-0 -translate-x-full z-50 flex flex-col items-center gap-2">
                                    <button
                                      title="Add left"
                                      className="inline-flex items-center justify-center h-7 w-7 rounded-full bg-white border shadow-sm text-purple-600 transform scale-50 hover:scale-100 opacity-70 hover:opacity-100"
                                      onMouseDown={(e)=> e.preventDefault()}
                                      onClick={()=> { setInsertIndex(idx); setInsertGroupTargetId(b.id); setInsertSide('left'); setShowAdd(true); }}
                                    >
                                      <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 5v14M5 12h14"/></svg>
                                    </button>
                                    <button title="Move left" className="inline-flex items-center justify-center h-7 w-7 rounded-full bg-white border shadow-sm text-gray-700 transform scale-50 hover:scale-100 opacity-70 hover:opacity-100" onMouseDown={(e)=>{e.preventDefault(); e.stopPropagation();}} onClick={()=> moveBlockLeft(b.id)}>
                                      <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2"><path d="M19 12H5"/><path d="M12 5l-7 7 7 7"/></svg>
                                    </button>
                                  </div>
                                  {/* Right delete + add + move right */}
                                  <div className="absolute top-1/2 -translate-y-1/2 right-[-3.5rem] flex flex-col items-center gap-2">
                                    <button
                                      title="Delete block"
                                      className={`inline-flex items-center justify-center h-7 w-7 rounded-full border border-red-200 bg-white text-red-600 shadow-sm`}
                                      onMouseDown={(e)=>{ e.preventDefault(); e.stopPropagation(); setSelectedId(b.id); removeBlock(b.id); }}
                                    >
                                      <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>
                                    </button>
                                    <button
                                      title="Add right"
                                      className="inline-flex items-center justify-center h-7 w-7 rounded-full bg-white border shadow-sm text-purple-600"
                                      onMouseDown={(e)=> e.preventDefault()}
                                      onClick={()=> { setInsertIndex(idx+1); setInsertGroupTargetId(b.id); setInsertSide('right'); setShowAdd(true); }}
                                    >
                                      <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 5v14M5 12h14"/></svg>
                                    </button>
                                    <button title="Move right" className="inline-flex items-center justify-center h-7 w-7 rounded-full bg-white border shadow-sm text-gray-700 transform scale-50 hover:scale-100 opacity-70 hover:opacity-100" onMouseDown={(e)=>{e.preventDefault(); e.stopPropagation();}} onClick={()=> moveBlockRight(b.id)}>
                                      <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2"><path d="M5 12h14"/><path d="M12 19l7-7-7-7"/></svg>
                                    </button>
                                  </div>
                                </>
                              )}
                            </div>
                          </div>
                        ) : b.type === 'embeddims' ? (
                          <div className="relative" data-block-id={b.id} onMouseDown={() => { if (!editable) return; isEditingRef.current = false; setSelectedId(b.id); }}>
                            <div className={`overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm transition-colors${selectedId===b.id ? ' ring-2 ring-purple-500 ring-offset-2 ring-offset-white' : ''}`}>
                              {/* overlay to capture clicks inside iframe while editing to select the block */}
                              {editable && (
                                <div
                                  className="absolute z-30"
                                  style={{ pointerEvents: 'auto', left: 0, right: 0, top: 0, bottom: 24 }}
                                  onMouseDown={(e)=>{ e.preventDefault(); e.stopPropagation(); setSelectedId(b.id); }}
                                />
                              )}
                              <EmbedDimsBlock
                                html={(b as any).embedHtml}
                                ratio={(b as any).embedDimRatio || (16/9)}
                                zoom={(b as any).embedDimZoom || 1}
                                extraDelta={(dragExtraById[b.id]||0)}
                                onApply={({ ratio, zoom }) => {
                                  try {
                                    const cur = modelRef.current;
                                    const idx = cur.blocks.findIndex(x => x.id === b.id);
                                    if (idx === -1) return;
                                    const nextBlocks = cur.blocks.slice();
                                    nextBlocks[idx] = { ...nextBlocks[idx], embedDimRatio: Number(ratio) || (16/9), embedDimZoom: Number(zoom) || 1 } as any;
                                    const next = { ...cur, blocks: nextBlocks };
                                    modelRef.current = next;
                                    setModel(next);
                                    onChange && onChange(next);
                                  } catch {}
                                }}
                              />
                            </div>
                            {/* right-side add for multi-column context */}
                            {editable && selectedId===b.id && (
                              <div className="absolute top-1/2 -translate-y-1/2 right-[-3.5rem] flex flex-col items-center gap-2">
                                <button
                                  title="Add right"
                                  className="inline-flex items-center justify-center h-7 w-7 rounded-full bg-white border shadow-sm text-purple-600"
                                  onMouseDown={(e)=> e.preventDefault()}
                                  onClick={()=> { setInsertIndex(idx+1); setInsertGroupTargetId(b.id); setInsertSide('right'); setShowAdd(true); }}
                                >
                                  <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 5v14M5 12h14"/></svg>
                                </button>
                              </div>
                            )}
                          </div>
                        ) : b.type === 'code' ? (
                          <EditableCode b={b} idx={idx} />
                        ) : (
                          <EditableBlock b={b} idx={idx} />
                        )}
                        {editable && (selectedId===b.id || (dragExtraById[b.id]||0) > 0) && (
                          <>
                            <div style={{ height: 0 }} />
                            {selectedId===b.id && (
                              <div
                                className="mt-2 cursor-row-resize select-none"
                                data-block-id={b.id}
                                onMouseDown={(e)=>{ isEditingRef.current = false; setSelectedId(b.id); startDragResize(e, b.id); }}
                              >
                                <DragLine />
                              </div>
                            )}
                          </>
                        )}
                        {editable && selectedId===b.id && b.group && cols >= 2 && (
                          <div
                            className="absolute top-0 left-full ml-3 h-full w-1 cursor-col-resize select-none z-50"
                            onMouseDown={(e)=> (colIdx === cols - 1 ? startRightPaddingResize(e, b.group as string) : startHorizontalResize(e, b.group as string, colIdx))}
                            title={colIdx === cols - 1 ? "Adjust right padding" : "Resize columns"}
                          >
                            <div className="h-full w-full rounded-full bg-gradient-to-b from-purple-400 via-fuchsia-400 to-pink-400 shadow-sm" />
                          </div>
                        )}
                        {editable && selectedId===b.id && b.group && cols >= 2 && colIdx === 0 && (
                          <div
                            className="absolute top-0 right-full mr-3 h-full w-1 cursor-col-resize select-none z-50"
                            onMouseDown={(e)=> startLeftPaddingResize(e, b.group as string)}
                            title="Adjust left padding"
                          >
                            <div className="h-full w-full rounded-full bg-gradient-to-b from-purple-400 via-fuchsia-400 to-pink-400 shadow-sm" />
                          </div>
                        )}
                        {editable && selectedId===b.id && b.group && cols >= 2 && colIdx > 0 && (
                          <div
                            className="absolute top-0 right-full mr-3 h-full w-1 cursor-col-resize select-none z-50"
                            onMouseDown={(e)=> startHorizontalResize(e, b.group as string, colIdx - 1)}
                            title="Resize columns"
                          >
                            <div className="h-full w-full rounded-full bg-gradient-to-b from-purple-400 via-fuchsia-400 to-pink-400 shadow-sm" />
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              );
            });
          })()}
        </div>
        {editable && model.blocks.length > 0 && (
          <div className="mt-10 mb-2 flex justify-center">
            <button
              className="group relative inline-flex items-center gap-2 rounded-full px-5 py-2.5 bg-gradient-to-r from-purple-600 to-fuchsia-600 text-white shadow-md hover:shadow-lg hover:brightness-110 focus:outline-none focus:ring-2 focus:ring-fuchsia-400/60"
              onMouseDown={(e)=> e.preventDefault()}
              onClick={()=> { setInsertIndex(model.blocks.length); setInsertGroupTargetId(null); setInsertSide(null); setShowAdd(true); }}
              title="Add a block"
            >
              <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-white/15">
                <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 5v14M5 12h14"/></svg>
              </span>
              <span className="font-medium">Add block</span>
            </button>
          </div>
        )}
        {showAdd && editable && (
          <div className="fixed inset-0 z-50 flex items-center justify-center">
            <div className="absolute inset-0 bg-black/40" onClick={()=> setShowAdd(false)} />
            <div className="relative z-10 w-full max-w-md rounded-2xl bg-white shadow-2xl p-6">
              <div className="mb-4 text-lg font-semibold text-gray-900">Add a block</div>
              <div className="grid grid-cols-2 gap-3">
                <button className="flex items-center gap-3 p-3 rounded-xl border hover:border-gray-300 hover:bg-indigo-50 transition" onClick={()=> (insertIndex==null?addBlockAtStart('paragraph'):addBlockAt(insertIndex,'paragraph'))}>
                  <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-indigo-100 text-indigo-600">
                    <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 19.5v-15"/><path d="M4 4.5h10a4 4 0 0 1 0 8H8"/></svg>
                  </span>
                  <span className="font-medium">Paragraph</span>
                </button>
                <button className="flex items-center gap-3 p-3 rounded-xl border hover:border-gray-300 hover:bg-amber-50 transition" onClick={()=> (insertIndex==null?addBlockAtStart('heading1'):addBlockAt(insertIndex,'heading1'))}>
                  <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-amber-100 text-amber-600">
                    <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 6v12"/><path d="M12 6v12"/><path d="M4 12h8"/><path d="M18 6v12"/></svg>
                  </span>
                  <span className="font-medium">Heading 1</span>
                </button>
                <button className="flex items-center gap-3 p-3 rounded-xl border hover:border-gray-300 hover:bg-sky-50 transition" onClick={()=> (insertIndex==null?addBlockAtStart('heading2'):addBlockAt(insertIndex,'heading2'))}>
                  <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-sky-100 text-sky-600">
                    <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 6v12"/><path d="M12 6v12"/><path d="M4 12h8"/><path d="M18 10h2a2 2 0 1 1 0 4h-2"/></svg>
                  </span>
                  <span className="font-medium">Heading 2</span>
                </button>
                <button className="flex items-center gap-3 p-3 rounded-xl border hover:border-gray-300 hover:bg-emerald-50 transition" onClick={()=> (insertIndex==null?addBlockAtStart('quote'):addBlockAt(insertIndex,'quote'))}>
                  <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-emerald-100 text-emerald-600">
                    <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 6H7a1 1 0 0 0-1 1v10l4-3h7a1 1 0 0 0 1-1V7a1 1 0 0 0-1-1z"/></svg>
                  </span>
                  <span className="font-medium">Quote</span>
                </button>
                <button className="flex items-center gap-3 p-3 rounded-xl border hover:border-gray-300 hover:bg-rose-50 transition" onClick={() => { try { const idx = insertIndex==null ? 0 : insertIndex; const ev: any = new CustomEvent('open-media-picker', { detail: { index: idx, groupTargetId: insertGroupTargetId || null, side: insertSide || null } }); window.dispatchEvent(ev); } catch {} setShowAdd(false); }}>
                  <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-rose-100 text-rose-600">
                    <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"/><circle cx="8.5" cy="8.5" r="1.5"/><path d="M21 15l-5-5L5 21"/></svg>
                  </span>
                  <span className="font-medium">Image</span>
                </button>
                <button className="flex items-center gap-3 p-3 rounded-xl border hover:border-gray-300 hover:bg-gray-50 transition" onClick={()=> (insertIndex==null?addBlockAtStart('code'):addBlockAt(insertIndex,'code'))}>
                  <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-gray-900 text-white">
                    <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="16 18 22 12 16 6"/><polyline points="8 6 2 12 8 18"/></svg>
                  </span>
                  <span className="font-medium">Code</span>
                </button>
                <button className="flex items-center gap-3 p-3 rounded-xl border hover:border-gray-300 hover:bg-purple-50 transition" onClick={()=> (insertIndex==null?addBlockAtStart('timeline'):addBlockAt(insertIndex,'timeline'))}>
                  <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-purple-100 text-purple-700">
                    <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18"/><path d="M6 12h12"/><path d="M3 18h18"/></svg>
                  </span>
                  <span className="font-medium">Timeline</span>
                </button>
                <button className="flex items-center gap-3 p-3 rounded-xl border hover:border-gray-300 hover:bg-fuchsia-50 transition" onClick={()=> (insertIndex==null?addBlockAtStart('carousel'):addBlockAt(insertIndex,'carousel'))}>
                  <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-fuchsia-100 text-fuchsia-700">
                    <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="6" width="18" height="12" rx="2"/><path d="M8 6v12"/><path d="M16 6v12"/></svg>
                  </span>
                  <span className="font-medium">Carousel</span>
                </button>
                <button className="flex items-center gap-3 p-3 rounded-xl border hover:border-gray-300 hover:bg-violet-50 transition" onClick={()=> (insertIndex==null?addBlockAtStart('faq'):addBlockAt(insertIndex,'faq'))}>
                  <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-violet-100 text-violet-700">
                    <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 1 1 5.83 1c0 2-3 2-3 4"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
                  </span>
                  <span className="font-medium">FAQ</span>
                </button>
                <button className="flex items-center gap-3 p-3 rounded-xl border hover:border-gray-300 hover:bg-teal-50 transition" onClick={()=> (insertIndex==null?addBlockAtStart('table'):addBlockAt(insertIndex,'table'))}>
                  <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-teal-100 text-teal-700">
                    <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M9 3v18"/><path d="M15 3v18"/><path d="M3 9h18"/><path d="M3 15h18"/></svg>
                  </span>
                  <span className="font-medium">Table</span>
                </button>
                <button className="flex items-center gap-3 p-3 rounded-xl border hover:border-gray-300 hover:bg-cyan-50 transition" onClick={()=> (insertIndex==null?addBlockAtStart('embed'):addBlockAt(insertIndex,'embed'))}>
                  <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-cyan-100 text-cyan-700">
                    <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="16 18 22 12 16 6"/><polyline points="8 6 2 12 8 18"/><rect x="9" y="4" width="6" height="16" rx="1"/></svg>
                  </span>
                  <span className="font-medium">HTML Embed</span>
                </button>
              </div>
              <div className="mt-5 flex justify-end">
                <button className="px-4 py-2 text-sm rounded-md border bg-white hover:bg-gray-50" onClick={()=> setShowAdd(false)}>Cancel</button>
              </div>
            </div>
          </div>
        )}
        {blockSettings && editable && (() => {
          const b = modelRef.current.blocks.find(x => x.id === blockSettings.id);
          if (!b) return null;
          // ensure local preview is initialized from block when opening/refreshing
          if (blockSettings.type === 'timeline' && (!timelinePreview || timelinePreview.id !== b.id)) {
            setTimelinePreview({
              id: b.id,
              items: b.items || [],
              variant: (b as any).timelineVariant || 'classic',
              scrollable: !!(b as any).timelineScrollable,
              expandable: !!(b as any).timelineExpandable,
              effects: (b as any).timelineEffects,
            });
            // Make sure extra drag padding is reset so the block height reflects the fixed height
            setDragExtraById(prev => (prev[b.id] === 0 ? prev : { ...prev, [b.id]: 0 }));
          }
          if (blockSettings.type === 'faq' && (!faqPreview || faqPreview.id !== b.id)) {
            setFaqPreview({
              id: b.id,
              items: ((b as any).faqItems || []) as any,
              palette: (b as any).faqPalette,
              style: (b as any).faqStyle,
              showIcons: !!(b as any).faqShowIcons,
              icon: (b as any).faqIcon,
            });
          }
          if (blockSettings.type === 'carousel' && (!carouselPreview || carouselPreview.id !== b.id)) {
            setCarouselPreview({
              id: b.id,
              slides: (b as any).slides || [],
              showDots: (b as any).carouselShowDots ?? true,
              showArrows: (b as any).carouselShowArrows ?? true,
              variant: (b as any).carouselVariant ?? 'classic',
              dotPosition: (b as any).carouselDotPosition ?? 'top',
              dotStyle: (b as any).carouselDotStyle ?? 'dot',
            });
          }
          if (blockSettings.type === 'table' && (!tablePreview || tablePreview.id !== b.id)) {
            setTablePreview({
              id: b.id,
              rows: (b as any).rows || 0,
              cols: (b as any).cols || 0,
              data: (b as any).data || [],
              colWidths: (b as any).colWidths || [],
              rowHeights: (b as any).rowHeights || [],
              dense: (b as any).dense || false,
              bordered: (b as any).bordered ?? true,
            });
          }
          if (blockSettings.type === 'embed' && (!embedPreview || embedPreview.id !== b.id)) {
            setEmbedPreview({
              id: b.id,
              html: (b as any).embedHtml,
              heightMode: (b as any).embedHeightMode || 'auto',
              fixedHeight: (b as any).embedFixedHeight || 360,
              bordered: (b as any).embedBordered ?? true,
              radius: (b as any).embedRadius ?? 12,
              shadow: (b as any).embedShadow ?? false,
              allowScripts: (b as any).embedAllowScripts ?? true,
              allowSameOrigin: (b as any).embedAllowSameOrigin ?? true,
              allowPopups: (b as any).embedAllowPopups ?? true,
              allowScroll: (b as any).embedAllowScroll ?? false,
              removeBg: (b as any).embedRemoveBg ?? false,
              responsiveFit: (b as any).embedResponsiveFit ?? false,
              offsetX: (b as any).embedOffsetX ?? 0,
              offsetY: (b as any).embedOffsetY ?? 0,
            });
          }
          return (
            <BlockSettingsModal
              open={true}
              title={blockSettings.type === 'timeline' ? 'Timeline settings' : blockSettings.type === 'carousel' ? 'Carousel settings' : blockSettings.type === 'faq' ? 'FAQ settings' : blockSettings.type === 'table' ? 'Table settings' : blockSettings.type === 'embed' ? 'HTML Embed settings' : 'Block settings'}
              onClose={() => setBlockSettings(null)}
              previewKey={`${b.id}:${JSON.stringify(blockSettings.type==='timeline' ? (timelinePreview || {}) : blockSettings.type==='carousel' ? (carouselPreview || {}) : blockSettings.type==='faq' ? (faqPreview || {}) : blockSettings.type==='table' ? (tablePreview || {}) : (embedPreview || {}) )}`}
              renderPreview={() => (
                blockSettings.type === 'timeline' ? (
                  <Timeline
                    items={(timelinePreview?.items) || (b.items || [])}
                    editable={false}
                    variant={(timelinePreview?.variant) || ((b as any).timelineVariant || 'classic')}
                    scrollable={(timelinePreview?.scrollable) ?? ((b as any).timelineScrollable || false)}
                    expandable={(timelinePreview?.expandable) ?? ((b as any).timelineExpandable || false)}
                    effects={(timelinePreview?.effects) ?? ((b as any).timelineEffects)}
                  />
                ) : blockSettings.type === 'carousel' ? (
                  <Carousel
                    slides={(carouselPreview?.slides) || ((b as any).slides || [])}
                    editable={false}
                    showDots={(carouselPreview?.showDots) ?? ((b as any).carouselShowDots ?? true)}
                    showArrows={(carouselPreview?.showArrows) ?? ((b as any).carouselShowArrows ?? true)}
                    variant={(carouselPreview?.variant) ?? ((b as any).carouselVariant ?? 'classic')}
                    dotPosition={(carouselPreview?.dotPosition) ?? ((b as any).carouselDotPosition ?? 'top')}
                    dotStyle={(carouselPreview?.dotStyle) ?? ((b as any).carouselDotStyle ?? 'dot')}
                  />
                ) : blockSettings.type === 'faq' ? (
                  <FAQ items={(faqPreview?.items) || (((b as any).faqItems) || [])} palette={(faqPreview?.palette) ?? ((b as any).faqPalette)} style={(faqPreview?.style) ?? ((b as any).faqStyle)} showIcons={(faqPreview?.showIcons) ?? ((b as any).faqShowIcons)} icon={(faqPreview?.icon) ?? ((b as any).faqIcon)} />
                ) : blockSettings.type === 'table' ? (
                  <Table
                    rows={(tablePreview?.rows) ?? ((b as any).rows || 0)}
                    cols={(tablePreview?.cols) ?? ((b as any).cols || 0)}
                    data={(tablePreview?.data) ?? (((b as any).data) || [])}
                    colWidths={(tablePreview?.colWidths) ?? (((b as any).colWidths) || [])}
                    rowHeights={(tablePreview?.rowHeights) ?? (((b as any).rowHeights) || [])}
                    dense={(tablePreview?.dense) ?? (((b as any).dense) || false)}
                    bordered={(tablePreview?.bordered) ?? (((b as any).bordered) ?? true)}
                    fitCols={(tablePreview as any)?.fitCols ?? (((b as any).fitCols) || false)}
                    fitRows={(tablePreview as any)?.fitRows ?? (((b as any).fitRows) || false)}
                    scrollable={(tablePreview as any)?.scrollable ?? (((b as any).scrollable) || false)}
                    editable={false}
                  />
                ) : blockSettings.type === 'embed' ? (
                  <Embed
                    html={(embedPreview?.html) ?? ((b as any).embedHtml)}
                    heightMode={(embedPreview?.heightMode) ?? ((b as any).embedHeightMode || 'auto')}
                    fixedHeight={((b as any).embedFixedHeight || (embedPreview?.fixedHeight) || 360)}
                    bordered={(embedPreview?.bordered) ?? ((b as any).embedBordered ?? true)}
                    radius={(embedPreview?.radius) ?? ((b as any).embedRadius ?? 12)}
                    shadow={(embedPreview?.shadow) ?? ((b as any).embedShadow ?? false)}
                    allowScripts={(embedPreview?.allowScripts) ?? ((b as any).embedAllowScripts ?? true)}
                    allowSameOrigin={(embedPreview?.allowSameOrigin) ?? ((b as any).embedAllowSameOrigin ?? true)}
                    allowPopups={(embedPreview?.allowPopups) ?? ((b as any).embedAllowPopups ?? true)}
                    allowScroll={(embedPreview?.allowScroll) ?? ((b as any).embedAllowScroll ?? false)}
                    removeBg={(embedPreview?.removeBg) ?? ((b as any).embedRemoveBg ?? false)}
                    responsiveFit={((embedPreview as any)?.responsiveFit) ?? ((b as any).embedResponsiveFit ?? false)}
                    offsetX={(embedPreview?.offsetX) ?? ((b as any).embedOffsetX ?? 0)}
                    offsetY={(embedPreview?.offsetY) ?? ((b as any).embedOffsetY ?? 0)}
                  />
                ) : null
              )}
            >
              {blockSettings.type === 'timeline' && (
                <TimelineSettings
                  items={(timelinePreview?.items) || (b.items || [])}
                  variant={(timelinePreview?.variant) || ((b as any).timelineVariant || 'classic')}
                  scrollable={(timelinePreview?.scrollable) ?? ((b as any).timelineScrollable || false)}
                  expandable={(timelinePreview?.expandable) ?? ((b as any).timelineExpandable || false)}
                  effects={(timelinePreview?.effects) ?? ((b as any).timelineEffects)}
                  onChange={(next) => {
                    // update local preview immediately
                    setTimelinePreview({
                      id: b.id,
                      items: next.items as any,
                      variant: (next.variant as any) || 'classic',
                      scrollable: !!next.scrollable,
                      expandable: !!next.expandable,
                      effects: next.effects as any,
                    });
                    // persist to block model
                    updateBlock(b.id, { items: next.items as any, timelineVariant: next.variant as any, timelineScrollable: !!next.scrollable, timelineExpandable: !!next.expandable, timelineEffects: next.effects as any } as any);
                  }}
                />
              )}
              {blockSettings.type === 'carousel' && (
                <CarouselSettings
                  slides={(carouselPreview?.slides) || ((b as any).slides || [])}
                  showDots={(carouselPreview?.showDots) ?? ((b as any).carouselShowDots ?? true)}
                  showArrows={(carouselPreview?.showArrows) ?? ((b as any).carouselShowArrows ?? true)}
                  variant={(carouselPreview?.variant) ?? ((b as any).carouselVariant ?? 'classic')}
                  dotPosition={(carouselPreview?.dotPosition) ?? ((b as any).carouselDotPosition ?? 'top')}
                  dotStyle={(carouselPreview?.dotStyle) ?? ((b as any).carouselDotStyle ?? 'dot')}
                  onChange={(next) => {
                    setCarouselPreview({
                      id: b.id,
                      slides: next.slides,
                      showDots: !!next.showDots,
                      showArrows: !!next.showArrows,
                      variant: (next.variant as any) ?? 'classic',
                      dotPosition: (next.dotPosition as any) ?? 'top',
                      dotStyle: (next.dotStyle as any) ?? 'dot',
                    });
                    updateBlock(b.id, { slides: next.slides as any, carouselShowDots: !!next.showDots, carouselShowArrows: !!next.showArrows, carouselVariant: (next.variant as any) ?? 'classic', carouselDotPosition: (next.dotPosition as any) ?? 'top', carouselDotStyle: (next.dotStyle as any) ?? 'dot' } as any);
                  }}
                />
              )}
              {blockSettings.type === 'faq' && (
                <FAQSettings
                  items={(faqPreview?.items) || (((b as any).faqItems) || [])}
                  palette={(faqPreview?.palette) ?? ((b as any).faqPalette)}
                  style={(faqPreview?.style) ?? ((b as any).faqStyle)}
                  showIcons={(faqPreview?.showIcons) ?? ((b as any).faqShowIcons)}
                  icon={(faqPreview?.icon) ?? ((b as any).faqIcon)}
                  onChange={(next) => {
                    setFaqPreview({ id: b.id, items: next.items, palette: next.palette, style: next.style, showIcons: next.showIcons, icon: next.icon });
                    updateBlock(b.id, { faqItems: next.items, faqPalette: next.palette as any, faqStyle: next.style as any, faqShowIcons: !!next.showIcons, faqIcon: next.icon as any } as any);
                  }}
                />
              )}
              {blockSettings.type === 'table' && (
                <TableSettings
                  rows={(tablePreview?.rows) ?? ((b as any).rows || 0)}
                  cols={(tablePreview?.cols) ?? ((b as any).cols || 0)}
                  data={(tablePreview?.data) ?? (((b as any).data) || [])}
                  colWidths={(tablePreview?.colWidths) ?? (((b as any).colWidths) || [])}
                  rowHeights={(tablePreview?.rowHeights) ?? (((b as any).rowHeights) || [])}
                  dense={(tablePreview?.dense) ?? (((b as any).dense) || false)}
                  bordered={(tablePreview?.bordered) ?? (((b as any).bordered) ?? true)}
                  fitCols={(tablePreview as any)?.fitCols ?? (((b as any).fitCols) || false)}
                  fitRows={(tablePreview as any)?.fitRows ?? (((b as any).fitRows) || false)}
                  scrollable={(tablePreview as any)?.scrollable ?? (((b as any).scrollable) || false)}
                  hoverOutline={(tablePreview as any)?.hoverOutline ?? ((((b as any).hoverOutline) !== false))}
                  onChange={(next) => {
                    setTablePreview({ id: b.id, ...next });
                    updateBlock(b.id, { rows: next.rows, cols: next.cols, data: next.data, colWidths: next.colWidths, rowHeights: next.rowHeights, dense: next.dense, bordered: next.bordered, fitCols: (next as any).fitCols, fitRows: (next as any).fitRows, scrollable: (next as any).scrollable, hoverOutline: (next as any).hoverOutline } as any);
                  }}
                />
              )}
              {blockSettings.type === 'embed' && (
                <EmbedSettings
                  html={(embedPreview?.html) ?? ((b as any).embedHtml)}
                  heightMode={(embedPreview?.heightMode) ?? ((b as any).embedHeightMode || 'auto')}
                  fixedHeight={((b as any).embedFixedHeight || (embedPreview?.fixedHeight) || 360)}
                  bordered={(embedPreview?.bordered) ?? ((b as any).embedBordered ?? true)}
                  radius={(embedPreview?.radius) ?? ((b as any).embedRadius ?? 12)}
                  shadow={(embedPreview?.shadow) ?? ((b as any).embedShadow ?? false)}
                  allowScripts={(embedPreview?.allowScripts) ?? ((b as any).embedAllowScripts ?? true)}
                  allowSameOrigin={(embedPreview?.allowSameOrigin) ?? ((b as any).embedAllowSameOrigin ?? true)}
                  allowPopups={(embedPreview?.allowPopups) ?? ((b as any).embedAllowPopups ?? true)}
                  allowScroll={(embedPreview?.allowScroll) ?? ((b as any).embedAllowScroll ?? false)}
                  removeBg={(embedPreview?.removeBg) ?? ((b as any).embedRemoveBg ?? false)}
                  responsiveFit={((embedPreview as any)?.responsiveFit) ?? ((b as any).embedResponsiveFit ?? false)}
                  offsetX={(embedPreview?.offsetX) ?? ((b as any).embedOffsetX ?? 0)}
                  offsetY={(embedPreview?.offsetY) ?? ((b as any).embedOffsetY ?? 0)}
                  onChange={(next: any) => {
                    setEmbedPreview({ id: b.id, ...next });
                    // Reset any extra drag padding, since height is explicitly set from settings
                    setDragExtraById(prev => (prev[b.id] === 0 ? prev : { ...prev, [b.id]: 0 }));
                    updateBlock(b.id, {
                      embedHtml: next.html,
                      embedHeightMode: next.heightMode,
                      embedFixedHeight: next.fixedHeight,
                      embedBordered: next.bordered,
                      embedRadius: next.radius,
                      embedShadow: next.shadow,
                      embedAllowScripts: next.allowScripts,
                      embedAllowSameOrigin: next.allowSameOrigin,
                      embedAllowPopups: next.allowPopups,
                      embedAllowScroll: next.allowScroll,
                      embedRemoveBg: next.removeBg,
                      embedResponsiveFit: next.responsiveFit,
                      embedOffsetX: next.offsetX ?? 0,
                      embedOffsetY: next.offsetY ?? 0,
                    } as any);
                  }}
                />
              )}
            </BlockSettingsModal>
          );
        })()}
      </div>
    </div>
  );
});

export type { PostModel, PostBlock };
const DisplayPost = memo(DisplayPostImpl, (prev, next) => {
  // Avoid re-render unless the post reference changes explicitly (we manage inner edits internally)
  return prev.post === next.post && prev.editable === next.editable && prev.showInnerTitle === next.showInnerTitle;
});

export default DisplayPost;
